import { useState, useEffect } from "react";
import React from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { FileText, Package, ChevronDown, Hash, Building2, RefreshCw, Loader2, X, Check, AlertTriangle, Tag, Filter, Search, Plus, Pencil, Trash2, Globe, MapPin, GitMerge, TriangleAlert, Users, Library, Pill, Copy, ChevronRight, Scale, ShieldCheck, ShieldAlert, ShieldQuestion, Lock, Stamp, Activity, TrendingUp, Clock, ArrowUpRight, Eye, BoxSelect, CircleAlert, CircleCheck, CalendarClock, Printer, Smartphone, Timer, ArrowRight, ArrowLeft, PoundSterling, PackageX, Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getBranchEntries, getMetricValue, METRIC, type DashboardPharmacy } from "@/pages/MultiBranchDashboard/dashboardMetrics";
import { getTitanClaims, getPrescriptionImage, saveClaims, getBlockedClaims, getItemInfo, addUpdateExemption, getExemption, replacementDispenseNotification, bulkRtec, getClaimedCurrent, getClaimedLast, claimAmendment, addEndorsement, saveClaimPrescriptionItems, getClaimPrescriptionItems, bulkExemption, getConcessionPrices, bulkClearClaims } from "@/dispatchers/titanTabDispatchers";
import { getPrescriptionByIncomingId } from "@/dispatchers/clinicalChecksDispatchers";
import { PrescriptionItemEndorseType } from "@/enum/PrescriptionItemEndorseType";
import { useToast, toast as showToast } from "@/hooks/use-toast";
import TableRowsSkeleton from "./TableRowsSkeleton";
import { PackSizePriceTable, type PackPriceOption } from "@/components/claims/PackSizePriceTable";
import { ConcessionInsightPanel } from "@/components/claims/ConcessionInsightPanel";
import { buildConcessionProducts, type ConcessionProduct } from "@/lib/claims/concessionFeedData";

// Estate overview branch-dot colours — real pharmacies carry no colour of their own.
const BRANCH_COLORS = ["#7c3aed", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"];

const TREATMENT_TYPE_LABELS: Record<string, string> = {
  "0001": "Acute",
  "0003": "Repeat Prescribing",
  "0004": "Repeat Dispensing",
};

const fmtClaimDate = (d?: string | null) =>
  d && !String(d).startsWith("0001") ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const firstRealDate = (...dates: any[]): any =>
  dates.find(d => d && !String(d).startsWith("0001"));

const parseDrugNames = (raw: any): string[] =>
  Array.from(new Set(String(raw ?? "").split("|").map(s => s.trim()).filter(Boolean)));

const CYCLE_TODAY = new Date();
const CURRENT_CYCLE_LABEL = CYCLE_TODAY.toLocaleDateString("en-GB", { month: "long" });
const PREVIOUS_CYCLE_LABEL = new Date(CYCLE_TODAY.getFullYear(), CYCLE_TODAY.getMonth() - 1, 1).toLocaleDateString("en-GB", { month: "long" });

// Mirrors the backend BlockedClaimRuleType enum (productRule value -> display name).
const BLOCKED_CLAIM_RULE_LABELS: Record<number, string> = {
  0: "Unknown", 1: "Concession", 2: "Equalisation Deals", 3: "Specials", 4: "Appliances", 5: "Pack Optimisation",
};

// Maps a prescription item's additionalEndorsements (from get-prescription-by-incoming)
// into the modal's endorsement shape.
const mapApiEndorsements = (additionalEndorsements: any[]): { code: string; label: string; appliedDate?: string; extraInfo?: Record<string, string>; api?: any }[] => {
  const list = Array.isArray(additionalEndorsements) ? additionalEndorsements : [];
  return list
    .filter((e: any) => e && !e.isDeleted)
    .map((e: any) => {
      const opt = ENDORSEMENT_OPTIONS.find(o => o.value === Number(e?.additionEndorseType));
      const code = opt?.code ?? (PrescriptionItemEndorseType as Record<number, string>)[e?.additionEndorseType] ?? "—";
      const extra: Record<string, string> = {};
      const add = (k: string, v: any) => { if (v !== null && v !== undefined && String(v).trim() !== "" && String(v) !== "0") extra[k] = String(v); };
      add("value", e?.value); add("price", e?.price); add("quantity", e?.quantity); add("packSize", e?.packSize);
      add("supplier", e?.supplier); add("brand", e?.brand); add("batchNumber", e?.batchNumber); add("mhraNumber", e?.mhraNumber);
      add("details", e?.details); add("pharmacistInitials", e?.pharmacistInitials);
      return {
        api: e,
        code,
        label: opt?.label ?? code,
        appliedDate: e?.dispenseDate && !String(e.dispenseDate).startsWith("0001") ? new Date(e.dispenseDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : undefined,
        extraInfo: Object.keys(extra).length > 0 ? extra : undefined,
      };
    });
};

const savedClaimItemsLookup = (record: any): Map<string, any> => {
  const lookup = new Map<string, any>();
  const saved: any[] = Array.isArray(record?.items) ? record.items : [];
  saved.forEach((s: any) => {
    [s?.prescriptionItemId, s?.dispenseItemId, s?.prescribedItemId].forEach((key: any) => {
      if (isRealGuid(key) && !lookup.has(key)) lookup.set(key, s);
    });
  });
  return lookup;
};

const findSavedClaimItem = (lookup: Map<string, any>, item: PrescriptionItem): any =>
  [item.prescriptionItemId, item.dispenseItemId, item.prescribedItemId]
    .map(key => (isRealGuid(key) ? lookup.get(key as string) : undefined))
    .find(Boolean);

const applySavedClaimItem = (item: PrescriptionItem, saved: any): void => {
  const text = (v: any) => (v === null || v === undefined ? "" : String(v).trim());
  const has = (v: any) => v !== null && v !== undefined;
  if (text(saved?.drug)) item.drug = text(saved.drug);
  if (text(saved?.pack)) item.pack = text(saved.pack);
  if (text(saved?.quantity)) item.quantity = text(saved.quantity);
  if (text(saved?.dosage)) item.dosage = text(saved.dosage);
  if (has(saved?.drugTariffPrice)) item.claimDtPrice = `£${Number(saved.drugTariffPrice).toFixed(2)}`;
  if (text(saved?.notDispensedReasonCode)) item.notDispensedReasonCode = text(saved.notDispensedReasonCode);
  if (has(saved?.itemCrossedOut)) item.itemCrossedOut = Boolean(saved.itemCrossedOut);
  if (has(saved?.packagedDoses)) item.packagedDoses = Number(saved.packagedDoses);
};

const claimItemTitle = (item: PrescriptionItem): string => {
  const prescribed = (item.drug ?? "").trim();
  return prescribed && prescribed !== "—" ? prescribed : "—";
};

// Extended ClaimRow — the Builder UI shape plus the real fields carried through for
// API-backed drill-in. drug / dtPrice / costPrice are not returned by the claims
// API, so they arrive as "" / "0" and render as zero in the derived columns.
interface ClaimRow {
  id: string;
  patient: string;
  drug: string;
  // get-claims returns every drug on the prescription in one pipe-separated
  // string ("Drug A | Drug B"); split so a claim can sit under each of its drugs.
  drugNames?: string[];
  exemption: "Exempt" | "Not Exempt" | "Evidence Not Seen" | "Maternity" | "Medical" | "Prepayment" | string;
  lastExemption: string;
  rxDate: string;
  epsBarcode: string;
  treatmentType: "Acute" | "Repeat Prescribing" | "Repeat Dispensing" | string;
  dtPrice: string;
  costPrice: string;
  endorsed: boolean;
  itemCount?: number;
  prescriptionIncomingId?: string;
  nhsNumber?: string;
  prescriptionTreatmentType?: string | null;
  claimStatus?: number;
  // Fields now supplied directly by the get-claims payload:
  dob?: string;                     // patient DOB (drives Age)
  age?: number;                     // derived from dob
  netReimb?: string;                // net reimbursement, numeric string
  categories?: string[];            // DT categories (free-string labels from the API)
  categoryIds?: number[];           // the raw DT category ids behind `categories`, in the same order
  optimiseIncome?: string;          // comma-separated income opportunities
  rtecAuditReason?: string;         // blank when a real-time exemption check ran
  sourceTypeId?: number;            // 1 = entered manually, everything else is EPS
  prescriptionType?: PrescriptionType;
}

// The API's sourceTypeId carries every prescription source Titan knows about,
// but a claim is only ever one of two things to the pharmacist.
type PrescriptionType = "EPS" | "Manual";
// PrescriptionSourceTypes: 1 Manual, 8 Central data entry, 10 Private Rx,
// 11 Power Mail — everything else reaches the pharmacy through EPS.
const MANUAL_SOURCE_TYPE_IDS = new Set([1, 8, 10, 11]);
// Claims open on EPS only: the Manual headline tick starts cleared, and the
// pharmacist can tick it back on at any time.
const DEFAULT_EXCLUDED_TILES = ["manual"];

function prescriptionTypeOf(sourceTypeId: any): PrescriptionType {
  return MANUAL_SOURCE_TYPE_IDS.has(Number(sourceTypeId)) ? "Manual" : "EPS";
}

function rowPrescriptionType(row: ClaimRow): PrescriptionType {
  return row.prescriptionType ?? prescriptionTypeOf(row.sourceTypeId);
}

// Parse a "£5.00" style price into a number (NaN when absent/unparseable).
// Numeric API fields that are optional — absent / blank stays undefined so it
// can be told apart from a real 0 (Drug Tariff category "Not Specified").
const numOrUndefined = (v: any): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const parsePrice = (v: any): number => {
  if (typeof v === "number") return v;
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(n) ? NaN : n;
};

// Whole-year age from an ISO DOB; NaN for missing / default (0001-...) dates.
const ageFromDob = (dob: any): number => {
  if (!dob || String(dob).startsWith("0001")) return NaN;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return NaN;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

// Backend ClaimStatus enum values that drive the claim warnings:
//   3  Fail                     (EPS Failed)                  -> Failed
//   4  RequiresExemption        (EPS Exemption Required)
//   9  ManualRequiresExemption  (Manual requires exemption)   -> Missing Exemption
//   10 ManualReadytoClaim       (Manual to be endorsed)       -> Incomplete Endorsement
const CLAIM_STATUS_REQUIRES_EXEMPTION = [4, 9];
const CLAIM_STATUS_TO_BE_ENDORSED = 10;
const CLAIM_STATUS_FAILED = 3;
const CLAIM_STATUS_AFTER_EXEMPTION: Record<number, number> = { 4: 8, 9: 10 };
function rowHasFailedSubmission(row: ClaimRow): boolean {
  return (row.claimStatus ?? -1) === CLAIM_STATUS_FAILED;
}
function claimNeedsExemption(row: ClaimRow): boolean {
  return CLAIM_STATUS_REQUIRES_EXEMPTION.includes(row.claimStatus ?? -1)
    || row.exemption === MISSING_EXEMPTION;
}
function claimNeedsEndorsement(row: ClaimRow): boolean {
  return (row.claimStatus ?? -1) === CLAIM_STATUS_TO_BE_ENDORSED;
}

// ── HELPERS (dispensed / attention / RTEC / category / net-reimb) ──────────────
// ── DISPENSED ITEM / BRAND ENDORSEMENT HELPERS ────────────────────────────────
// Deterministic mock data describing what pack/dose was actually dispensed for
// a claim line, plus alternative brands/pack sizes it could be endorsed as.

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

// Flat NHS prescription charge per item (England), deducted from the Drug
// Tariff reimbursement for non-exempt / unconfirmed-exemption claims.
const NHS_PRESCRIPTION_CHARGE = 9.90;

const ALT_MANUFACTURERS = ["Accord", "Teva", "Sandoz", "Aurobindo", "Wockhardt", "Almus", "Mylan"];
const ALT_PACK_SIZES = ["21", "28", "30", "56", "60", "84", "100"];
const DOSAGE_INSTRUCTIONS = ["Take ONE daily", "Take ONE twice a day", "Take ONE three times a day", "Take TWO daily"];
// Pack sizes a manufacturer might supply in - used to split a dispensed
// quantity into N packs of a smaller size (e.g. 28 dispensed as 2 x 14).
const COMMON_PACK_SIZES = [1, 7, 10, 14, 15, 20, 21, 25, 28, 30, 50, 56, 60, 84, 100, 112, 168];

function dispensedFormFor(drug: string): string {
  if (/inhaler/i.test(drug)) return "inhaler";
  if (/capsule/i.test(drug)) return "capsules";
  return "tablets";
}

// Deterministic mock manufacturer/brand for what was actually dispensed on a claim line.
function getDispensedManufacturer(row: ClaimRow): string {
  const h = hashString(row.id + "-dispensed-manufacturer");
  return ALT_MANUFACTURERS[h % ALT_MANUFACTURERS.length];
}

function buildDispensedDetails(row: ClaimRow): { pack: string; quantity: string; dosage: string; manufacturer: string } {
  const h = hashString(row.id + row.drug);
  const form = dispensedFormFor(row.drug);
  const manufacturer = getDispensedManufacturer(row);
  if (form === "inhaler") return { pack: "1 inhaler", quantity: "1", dosage: DOSAGE_INSTRUCTIONS[h % DOSAGE_INSTRUCTIONS.length], manufacturer };
  const qty = ALT_PACK_SIZES[h % ALT_PACK_SIZES.length];
  return { pack: `${qty} ${form}`, quantity: qty, dosage: DOSAGE_INSTRUCTIONS[(h >> 2) % DOSAGE_INSTRUCTIONS.length], manufacturer };
}

// Patient age comes from the claim row's DOB (mapped into row.age).
function getPatientAge(row: ClaimRow): number {
  return row.age ?? NaN;
}
// Age for display — a dash when we couldn't derive it.
const ageDisplay = (row?: ClaimRow): string =>
  row && Number.isFinite(row.age) ? String(row.age) : "—";

function buildBrandOptions(row: ClaimRow): { id: string; brand: string }[] {
  const genericName = row.drug.replace(/\s*\d.*$/, "").trim();
  const h = hashString(row.id + "-brand");
  const brandA = ALT_MANUFACTURERS[h % ALT_MANUFACTURERS.length];
  const brandB = ALT_MANUFACTURERS[(h + 3) % ALT_MANUFACTURERS.length];
  return [
    { id: "dispensed", brand: `${row.drug} (as dispensed)` },
    { id: "brand-a", brand: `${genericName} (${brandA})` },
    { id: "brand-b", brand: `${genericName} (${brandB})` },
  ];
}

// Pack sizes that evenly divide the dispensed quantity, so N x packSize
// always adds back up to the full amount dispensed.
function buildPackSizeOptions(totalQuantity: number): number[] {
  const options = COMMON_PACK_SIZES.filter(p => p <= totalQuantity && totalQuantity % p === 0);
  if (!options.includes(totalQuantity)) options.push(totalQuantity);
  return Array.from(new Set(options)).sort((a, b) => b - a);
}

// Deterministic mock cost for claiming a given brand/pack size combination,
// used to surface cheaper alternatives to what was actually dispensed.
function estimatePackCost(itemId: string, dispensedCost: number, brandId: string, packSize: number, dispensedQuantity: number): number {
  if (brandId === "dispensed" && packSize === dispensedQuantity) return dispensedCost;
  const h = hashString(`${itemId}-${brandId}-${packSize}`);
  const multiplier = 0.8 + (h % 41) / 100; // 0.80 - 1.20
  return dispensedCost * multiplier;
}

// Deterministic mock Drug Tariff (reimbursement) price for a single pack of
// a given brand/size - shown against each pack size option to claim against.
function estimatePackTariffPrice(itemId: string, dispensedDtPrice: number, brandId: string, packSize: number, dispensedQuantity: number): number {
  const perUnit = dispensedQuantity > 0 ? dispensedDtPrice / dispensedQuantity : 0;
  if (brandId === "dispensed" && packSize === dispensedQuantity) return dispensedDtPrice;
  const h = hashString(`${itemId}-dt-${brandId}-${packSize}`);
  const multiplier = 0.85 + (h % 31) / 100; // 0.85 - 1.15 per-unit variance
  return perUnit * multiplier * packSize;
}

function findCheapestPackOption(
  itemId: string,
  dispensedCost: number,
  dispensedQuantity: number,
  brandOptions: { id: string }[],
  packSizeOptions: number[],
): { brandId: string; packSize: number; cost: number } {
  let best = { brandId: "dispensed", packSize: dispensedQuantity, cost: dispensedCost };
  brandOptions.forEach(b => {
    packSizeOptions.forEach(p => {
      const cost = estimatePackCost(itemId, dispensedCost, b.id, p, dispensedQuantity);
      if (cost < best.cost - 0.005) best = { brandId: b.id, packSize: p, cost };
    });
  });
  return best;
}

// Whether some cheaper brand/pack combination exists for what was actually
// dispensed on this claim line - used to flag potential savings in lists.
function rowHasCheaperPack(row: ClaimRow): boolean {
  const dispensed = buildDispensedDetails(row);
  const totalQty = parseInt(dispensed.quantity, 10) || 1;
  const dispensedCost = parseFloat(row.costPrice) || 0;
  const cheapest = findCheapestPackOption(row.id, dispensedCost, totalQty, buildBrandOptions(row), buildPackSizeOptions(totalQty));
  return cheapest.cost < dispensedCost - 0.005;
}

// ── NEEDS ATTENTION CLASSIFICATION ────────────────────────────────────────────
// A claim line can flag up to four distinct warning reasons (things that need
// action before a claim can be safely submitted):
//  - "Failed": the NHSBSA rejected a previous submission of this claim.
//  - "Incomplete Endorsement": a required endorsement has not been completed.
//  - "Missing Exemption": the patient's exemption is unconfirmed or not exempt.
//  - "Expired": the prescription is past its NHS dispensing window.
//  - "Expiring": the prescription is nearing the end of its dispensing window.
const ATTENTION_REASONS = ["Failed", "Incomplete Endorsement", "Missing Exemption", "Expired", "Expiring"] as const;
type AttentionReason = typeof ATTENTION_REASONS[number];

// Income opportunities - not blockers, just ways to improve the claim value:
//  - "Additional Endorsement": the item is endorsed but a better pack/brand
//    could still be endorsed to improve the claim.
//  - "Better Pack": the item isn't endorsed yet and a cheaper pack/brand exists.
const INCOME_REASONS = ["Additional Endorsement", "Better Pack"] as const;
type IncomeReason = typeof INCOME_REASONS[number];

// Mock dispensing-window status for a claim line - how many days remain
// before the prescription falls outside its NHS dispensing window.
type ExpiryStatus = "Expired" | "Expiring" | "Current";

// The claims API does not expose the NHS dispensing-window state, so we never
// fabricate an Expired/Expiring flag — every row is treated as Current.
function getExpiryStatus(_row: ClaimRow): ExpiryStatus {
  return "Current";
}

// A claim counts as "missing exemption" when the patient pays / evidence is
// unconfirmed, or when the API returns no exemption at all (mapped to "Missing").
const MISSING_EXEMPTION = "Missing";
function isMissingExemption(exemption: string): boolean {
  return exemption === "Not Exempt" || exemption === "Evidence Not Seen" || exemption === MISSING_EXEMPTION;
}

// Search runs over every column the row shows, so a barcode, a category, a
// price or a warning finds the claim just as a patient name does.
function rowSearchValues(row: ClaimRow, claimedAt?: string): string[] {
  return [
    row.id,
    row.patient,
    ageDisplay(row),
    String(row.itemCount ?? 1),
    rowPrescriptionType(row),
    rowCategories(row).join(" "),
    row.exemption,
    row.lastExemption,
    row.rxDate,
    claimedAt ?? "",
    row.epsBarcode,
    row.nhsNumber ?? "",
    (parseFloat(row.dtPrice) || 0).toFixed(2),
    calcNetReimb(row).toFixed(2),
    getAttentionReasons(row).join(" "),
    getIncomeReasons(row).join(" "),
    ...rowDrugNames(row),
  ].filter(Boolean);
}

// Spacing shouldn't hide a claim: both sides drop their whitespace, so "jo hn"
// finds "John Smith" and a barcode types the same with or without its dashes.
const normaliseSearch = (value: string): string => value.toLowerCase().replace(/\s+/g, "");

// Damerau-Levenshtein distance, abandoned as soon as it passes the allowance.
// Swapped letters ("jhon" for "john") are the commonest typo of all, so they
// count as one edit rather than two.
function withinEditDistance(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  let beforePrevious: number[] = [];
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current: number[] = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, beforePrevious[j - 2]! + 1);
      }
      current[j] = value;
      if (value < best) best = value;
    }
    if (best > max) return false;
    beforePrevious = previous;
    previous = current;
  }
  return previous[b.length]! <= max;
}

// How many typos a term may carry: short terms must be exact, so a code or an
// initial doesn't match half the list.
function editAllowance(term: string): number {
  if (term.length >= 8) return 2;
  if (term.length >= 4) return 1;
  return 0;
}

// A term matches a field when it appears in it, or when it is a near miss for
// the field or one of its words ("jhon" → "John", "amoxicilin" → "Amoxicillin").
function termMatchesValue(term: string, value: string): boolean {
  const haystack = normaliseSearch(value);
  if (!haystack) return false;
  if (haystack.includes(term)) return true;
  const allowance = editAllowance(term);
  if (allowance === 0) return false;
  if (withinEditDistance(term, haystack, allowance)) return true;
  return value.toLowerCase().split(/[^a-z0-9]+/).some(word => word.length > 0 && withinEditDistance(term, word, allowance));
}

// Every term the user typed has to match some field, so extra words narrow the
// list, and the order they are typed in never matters.
function rowMatchesClaimSearch(row: ClaimRow, query: string, claimedAt?: string, fuzzy = true): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const values = rowSearchValues(row, claimedAt);
  const joined = normaliseSearch(values.join(" "));
  return terms.every(term => joined.includes(term) || (fuzzy && values.some(value => termMatchesValue(term, value))));
}

function filterRowsByClaimSearch<T extends ClaimRow>(rows: T[], query: string, claimedAtOf?: (row: T) => string | undefined): T[] {
  if (!query.trim()) return rows;
  const exact = rows.filter(r => rowMatchesClaimSearch(r, query, claimedAtOf?.(r), false));
  if (exact.length > 0) return exact;
  return rows.filter(r => rowMatchesClaimSearch(r, query, claimedAtOf?.(r)));
}

function getAttentionReasons(row: ClaimRow): AttentionReason[] {
  const reasons: AttentionReason[] = [];
  if (rowHasFailedSubmission(row)) reasons.push("Failed");
  if (claimNeedsEndorsement(row)) reasons.push("Incomplete Endorsement");
  if (claimNeedsExemption(row)) reasons.push("Missing Exemption");
  const expiry = getExpiryStatus(row);
  if (expiry === "Expired") reasons.push("Expired");
  else if (expiry === "Expiring") reasons.push("Expiring");
  return reasons;
}

// Income opportunities come straight from the API's optimiseIncome string
// (e.g. "Better pack, Additional endorsement"), matched case-insensitively.
function getIncomeReasons(row: ClaimRow): IncomeReason[] {
  const raw = (row.optimiseIncome ?? "").toLowerCase();
  const reasons: IncomeReason[] = [];
  if (raw.includes("additional endorsement")) reasons.push("Additional Endorsement");
  if (raw.includes("better pack")) reasons.push("Better Pack");
  return reasons;
}

// ── RTEC (REAL TIME EXEMPTION CHECKING) STATUS ────────────────────────────────
// Mock RTEC lookup result for a claim line - independent of the exemption the
// patient declared, so it can agree or flag a mismatch for the pharmacist to review.
type RtecStatus = "RTEC Exempt" | "RTEC Not Exempt" | "RTEC Not Done";

// rtecAuditReason explains why a real-time exemption check did not run, so an
// empty reason is the only signal that one did.
function getRtecStatus(row: ClaimRow): RtecStatus {
  return (row.rtecAuditReason ?? "").trim() ? "RTEC Not Done" : "RTEC Exempt";
}

// Compact chip indicating whether a Real Time Exemption Check has been run.
function RtecChip({ status, dark, reason }: { status: RtecStatus; dark: boolean; reason?: string }) {
  const done = status !== "RTEC Not Done";
  const cls = done
    ? dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"
    : dark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-500";
  return (
    <span title={!done && reason ? reason : undefined} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${cls}`}>
      <ShieldCheck className="w-3 h-3" />
      {done ? "RTEC Done" : "RTEC Not Done"}
    </span>
  );
}

// Two independent status filters drive the claims list: one for Warnings
// (things that need action) and one for NHS Optimise (income opportunities).
type ClaimStatusFilter = "all" | "needs-attention" | "clear" | "any-income" | "no-income" | AttentionReason | IncomeReason | "RTEC Not Done";

function rowMatchesStatusFilter(row: ClaimRow, filter: ClaimStatusFilter, resolveRtec: (r: ClaimRow) => RtecStatus = getRtecStatus): boolean {
  switch (filter) {
    case "all": return true;
    case "needs-attention": return getAttentionReasons(row).length > 0;
    case "clear": return getAttentionReasons(row).length === 0;
    case "any-income": return getIncomeReasons(row).length > 0;
    case "no-income": return getIncomeReasons(row).length === 0;
    case "RTEC Not Done": return resolveRtec(row) === "RTEC Not Done";
    case "Additional Endorsement":
    case "Better Pack":
      return getIncomeReasons(row).includes(filter);
    default: return getAttentionReasons(row).includes(filter);
  }
}

// ── DRUG TARIFF CATEGORY CLASSIFICATION ───────────────────────────────────────
// Mock classification of a dispensed drug into its NHS Drug Tariff reimbursement
// category, used to flag how each item on a prescription gets priced/endorsed.

const CLAIM_LINE_CATEGORIES = [
  "Category A", "Category C", "Category M",
  "Special (listed)", "Special (unlisted)",
  "Import (VIIIC)", "Unlicensed biological (VIIID)", "Extemporaneous preparation",
  "Appliance (Part IX)", "Reagent (IXR)", "Borderline substance / food (Part XV)",
  "Branded (not in Part VIII)", "Unclassified",
] as const;
type ClaimLineCategory = typeof CLAIM_LINE_CATEGORIES[number];

const CLAIM_CATEGORY_DESCRIPTIONS: Record<ClaimLineCategory, string> = {
  "Category A": "Readily available generics, Part VIIIA, weighted-average price",
  "Category C": "Part VIIIA, priced against a specific brand/supplier",
  "Category M": "Part VIIIA, DHSC-calculated price from Scheme M data (margin control)",
  "Special (listed)": "Unlicensed special/import listed in Part VIIIB, fixed reimbursement price + £20 fee",
  "Special (unlisted)": "Unlicensed special not in VIIIB, reimbursed at endorsed invoice price; heaviest endorsement burden",
  "Import (VIIIC)": "Imported equivalent of a UK-licensed product, priced per Part VIIIC",
  "Unlicensed biological (VIIID)": "Part VIIID specialist/biological products",
  "Extemporaneous preparation": "Compounded in-pharmacy, unlicensed, own endorsement rules",
  "Appliance (Part IX)": "Dressings, ostomy, catheters etc. (Parts IXA/IXB/IXC)",
  "Reagent (IXR)": "Chemical reagents, e.g. glucose test strips",
  "Borderline substance / food (Part XV)": "ACBS items, gluten-free foods, nutritional products",
  "Branded (not in Part VIII)": "Licensed proprietary medicines reimbursed at list price",
  "Unclassified": "Mapping failed or no clean classification",
};

// Weighted pool used for drugs that don't match a specific keyword rule -
// most dispensed generics fall under Category A/M/C in practice.
const CLAIM_GENERIC_CATEGORY_POOL: ClaimLineCategory[] = [
  "Category A", "Category A", "Category A", "Category A",
  "Category M", "Category M", "Category M",
  "Category C", "Category C",
  "Branded (not in Part VIII)",
  "Import (VIIIC)",
];

function classifyDrugCategory(drug: string): ClaimLineCategory {
  if (!drug || drug === "—") return "Unclassified";
  if (/dressing|catheter|stoma|ostomy|appliance/i.test(drug)) return "Appliance (Part IX)";
  if (/test strip|reagent|glucose strip/i.test(drug)) return "Reagent (IXR)";
  if (/gluten|nutrition|nutritional|supplement/i.test(drug)) return "Borderline substance / food (Part XV)";
  if (/inhaler/i.test(drug)) return "Branded (not in Part VIII)";
  if (/oral suspension|oral solution/i.test(drug)) {
    return hashString(drug + "-special") % 2 === 0 ? "Special (listed)" : "Special (unlisted)";
  }
  return CLAIM_GENERIC_CATEGORY_POOL[hashString(drug) % CLAIM_GENERIC_CATEGORY_POOL.length];
}

const CLAIM_CATEGORY_STYLE: Record<ClaimLineCategory, { light: string; dark: string }> = {
  "Category A": { light: "bg-blue-100 text-blue-700", dark: "bg-blue-500/15 text-blue-400" },
  "Category C": { light: "bg-cyan-100 text-cyan-700", dark: "bg-cyan-500/15 text-cyan-400" },
  "Category M": { light: "bg-indigo-100 text-indigo-700", dark: "bg-indigo-500/15 text-indigo-400" },
  "Special (listed)": { light: "bg-amber-100 text-amber-700", dark: "bg-amber-500/15 text-amber-400" },
  "Special (unlisted)": { light: "bg-red-100 text-red-700", dark: "bg-red-500/15 text-red-400" },
  "Import (VIIIC)": { light: "bg-purple-100 text-purple-700", dark: "bg-purple-500/15 text-purple-400" },
  "Unlicensed biological (VIIID)": { light: "bg-pink-100 text-pink-700", dark: "bg-pink-500/15 text-pink-400" },
  "Extemporaneous preparation": { light: "bg-orange-100 text-orange-700", dark: "bg-orange-500/15 text-orange-400" },
  "Appliance (Part IX)": { light: "bg-teal-100 text-teal-700", dark: "bg-teal-500/15 text-teal-400" },
  "Reagent (IXR)": { light: "bg-lime-100 text-lime-700", dark: "bg-lime-500/15 text-lime-400" },
  "Borderline substance / food (Part XV)": { light: "bg-fuchsia-100 text-fuchsia-700", dark: "bg-fuchsia-500/15 text-fuchsia-400" },
  "Branded (not in Part VIII)": { light: "bg-violet-100 text-violet-700", dark: "bg-violet-500/15 text-violet-400" },
  "Unclassified": { light: "bg-gray-100 text-gray-600", dark: "bg-gray-500/15 text-gray-400" },
};

// Category labels now come straight from the API as free strings, so fall back
// to a neutral style/description for any label not in the known catalog.
function CategoryTag({ category, dark }: { category: string; dark: boolean }) {
  const style = CLAIM_CATEGORY_STYLE[category as ClaimLineCategory]
    ?? DRUG_TARIFF_CATEGORY_STYLE[category]
    ?? { light: "bg-gray-100 text-gray-600", dark: "bg-gray-500/15 text-gray-400" };
  return (
    <span
      title={CLAIM_CATEGORY_DESCRIPTIONS[category as ClaimLineCategory] ?? category}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap ${dark ? style.dark : style.light}`}
    >
      {category}
    </span>
  );
}

// Net reimbursement discount-deduction rates, applied against DT Price.
const RATE_GENERICS = 0.2000;   // Part VIIIA Cat A & M, prescribed generically
const RATE_BRANDS = 0.0500;     // everything not generics or appliances
const RATE_APPLIANCES = 0.0985; // Part IX
const RATE_EXEMPT = 0.0000;     // Specials, imports, unlicensed biologicals, extemp preps, reagents - discount not deducted

function getNetReimbRate(category: ClaimLineCategory): number {
  switch (category) {
    case "Category A":
    case "Category M":
      return RATE_GENERICS;
    case "Appliance (Part IX)":
      return RATE_APPLIANCES;
    case "Special (listed)":
    case "Special (unlisted)":
    case "Import (VIIIC)":
    case "Unlicensed biological (VIIID)":
    case "Extemporaneous preparation":
    case "Reagent (IXR)":
      return RATE_EXEMPT;
    default:
      return RATE_BRANDS;
  }
}

// Part V discount-deduction rate keyed on the Drug Tariff category the API
// actually sends (prescribedItem.drugTariffCategoryType / claim `category`),
// rather than on the drug name. The ids are the DRUG_TARIFF_CATEGORY_LABELS
// keys below.
//
// Anything not listed here — Part X, Part XV borderline, branded not in Part
// VIII, "Not Specified" — is on the branded sliding scale, whose rate depends
// on the contractor's month-to-date branded total. Until that total is
// available the flat RATE_BRANDS stands in as the forecast rate.
const NET_REIMB_RATE_BY_TARIFF_CATEGORY: Record<number, number> = {
  1: RATE_GENERICS,    // Part VIIIA Category A
  3: RATE_GENERICS,    // Part VIIIA Category C
  11: RATE_GENERICS,   // Part VIIIA Category M
  5: RATE_APPLIANCES,  // Part IXa
  6: RATE_APPLIANCES,  // Part IXb
  7: RATE_APPLIANCES,  // Part IXc
  8: RATE_APPLIANCES,  // Part IXr
  10: RATE_APPLIANCES, // Parts IXb and IXc
  12: RATE_EXEMPT,     // Part VIIIB (listed special)
  13: RATE_EXEMPT,     // Part VIIIC (import)
  14: RATE_EXEMPT,     // Part VIIID (unlicensed biological)
  15: RATE_EXEMPT,     // Unlisted Special
  16: RATE_EXEMPT,     // Extemporaneous
};

function tariffNetReimbRate(categoryId?: number | null): number {
  if (categoryId == null) return RATE_BRANDS;
  const rate = NET_REIMB_RATE_BY_TARIFF_CATEGORY[categoryId];
  return rate === undefined ? RATE_BRANDS : rate;
}

// Net reimbursement is the claim's DT price less the Part V discount
// deduction. row.dtPrice carries the reimbursement price the claim is made
// against, so the deduction applies straight to it.
function calcNetReimb(row: ClaimRow): number {
  const dt = parseFloat(row.dtPrice) || 0;
  return dt * (1 - tariffNetReimbRate(row.categoryIds?.[0]));
}

const DRUG_TARIFF_CATEGORY_LABELS: Record<string, string> = {
  "0": "Not Specified",
  "1": "Part VIIIA Category A",
  "3": "Part VIIIA Category C",
  "5": "Part IXa",
  "6": "Part IXb",
  "7": "Part IXc",
  "8": "Part IXr",
  "9": "Part X",
  "10": "Parts IXb and IXc",
  "11": "Part VIIIA Category M",
  "12": "Part VIIIB",
  "13": "Part VIIIC",
  "14": "Part VIIID",
  "15": "Unlisted Special",
  "16": "Extemporaneous",
  "17": "Part XV Borderline",
  "18": "Branded Not In Part VIII",
};

const DRUG_TARIFF_CATEGORY_STYLE: Record<string, { light: string; dark: string }> = {
  "Not Specified": { light: "bg-gray-100 text-gray-600", dark: "bg-gray-500/15 text-gray-400" },
  "Part VIIIA Category A": { light: "bg-blue-100 text-blue-700", dark: "bg-blue-500/15 text-blue-400" },
  "Part VIIIA Category C": { light: "bg-cyan-100 text-cyan-700", dark: "bg-cyan-500/15 text-cyan-400" },
  "Part VIIIA Category M": { light: "bg-indigo-100 text-indigo-700", dark: "bg-indigo-500/15 text-indigo-400" },
  "Part VIIIB": { light: "bg-amber-100 text-amber-700", dark: "bg-amber-500/15 text-amber-400" },
  "Part VIIIC": { light: "bg-purple-100 text-purple-700", dark: "bg-purple-500/15 text-purple-400" },
  "Part VIIID": { light: "bg-pink-100 text-pink-700", dark: "bg-pink-500/15 text-pink-400" },
  "Part IXa": { light: "bg-teal-100 text-teal-700", dark: "bg-teal-500/15 text-teal-400" },
  "Part IXb": { light: "bg-teal-100 text-teal-700", dark: "bg-teal-500/15 text-teal-400" },
  "Part IXc": { light: "bg-teal-100 text-teal-700", dark: "bg-teal-500/15 text-teal-400" },
  "Parts IXb and IXc": { light: "bg-teal-100 text-teal-700", dark: "bg-teal-500/15 text-teal-400" },
  "Part IXr": { light: "bg-lime-100 text-lime-700", dark: "bg-lime-500/15 text-lime-400" },
  "Part X": { light: "bg-sky-100 text-sky-700", dark: "bg-sky-500/15 text-sky-400" },
  "Unlisted Special": { light: "bg-red-100 text-red-700", dark: "bg-red-500/15 text-red-400" },
  "Extemporaneous": { light: "bg-orange-100 text-orange-700", dark: "bg-orange-500/15 text-orange-400" },
  "Part XV Borderline": { light: "bg-fuchsia-100 text-fuchsia-700", dark: "bg-fuchsia-500/15 text-fuchsia-400" },
  "Branded Not In Part VIII": { light: "bg-violet-100 text-violet-700", dark: "bg-violet-500/15 text-violet-400" },
};

function drugTariffCategoryLabel(raw: any): string {
  const key = String(raw ?? "").trim();
  return DRUG_TARIFF_CATEGORY_LABELS[key] ?? DRUG_TARIFF_CATEGORY_LABELS["0"];
}

function normaliseCategories(raw: any, _drug: string): string[] {
  const list = Array.isArray(raw)
    ? raw
    : String(raw ?? "").split("|").map(s => s.trim()).filter(Boolean);
  const labels = Array.from(new Set(list.map(drugTariffCategoryLabel)));
  const specified = labels.filter(l => l !== DRUG_TARIFF_CATEGORY_LABELS["0"]);
  if (specified.length > 0) return specified;
  return labels.length > 0 ? labels : [drugTariffCategoryLabel(null)];
}

// The same pipe-separated `category` field as numbers, kept alongside the
// labels so the Part V deduction rate can be looked up by id. Unknown or
// unparseable ids are dropped, which lands the row on the branded rate.
function parseCategoryIds(raw: any): number[] {
  const list = Array.isArray(raw)
    ? raw
    : String(raw ?? "").split("|").map(s => s.trim()).filter(Boolean);
  const ids = list
    .map(v => Number(String(v).trim()))
    .filter(n => Number.isInteger(n));
  const specified = ids.filter(n => n !== 0);
  return Array.from(new Set(specified.length > 0 ? specified : ids));
}
// A claim row's DT categories, and its primary (first) category for the
// single-value contexts (group-by / filter dropdown / counts).
function rowCategories(row: ClaimRow): string[] {
  return row.categories && row.categories.length > 0 ? row.categories : [classifyDrugCategory(row.drug)];
}
function rowPrimaryCategory(row: ClaimRow): string {
  return rowCategories(row)[0] ?? "Unclassified";
}

// Every drug on the claim. Falls back to the joined display string for rows that
// never came from get-claims (blocked claims, session snapshots).
function rowDrugNames(row: ClaimRow): string[] {
  if (row.drugNames && row.drugNames.length > 0) return row.drugNames;
  const single = (row.drug ?? "").trim();
  return single ? [single] : [];
}

function rowDrugLabel(row: ClaimRow): string {
  const names = rowDrugNames(row);
  return names.length > 0 ? names.join(", ") : "—";
}

const uniqueDrugNames = (list: ClaimRow[]): string[] =>
  Array.from(new Set(list.flatMap(rowDrugNames)));

// A category can hold hundreds of claims, so the group subtitle lists a few
// drugs and counts the rest instead of running the table off the screen.
const DRUG_SUMMARY_LIMIT = 4;
const summariseDrugNames = (names: string[]): string => {
  if (names.length <= DRUG_SUMMARY_LIMIT) return names.join(", ");
  return `${names.slice(0, DRUG_SUMMARY_LIMIT).join(", ")} +${names.length - DRUG_SUMMARY_LIMIT} more`;
};

// Combined "DT / Net Reimb" display - net reimbursement is shown in red when
// it's lower than the DT price, or the normal text colour when they match.
function DtNetReimbCell({ dt, netReimb, dark }: { dt: number; netReimb: number; dark: boolean }) {
  const lower = netReimb < dt - 0.005;
  return (
    <span className={`font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>
      £{dt.toFixed(2)}
      <span className={dark ? "text-gray-600" : "text-gray-400"}> / </span>
      <span className={lower ? (dark ? "text-red-400" : "text-red-600") : (dark ? "text-gray-300" : "text-gray-700")}>
        £{netReimb.toFixed(2)}
      </span>
    </span>
  );
}

// Consolidated per-row status: RTEC result plus any attention/value signals,
// rendered as compact colour-coded icon chips. Each chip has a rich hover
// overlay explaining what it means for this specific prescription. Accepts one
// or more claim lines so it works for single rows and grouped prescription rows.
function StatusChips({ items, dark, resolveRtec }: { items: ClaimRow[]; dark: boolean; resolveRtec?: (row: ClaimRow) => RtecStatus }) {
  const red = dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700";
  const amber = dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700";
  const green = dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700";
  const grey = dark ? "bg-gray-500/20 text-gray-400" : "bg-gray-200 text-gray-600";

  const n = items.length;
  const isGroup = n > 1;
  const drugNames = uniqueDrugNames;

  const failedItems = items.filter(rowHasFailedSubmission);
  const incompleteItems = items.filter(claimNeedsEndorsement);
  const exemptionItems = items.filter(claimNeedsExemption);
  const expiredItems = items.filter(r => getExpiryStatus(r) === "Expired");
  const expiringItems = items.filter(r => getExpiryStatus(r) === "Expiring");

  const rtecStatuses = items.map(resolveRtec ?? getRtecStatus);
  const rtec: RtecStatus = rtecStatuses.includes("RTEC Not Exempt")
    ? "RTEC Not Exempt"
    : rtecStatuses.includes("RTEC Not Done")
      ? "RTEC Not Done"
      : "RTEC Exempt";
  const rtecStyle = rtec === "RTEC Exempt" ? green : rtec === "RTEC Not Exempt" ? red : grey;
  const rtecIcon = rtec === "RTEC Exempt"
    ? <ShieldCheck className="w-3.5 h-3.5" />
    : rtec === "RTEC Not Exempt"
      ? <ShieldAlert className="w-3.5 h-3.5" />
      : <ShieldQuestion className="w-3.5 h-3.5" />;

  const rtecBody = rtec === "RTEC Exempt"
    ? "The NHS BSA real-time check confirms the patient is exempt from the prescription charge — no charge to collect."
    : rtec === "RTEC Not Exempt"
      ? "The real-time check indicates the patient is NOT exempt, so a prescription charge may be payable. Verify the declared exemption before claiming."
      : "No real-time exemption check has been run for this prescription yet. Perform an RTEC check to confirm the patient's status.";

  const drugLine = (list: ClaimRow[]) => isGroup ? drugNames(list).join(", ") : null;

  type Chip = { key: string; label: string; body: React.ReactNode; style: string; icon: React.ReactNode };

  // Warnings: things that need action before a claim can be safely submitted.
  const warnings: Chip[] = [];

  if (failedItems.length > 0) warnings.push({
    key: "failed", label: "Failed", style: red, icon: <X className="w-3.5 h-3.5" />,
    body: (
      <>
        {isGroup
          ? `${failedItems.length} of ${n} items were rejected by the NHSBSA and need to be corrected and resubmitted.`
          : "This claim was rejected by the NHSBSA. Correct the prescription and submit it again."}
        {drugLine(failedItems) && <span className="block mt-1 opacity-80">{drugLine(failedItems)}</span>}
      </>
    ),
  });

  if (rtec !== "RTEC Exempt") warnings.push({
    key: "rtec", label: rtec, style: rtecStyle, icon: rtecIcon,
    body: (
      <>
        {rtecBody}
        {isGroup && rtecStatuses.some(s => s !== rtec) && (
          <span className="block mt-1 opacity-80">Most significant result across {n} items.</span>
        )}
      </>
    ),
  });

  if (exemptionItems.length > 0) warnings.push({
    key: "exemption", label: "Missing Exemption", style: red, icon: <CircleAlert className="w-3.5 h-3.5" />,
    body: (
      <>
        {isGroup
          ? `${exemptionItems.length} of ${n} items have an unconfirmed exemption (Not Exempt / Evidence Not Seen). Confirm evidence before claiming.`
          : `Declared exemption is “${items[0].exemption}”. Confirm the patient's evidence or update the exemption status before this can be claimed.`}
        {drugLine(exemptionItems) && <span className="block mt-1 opacity-80">{drugLine(exemptionItems)}</span>}
      </>
    ),
  });

  if (incompleteItems.length > 0) warnings.push({
    key: "endorsement", label: "Incomplete Endorsement", style: red, icon: <Stamp className="w-3.5 h-3.5" />,
    body: (
      <>
        {isGroup
          ? `${incompleteItems.length} of ${n} items still need a required endorsement before they can be claimed.`
          : "This item still needs its required endorsement(s) added before it can be claimed."}
        {drugLine(incompleteItems) && <span className="block mt-1 opacity-80">{drugLine(incompleteItems)}</span>}
      </>
    ),
  });

  if (expiredItems.length > 0) warnings.push({
    key: "expired", label: "Expired", style: red, icon: <PackageX className="w-3.5 h-3.5" />,
    body: (
      <>
        {isGroup
          ? `${expiredItems.length} of ${n} items are past their NHS dispensing window and can no longer be claimed as-is.`
          : "This prescription is past its NHS dispensing window and can no longer be claimed as-is."}
        {drugLine(expiredItems) && <span className="block mt-1 opacity-80">{drugLine(expiredItems)}</span>}
      </>
    ),
  });

  if (expiringItems.length > 0) warnings.push({
    key: "expiring", label: "Expiring", style: amber, icon: <CalendarClock className="w-3.5 h-3.5" />,
    body: (
      <>
        {isGroup
          ? `${expiringItems.length} of ${n} items are nearing the end of their NHS dispensing window. Dispense and claim soon.`
          : "This prescription is nearing the end of its NHS dispensing window. Dispense and claim soon."}
        {drugLine(expiringItems) && <span className="block mt-1 opacity-80">{drugLine(expiringItems)}</span>}
      </>
    ),
  });

  const allClear: Chip = {
    key: "clear", label: "All Clear", style: green, icon: <CircleCheck className="w-3.5 h-3.5" />,
    body: isGroup
      ? `All ${n} items are ready — exemptions confirmed and endorsements complete.`
      : "No outstanding actions — exemption confirmed and endorsement complete.",
  };
  const listed = warnings.length > 0 ? warnings : [allClear];
  const pill = warnings.length > 0
    ? {
        style: warnings.some(w => w.style === red) ? red : warnings.some(w => w.style === amber) ? amber : grey,
        icon: <TriangleAlert className="w-3.5 h-3.5" />,
        label: `${warnings.length} warning${warnings.length === 1 ? "" : "s"}`,
      }
    : allClear;
  const chipCls = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap";

  return (
    <td className="px-3 py-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} onClick={e => e.stopPropagation()} className={`${chipCls} cursor-default ${pill.style}`}>
            {pill.icon}
            {pill.label}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="end"
          className={`max-w-[300px] ${dark ? "bg-gray-900 border-gray-700 text-gray-100" : "bg-white border-gray-200 text-gray-900"}`}
        >
          <ul className="space-y-2.5">
            {listed.map(c => (
              <li key={c.key}>
                <span className={`${chipCls} ${c.style}`}>{c.icon}{c.label}</span>
                <div className="mt-1 text-[11px] leading-relaxed opacity-90">{c.body}</div>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </td>
  );
}

// Memoised: the claims table renders one of these per prescription with no
// pagination, so without this every checkbox tick re-rendered the whole list.
const ClaimRowLine = React.memo(function ClaimRowLine({ row, i, dark, isChecked, onToggle, onOpen, focusDrug, exemptionStyle, indent, nameOverride, itemCount, claimedAt, resolveRtec }: {
  row: ClaimRow;
  i: number;
  dark: boolean;
  isChecked: boolean;
  onToggle: (id: string) => void;
  onOpen: (row: ClaimRow, focusDrug?: string) => void;
  focusDrug?: string;
  exemptionStyle: Record<string, string>;
  indent?: boolean;
  nameOverride?: string;
  itemCount?: number;
  claimedAt?: string;
  resolveRtec?: (row: ClaimRow) => RtecStatus;
}) {
  const netReimb = calcNetReimb(row);
  return (
    <tr onClick={() => onOpen(row, focusDrug)} className={`border-t transition-colors cursor-pointer ${dark ? "border-gray-800 hover:bg-gray-800/50" : "border-gray-100 hover:bg-gray-50"} ${isChecked ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : indent ? dark ? "bg-gray-900/50" : "bg-gray-50/50" : i % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"}`}>
      <td className={`pl-3 pr-3 py-3 cursor-pointer ${indent ? "pl-9" : ""}`} onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onToggle(row.id); }}>
        <Checkbox checked={isChecked} onCheckedChange={() => onToggle(row.id)} />
      </td>
      <td className={`pl-1 pr-6 py-3 font-medium ${indent ? "pl-6" : ""} ${dark ? "text-white" : "text-gray-900"}`}>
        <div className="leading-tight">
          <span>{nameOverride ?? row.patient}</span>
          {rowDrugNames(row).length > 0 && (
            <span title={rowDrugLabel(row)} className={`block max-w-[260px] truncate text-[10px] font-normal ${dark ? "text-gray-500" : "text-gray-400"}`}>
              {rowDrugLabel(row)}
            </span>
          )}
        </div>
      </td>
      <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>{ageDisplay(row)}</td>
      <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>
        {(() => {
          const count = itemCount ?? 1;
          const chargeable = isMissingExemption(row.exemption);
          const chargeCount = chargeable ? count : 0;
          return (
            <div className="leading-tight">
              <span>{count}</span>
              <span className={`block text-[10px] font-normal ${dark ? "text-gray-500" : "text-gray-400"}`}>{chargeCount} NHS charge{chargeCount !== 1 ? "s" : ""}</span>
            </div>
          );
        })()}
      </td>
      <td className={`px-3 py-3 text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>{rowPrescriptionType(row)}</td>
      <td className="px-3 py-3"><div className="flex flex-wrap gap-1 max-w-[180px]">{rowCategories(row).map(cat => <CategoryTag key={cat} category={cat} dark={dark} />)}</div></td>
      <td className="px-3 py-3">
        <div className="flex flex-col items-start gap-1">
          <span className={`text-sm ${row.exemption === MISSING_EXEMPTION ? (dark ? "text-red-400 font-medium" : "text-red-600 font-medium") : dark ? "text-gray-300" : "text-gray-700"}`}>{row.exemption === MISSING_EXEMPTION ? "Missing" : row.exemption}</span>
          <RtecChip status={resolveRtec ? resolveRtec(row) : getRtecStatus(row)} dark={dark} reason={row.rtecAuditReason} />
        </div>
      </td>
      <td className={`px-3 py-3 ${dark ? "text-gray-300" : "text-gray-700"}`}>{row.rxDate}</td>
      {claimedAt !== undefined && <td className={`px-3 py-3 ${dark ? "text-gray-300" : "text-gray-700"}`}>{claimedAt}</td>}
      <td className="px-3 py-3 text-right"><DtNetReimbCell dt={parseFloat(row.dtPrice)} netReimb={netReimb} dark={dark} /></td>
      <StatusChips items={[row]} dark={dark} resolveRtec={resolveRtec} />
    </tr>
  );
});

// get-claims returns the exemption as a PrescriptionExemptionStatusTypes value
// in exemptionId, so the column is rendered from the enum. Labels are the short
// form of each FP10 exemption reason so they fit the column width.
const EXEMPTION_ID_LABELS: Record<number, string> = {
  1: "Not Exempt",
  2: "Age exempt",
  3: "16-18 Education",
  4: "Over 60",
  5: "Maternity",
  6: "Medical",
  7: "Prepayment",
  8: "MoD Certificate",
  9: "HC2",
  10: "Contraceptive",
  11: "Income Support",
  12: "Jobseeker's",
  13: "Tax Credit",
  14: "Pension Credit",
  15: "Detained Patient",
  16: "Paid (Old Rate)",
  17: "Universal Credit",
  18: "Sexual Health",
  19: "RTEC Maternity",
  20: "RTEC No Charge",
  21: "HRT",
  22: "No Charge (Wales)",
  23: "Welsh Entitlement",
};

// 0 (not entered) and 99 (status not found) both mean nothing is on record.
const exemptionLabelFromApi = (r: any, needsExemption: boolean): string => {
  const id = typeof r?.exemptionId === "number" ? r.exemptionId : undefined;
  const label = id !== undefined ? EXEMPTION_ID_LABELS[id] : undefined;
  if (label) return label;
  if (r?.exemptionStatus) return String(r.exemptionStatus);
  return needsExemption || id === 0 || id === 99 ? MISSING_EXEMPTION : "—";
};

const mapApiClaimRow = (r: any): ClaimRow => {
  const claimStatus = typeof r.claimStatus === "number" ? r.claimStatus : -1;
  const needsExemption = CLAIM_STATUS_REQUIRES_EXEMPTION.includes(claimStatus);
  // The claim is made against the reimbursement price, so that is the Claim DT
  // price. tradePrice is what the pharmacy paid, i.e. the cost side.
  const dt = parsePrice(r.reimbursementPrice);
  const cost = parsePrice(r.tradePrice);
  const drugNames = parseDrugNames(r.drugNames);
  return {
    id: r.prescriptionId,
    patient: r.patientName ?? "—",
    drug: drugNames.join(", "),
    drugNames,
    exemption: exemptionLabelFromApi(r, needsExemption) as any,
    lastExemption: r.lastExemption ?? "—",
    rxDate: fmtClaimDate(firstRealDate(r.prescriptionDate, r.prescriptionReceived)),
    epsBarcode: r.epsBarcode ?? "—",
    treatmentType: (TREATMENT_TYPE_LABELS[r.prescriptionTreatmentType ?? ""] ?? "Acute") as any,
    dtPrice: Number.isFinite(dt) ? String(dt) : "0",
    costPrice: Number.isFinite(cost) ? String(cost) : "0",
    endorsed: claimStatus !== CLAIM_STATUS_TO_BE_ENDORSED,
    itemCount: typeof r.itemCount === "number" ? r.itemCount : undefined,
    prescriptionIncomingId: r.prescriptionIncomingId,
    nhsNumber: r.nhsNumber ?? undefined,
    prescriptionTreatmentType: r.prescriptionTreatmentType ?? null,
    claimStatus,
    dob: r.dob ?? undefined,
    age: ageFromDob(r.dob),
    categories: normaliseCategories(r.category, ""),
    categoryIds: parseCategoryIds(r.category),
    optimiseIncome: r.optimiseIncome ?? "",
    rtecAuditReason: r.rtecAuditReason ?? "",
    sourceTypeId: numOrUndefined(r.sourceTypeId),
    prescriptionType: prescriptionTypeOf(r.sourceTypeId),
  };
};

type ClaimedRow = ClaimRow & { _source: "Claim" | "Recovered Block"; _claimedAt: string };

const ROWS_PAGE_SIZE = 400;

function ShowMoreRow({ shown, total, columns, dark, onShowMore }: {
  shown: number; total: number; columns: number; dark: boolean; onShowMore: () => void;
}) {
  return (
    <tr className={`border-t ${dark ? "border-gray-800" : "border-gray-100"}`}>
      <td colSpan={columns} className="py-4 text-center">
        <button
          type="button"
          onClick={onShowMore}
          className={`text-xs font-medium px-4 py-2 rounded-lg cursor-pointer transition-colors ${dark ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
        >
          Show {Math.min(ROWS_PAGE_SIZE, total - shown)} more
        </button>
        <span className={`block mt-2 text-[11px] ${dark ? "text-gray-500" : "text-gray-400"}`}>
          Showing {shown} of {total}
        </span>
      </td>
    </tr>
  );
}

export default function ClaimsTab({ isDarkMode, selectedBranches, onBranchChange }: { isDarkMode: boolean; selectedBranches: string[]; onBranchChange: (id: string) => void }) {
  const dark = isDarkMode;
  // Subscribe to the one slice this tab reads. useTitanApp() registers ~30
  // useSelector subscriptions, so an unrelated dispatch re-ran the entire
  // derived-data pipeline below (map → count → 6× filter → sort) for nothing.
  const titanTabState = useSelector((state: any) => state.titanTab);
  // Branch list + per-branch metrics come from the shared get-dashboard-stats
  // payload (loaded once by MultiBranchDashboard). Colours are assigned locally.
  const dashboardStats: DashboardPharmacy[] = React.useMemo(
    () => (Array.isArray(titanTabState?.dashboardStats) ? titanTabState.dashboardStats : []),
    [titanTabState?.dashboardStats],
  );
  const pharmacies = React.useMemo(
    () =>
      getBranchEntries(dashboardStats).map((p, i) => ({
        id: p.pharmacyId,
        name: p.name,
        color: BRANCH_COLORS[i % BRANCH_COLORS.length],
        metrics: p.metrics,
      })),
    [dashboardStats],
  );
  // get-claims returns Result<EndorseDataView>; after the slice unwrap the state
  // holds the EndorseDataView object { schema, data: [...] }. The claim rows are
  // its `data` array — fall back to a bare array in case the shape ever flattens.
  const titanClaimsState: any = titanTabState?.titanClaims;
  const titanClaims: any[] = Array.isArray(titanClaimsState)
    ? titanClaimsState
    : Array.isArray(titanClaimsState?.data)
      ? titanClaimsState.data
      : [];
  const isTitanClaimsLoading: boolean = titanTabState?.isTitanClaimsLoading ?? false;
  const unwrapClaimRows = (s: any): any[] =>
    Array.isArray(s) ? s : Array.isArray(s?.data) ? s.data : [];
  const claimedCurrentClaims: any[] = unwrapClaimRows(titanTabState?.claimedCurrentClaims);
  const claimedLastClaims: any[] = unwrapClaimRows(titanTabState?.claimedLastClaims);
  const isClaimedCurrentLoading: boolean = titanTabState?.isClaimedCurrentClaimsLoading ?? false;
  const isClaimedLastLoading: boolean = titanTabState?.isClaimedLastClaimsLoading ?? false;
  const blockedClaimsRaw: any[] = (titanTabState?.blockedClaims || []) as any[];
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimsLoadError, setClaimsLoadError] = useState<{ branch: string; message: string } | null>(null);
  // Snapshot of rows claimed this session so the Claimed tab still shows them
  // after they leave the live to-be-claimed list on refresh.
  const [claimedSnapshots, setClaimedSnapshots] = useState<Record<string, any>>({});
  const [showOverview, setShowOverview] = useState(true);
  const [overviewSelectedIds, setOverviewSelectedIds] = useState<Set<string>>(new Set());
  const [claimsView, setClaimsView] = useState<"claims" | "blocked" | "claimed">("claims");
  const [visitedViews, setVisitedViews] = useState<Set<"claims" | "blocked" | "claimed">>(() => new Set(["claims"]));
  const [claimedCycle, setClaimedCycle] = useState<"current" | "last">("current");
  const [claimedClaimIds, setClaimedClaimIds] = useState<Set<string>>(new Set());
  const [claimedBlockedIds, setClaimedBlockedIds] = useState<Set<string>>(new Set());
  const [claimedAtMap, setClaimedAtMap] = useState<Record<string, string>>({});
  const [claimedSearch, setClaimedSearch] = useState("");
  const [claimedGroupBy, setClaimedGroupBy] = useState<"none" | "drug" | "patient">("none");
  const [expandedClaimedGroups, setExpandedClaimedGroups] = useState<Set<string>>(new Set());
  const [claimedSelectedIds, setClaimedSelectedIds] = useState<Set<string>>(new Set());
  const [claimedCategoryFilter, setClaimedCategoryFilter] = useState<string>("All");
  const [claimedExemptionFilter, setClaimedExemptionFilter] = useState<string>("All");
  const [claimedFilterOpen, setClaimedFilterOpen] = useState(false);
  const [pendingClaimedGroupBy, setPendingClaimedGroupBy] = useState<"none" | "drug" | "patient">("none");
  const [pendingClaimedCategoryFilter, setPendingClaimedCategoryFilter] = useState<string>("All");
  const [pendingClaimedExemptionFilter, setPendingClaimedExemptionFilter] = useState<string>("All");
  const [activeBranch, setActiveBranch] = useState(selectedBranches[0] ?? "");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [blockedFilter, setBlockedFilter] = useState<string>("All");
  const [endorsedFilter, setEndorsedFilter] = useState<"all" | "endorsed" | "not-endorsed">("all");
  const [blockedSelectedIds, setBlockedSelectedIds] = useState<Set<string>>(new Set());
  const [expandedDrugs, setExpandedDrugs] = useState<Set<string>>(new Set());
  const [prescriptionModal, setPrescriptionModal] = useState<PrescriptionDetail | null>(null);
  const [exemptionFilter, setExemptionFilter] = useState<string>("All");
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  // Manual claims are the exception, so the list opens on EPS only and the
  // pharmacist switches the filter when they want to see the rest.
  const [pendingCategoryFilter, setPendingCategoryFilter] = useState<string>("All");
  const [pendingPrescriptionType, setPendingPrescriptionType] = useState<"All" | "EPS" | "Manual">("All");
  const [claimsGroupBy, setClaimsGroupBy] = useState<"none" | "drug" | "patient" | "category">("none");
  const [expandedClaimGroups, setExpandedClaimGroups] = useState<Set<string>>(new Set());
  const [claimAttentionWarningOpen, setClaimAttentionWarningOpen] = useState(false);
  const [rtecOverrides, setRtecOverrides] = useState<Record<string, RtecStatus>>({});
  const [rtecBulkChecking, setRtecBulkChecking] = useState(false);
  const [rtecBulkMessage, setRtecBulkMessage] = useState<string | null>(null);
  const [copyingExemptions, setCopyingExemptions] = useState(false);
  const [clearClaimsWarningOpen, setClearClaimsWarningOpen] = useState(false);
  const [clearingClaims, setClearingClaims] = useState(false);
  const [clearedClaimIds, setClearedClaimIds] = useState<Set<string>>(new Set());
  const [claimRowPatches, setClaimRowPatches] = useState<Record<string, Record<string, any>>>({});
  const resolveRtec = React.useCallback((row: ClaimRow): RtecStatus => rtecOverrides[row.id] ?? getRtecStatus(row), [rtecOverrides]);
  const [patientSearch, setPatientSearch] = useState("");
  const [claimsAttentionFilter, setClaimsAttentionFilter] = useState<ClaimStatusFilter>("all");
  const [claimsExcludedTiles, setClaimsExcludedTiles] = useState<Set<string>>(new Set(DEFAULT_EXCLUDED_TILES));
  const [claimsFilterOpen, setClaimsFilterOpen] = useState(false);
  const [pendingExemptionFilter, setPendingExemptionFilter] = useState<string>("All");
  const [pendingAttentionFilter, setPendingAttentionFilter] = useState<ClaimStatusFilter>("all");
  const [sortColumn, setSortColumn] = useState< "patient" | "age" | "epsBarcode" | "exemption" | "prescriptionType" | "rxDate" | "dtPrice" | "netReimb" | "attention">("patient");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [claimsVisibleCount, setClaimsVisibleCount] = useState(ROWS_PAGE_SIZE);
  const [claimedVisibleCount, setClaimedVisibleCount] = useState(ROWS_PAGE_SIZE);
  const [blockedVisibleCount, setBlockedVisibleCount] = useState(ROWS_PAGE_SIZE);
  const [blockedSearch, setBlockedSearch] = useState("");
  const [blockedFilterOpen, setBlockedFilterOpen] = useState(false);
  const [pendingBlockedFilter, setPendingBlockedFilter] = useState<string>("All");
  const [pendingEndorsedFilter, setPendingEndorsedFilter] = useState<"all" | "endorsed" | "not-endorsed">("all");

  // Keep activeBranch in sync if selectedBranches changes
  React.useEffect(() => {
    if (!selectedBranches.includes(activeBranch)) {
      setActiveBranch(selectedBranches[0] ?? "");
    }
  }, [selectedBranches, activeBranch]);

  // Sync activeBranch to the first pharmacy once the estate list loads.
  React.useEffect(() => {
    if (pharmacies.length > 0 && !pharmacies.some(p => p.id === activeBranch)) {
      setActiveBranch(pharmacies[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacies.length]);

  // Every claims API call routes its failure here; the dispatchers swallow the
  // error and hand back null, so a null result is the failure signal.
  const { toast } = useToast();
  const warn = (title: string, message: string) => toast({ variant: "destructive", title, description: message });
  const notify = (title: string, message: string) => toast({ variant: "success", title, description: message });

  // Fetch claims + blocked claims once a branch is opened (not on the overview).
  React.useEffect(() => {
    if (showOverview || !activeBranch) return;
    if (pharmacies.some(p => p.id === activeBranch)) {
      loadClaims(activeBranch);
      loadBlockedClaims(activeBranch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch, pharmacies.length, showOverview]);

  // Prefer whatever the API said went wrong (e.g. "Your account is not linked
  // to Titan") over the generic copy — it tells the user what to do next.
  const apiFailure = () => {
    const capture = { message: "" };
    return {
      onError: (message: string) => { capture.message = message; },
      reason: (fallback: string) => capture.message || fallback,
    };
  };

  const loadClaims = async (branch: string) => {
    const failure = apiFailure();
    if (await getTitanClaims(branch, failure.onError) === null) {
      const message = failure.reason("The claims list couldn't be loaded. Please try again.");
      setClaimsLoadError({ branch, message });
      warn("Couldn't load claims", message);
      return;
    }
    setClaimsLoadError(prev => (prev?.branch === branch ? null : prev));
  };

  const loadBlockedClaims = async (branch: string) => {
    const failure = apiFailure();
    if (await getBlockedClaims(branch, failure.onError) === null)
      warn("Couldn't load blocked claims", failure.reason("The blocked claims list couldn't be loaded. Please try again."));
  };

  const claimedLoadedFor = React.useRef<{ current?: string; last?: string }>({});
  const invalidateClaimedCycles = () => { claimedLoadedFor.current = {}; };

  const refreshClaimedCycle = async () => {
    if (!activeBranch) return;
    const cycle = claimedCycle;
    claimedLoadedFor.current[cycle] = activeBranch;
    const failure = apiFailure();
    const result = cycle === "last"
      ? await getClaimedLast(activeBranch, failure.onError)
      : await getClaimedCurrent(activeBranch, failure.onError);
    if (result === null) {
      if (claimedLoadedFor.current[cycle] === activeBranch) delete claimedLoadedFor.current[cycle];
      warn("Couldn't load claimed items", failure.reason("The claimed list couldn't be loaded. Please try again."));
    }
  };

  React.useEffect(() => {
    if (showOverview) invalidateClaimedCycles();
  }, [showOverview]);

  React.useEffect(() => {
    if (showOverview || claimsView !== "claimed" || !activeBranch) return;
    if (!pharmacies.some(p => p.id === activeBranch)) return;
    if (claimedLoadedFor.current[claimedCycle] === activeBranch) return;
    refreshClaimedCycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch, claimsView, claimedCycle, showOverview]);

  React.useEffect(() => {
    setVisitedViews(prev => (prev.has(claimsView) ? prev : new Set(prev).add(claimsView)));
  }, [claimsView]);

  React.useEffect(() => {
    setClaimsVisibleCount(ROWS_PAGE_SIZE);
  }, [activeBranch, exemptionFilter, categoryFilter, patientSearch, claimsAttentionFilter, claimsGroupBy, sortColumn, sortDirection]);

  React.useEffect(() => {
    setClaimedVisibleCount(ROWS_PAGE_SIZE);
  }, [activeBranch, claimedCycle, claimedCategoryFilter, claimedExemptionFilter, claimedSearch, claimedGroupBy]);

  React.useEffect(() => {
    setBlockedVisibleCount(ROWS_PAGE_SIZE);
  }, [activeBranch, blockedFilter, endorsedFilter, blockedSearch]);

  // Clear selection and filter when switching stores
  React.useEffect(() => {
    setVisitedViews(new Set([claimsView]));
    setSelectedIds(new Set());
    setExemptionFilter("All");
    setCategoryFilter("All");
    setExpandedClaimGroups(new Set());
    setPatientSearch("");
    setClaimsAttentionFilter("all");
    setClaimsExcludedTiles(new Set(DEFAULT_EXCLUDED_TILES));
    setClaimsGroupBy("none");
    setClaimedClaimIds(new Set());
    setClaimedBlockedIds(new Set());
    setClaimedAtMap({});
    setClaimedGroupBy("none");
    setExpandedClaimedGroups(new Set());
    setClaimedSelectedIds(new Set());
    setClaimedCategoryFilter("All");
    setClaimedExemptionFilter("All");
    setRtecOverrides({});
    setClearedClaimIds(new Set());
    setClearClaimsWarningOpen(false);
  }, [activeBranch]);

  // Map the real get-claims payload (prescription-level) into the Builder ClaimRow
  // shape. Warnings are driven by the backend ClaimStatus enum (see
  // claimNeedsExemption / claimNeedsEndorsement): 4/9 => Missing Exemption,
  // 10 => Incomplete Endorsement. dob/dtPrice/netReimb/category/optimiseIncome
  // are now supplied by the API and mapped straight through.
  // mapApiClaimRow parses dates, derives age and normalises categories per row,
  // so this only re-runs when the claims payload itself changes.
  React.useEffect(() => { setClaimRowPatches({}); }, [titanClaimsState]);
  const allRows: ClaimRow[] = React.useMemo(
    () => titanClaims
      .map(r => mapApiClaimRow(claimRowPatches[r.prescriptionId] ? { ...r, ...claimRowPatches[r.prescriptionId] } : r))
      .filter(r => !clearedClaimIds.has(r.id)),
    [titanClaims, claimRowPatches, clearedClaimIds],
  );
  const apiClaimedRows = React.useMemo(
    () =>
      (claimedCycle === "last" ? claimedLastClaims : claimedCurrentClaims).map((r: any) => ({
        ...mapApiClaimRow(r),
        _source: "Claim" as const,
        _claimedAt: fmtClaimDate(r.claimDate),
      })),
    [claimedCycle, claimedLastClaims, claimedCurrentClaims],
  );
  const activeBranchData = React.useMemo(() => pharmacies.find(p => p.id === activeBranch), [pharmacies, activeBranch]);

  // Exemption counts
  const { exemptionCounts, exemptionTypes } = React.useMemo(() => {
    const counts: Record<string, number> = {};
    allRows.forEach(r => { counts[r.exemption] = (counts[r.exemption] || 0) + 1; });
    return { exemptionCounts: counts, exemptionTypes: ["All", ...Object.keys(counts)] };
  }, [allRows]);

  // Drug Tariff category counts
  const { categoryCounts, categoryTypes } = React.useMemo(() => {
    const counts: Record<string, number> = {};
    allRows.forEach(r => { rowCategories(r).forEach(c => { counts[c] = (counts[c] || 0) + 1; }); });
    return { categoryCounts: counts, categoryTypes: ["All", ...Object.keys(counts)] };
  }, [allRows]);

  // Single pass instead of six chained .filter() calls, each of which allocated
  // a fresh array over the full claim list on every render.
  const preTileRows = React.useMemo(() => {
    const search = patientSearch.trim();
    return filterRowsByClaimSearch(allRows.filter(r =>
      (exemptionFilter === "All" || r.exemption === exemptionFilter) &&
      (categoryFilter === "All" || rowCategories(r).includes(categoryFilter)) &&
      !claimedClaimIds.has(r.id) &&
      rowMatchesStatusFilter(r, claimsAttentionFilter, resolveRtec)
    ), search);
  }, [allRows, exemptionFilter, categoryFilter, claimedClaimIds, patientSearch, claimsAttentionFilter, resolveRtec]);

  // The headline tiles double as filters: unticking one drops its claims from
  // the table without touching the filter modal's own selections.
  const filteredRows = React.useMemo(() => {
    if (claimsExcludedTiles.size === 0) return preTileRows;
    return preTileRows.filter(r => {
      for (const key of claimsExcludedTiles) {
        if (key === "eps") { if (rowPrescriptionType(r) === "EPS") return false; continue; }
        if (key === "manual") { if (rowPrescriptionType(r) === "Manual") return false; continue; }
        if (key === "todo") { if (!rowHasFailedSubmission(r) && getAttentionReasons(r).length > 0) return false; continue; }
        if (key === "ready") { if (getAttentionReasons(r).length === 0) return false; continue; }
        if (key === "failed" && rowHasFailedSubmission(r)) return false;
      }
      return true;
    });
  }, [preTileRows, claimsExcludedTiles]);

  const rows = React.useMemo(() => {
    // Precompute each row's sort key once instead of recomputing it inside the
    // comparator, which called new Date()/getAttentionReasons() O(n log n) times.
    const keyed = filteredRows.map(r => {
      let key: any;
      switch (sortColumn) {
        case "patient": key = r.patient.toLowerCase(); break;
        case "age": key = r.age ?? 0; break;
        case "epsBarcode": key = r.epsBarcode.toLowerCase(); break;
        case "exemption": key = r.exemption.toLowerCase(); break;
        case "prescriptionType": key = rowPrescriptionType(r); break;
        case "rxDate": key = new Date(r.rxDate).getTime(); break;
        case "dtPrice": key = parseFloat(r.dtPrice.replace("£", "")); break;
        case "netReimb": key = calcNetReimb(r); break;
        case "attention": key = getAttentionReasons(r).length > 0 ? 0 : 1; break;
        default: key = r.patient.toLowerCase();
      }
      return { r, key };
    });
    keyed.sort((a, b) =>
      sortDirection === "asc"
        ? (a.key > b.key ? 1 : a.key < b.key ? -1 : 0)
        : (a.key < b.key ? 1 : a.key > b.key ? -1 : 0),
    );
    return keyed.map(k => k.r);
  }, [filteredRows, sortColumn, sortDirection]);

  const claimsPrescriptionType: "All" | "EPS" | "Manual" | "" = (() => {
    const noEps = claimsExcludedTiles.has("eps");
    const noManual = claimsExcludedTiles.has("manual");
    if (!noEps && !noManual) return "All";
    if (noManual && !noEps) return "EPS";
    if (noEps && !noManual) return "Manual";
    return "";
  })();

  const applyPrescriptionType = (value: "All" | "EPS" | "Manual") => {
    setClaimsExcludedTiles(prev => {
      const next = new Set(prev);
      next.delete("eps");
      next.delete("manual");
      if (value === "EPS") next.add("manual");
      if (value === "Manual") next.add("eps");
      return next;
    });
  };

  const claimsActiveFilterCount = (claimsPrescriptionType !== "All" ? 1 : 0) + (exemptionFilter !== "All" ? 1 : 0) + (categoryFilter !== "All" ? 1 : 0) + (claimsAttentionFilter !== "all" ? 1 : 0);

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < rows.length;

  // Stable identity — ClaimRowLine is memoised and takes this as a prop.
  const toggleOne = React.useCallback((id: string) => setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }), []);
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map(r => r.id)));
  };

  const claimGroups: [string, ClaimRow[]][] = React.useMemo(() => {
    if (claimsGroupBy === "none") return [];
    const groups = new Map<string, ClaimRow[]>();
    // A claim covering several drugs is listed under each of them, so the drug
    // groups total more rows than the claims list — that is intentional.
    const search = patientSearch.trim().toLowerCase();
    const groupKeysFor = (r: ClaimRow): string[] => {
      if (claimsGroupBy === "category") return [rowPrimaryCategory(r)];
      if (claimsGroupBy === "drug") {
        const names = rowDrugNames(r);
        const matched = search && !r.patient.toLowerCase().includes(search)
          ? names.filter(d => d.toLowerCase().includes(search))
          : names;
        return matched.length > 0 ? matched : ["—"];
      }
      return [r.patient];
    };
    rows.forEach(r => {
      groupKeysFor(r).forEach(groupKey => {
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey)!.push(r);
      });
    });
    if (claimsGroupBy === "drug") {
      const direction = sortColumn === "patient" && sortDirection === "desc" ? -1 : 1;
      return Array.from(groups.entries()).sort(
        (a, b) => direction * a[0].localeCompare(b[0], "en-GB", { sensitivity: "base" }),
      );
    }
    return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [rows, claimsGroupBy, patientSearch, sortColumn, sortDirection]);

  // Default (non-grouped) view: one row per prescription. Items sharing a
  // patient, Rx date and EPS barcode belong to the same prescription.
  const prescriptions: { key: string; patient: string; epsBarcode: string; rxDate: string; exemption: ClaimRow["exemption"]; lastExemption: string; items: ClaimRow[] }[] = React.useMemo(() => {
    const map = new Map<string, ClaimRow[]>();
    rows.forEach(r => {
      const key = r.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      patient: items[0].patient,
      epsBarcode: items[0].epsBarcode,
      rxDate: items[0].rxDate,
      exemption: items[0].exemption,
      lastExemption: items[0].lastExemption,
      items,
    }));
  }, [rows]);

  const toggleClaimGroup = (groupKey: string) => setExpandedClaimGroups(prev => {
    const next = new Set(prev);
    if (next.has(groupKey)) next.delete(groupKey);
    else next.add(groupKey);
    return next;
  });
  const toggleClaimGroupSelection = (ids: string[]) => setSelectedIds(prev => {
    const allIn = ids.every(id => prev.has(id));
    const next = new Set(prev);
    ids.forEach(id => { if (allIn) next.delete(id); else next.add(id); });
    return next;
  });

  // Stable identity so the memoised ClaimRowLine below isn't invalidated by a
  // fresh object literal on every render.
  const exemptionStyle: Record<string, string> = React.useMemo(() => ({
    "Exempt":             dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700",
    "Not Exempt":         dark ? "bg-red-500/15 text-red-400"     : "bg-red-100 text-red-700",
    "Evidence Not Seen":  dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700",
    "Maternity":          dark ? "bg-purple-500/15 text-purple-400" : "bg-purple-100 text-purple-700",
    "Medical":            dark ? "bg-blue-500/15 text-blue-400"   : "bg-blue-100 text-blue-700",
    "Prepayment":         dark ? "bg-cyan-500/15 text-cyan-400"   : "bg-cyan-100 text-cyan-700",
    "Missing":            dark ? "bg-red-500/15 text-red-400"     : "bg-red-100 text-red-700",
  }), [dark]);

  // Blocked claims data
  // Blocked claims — mapped from the get-blocked-claims API (grouped by drug,
  // each with per-patient entries). Fields the API doesn't return (rxDate,
  // prescriber, prices, concession) surface as "—" / zero.
  const blockedClaims: {
    id: string; prescriptionIncomingId: string; patient: string; drug: string; pack: string; rxDate: string; prescriber: string; epsBarcode: string; ruleCategory: string; ruleName: string; blockedDate: string; status: "pending" | "approved" | "rejected"; dtPrice: string; costPrice: string; concessionPrice?: string; concessionActive?: boolean; concessionStartDate?: string; endorsed: boolean;
  }[] = React.useMemo(() => blockedClaimsRaw.flatMap((item: any) =>
    (item?.entries ?? []).map((entry: any) => ({
      id: entry?.prescriptionIncomingId ?? entry?.patientId ?? `${item?.prescribedItemId}-${entry?.patientName}`,
      prescriptionIncomingId: entry?.prescriptionIncomingId ?? "",
      patient: entry?.patientName ?? "—",
      drug: item?.drugName ?? "—",
      pack: item?.packSize ?? "—",
      rxDate: "—",
      prescriber: "—",
      epsBarcode: entry?.barcode ?? "—",
      ruleCategory: item?.productRule != null ? (BLOCKED_CLAIM_RULE_LABELS[item.productRule] ?? "—") : "—",
      ruleName: item?.productRule != null ? (BLOCKED_CLAIM_RULE_LABELS[item.productRule] ?? "—") : "—",
      blockedDate: fmtClaimDate(item?.ruleExpiry),
      status: "pending" as const,
      dtPrice: "£0.00",
      costPrice: "£0.00",
      endorsed: Boolean(item?.endorsed && String(item.endorsed).trim() !== ""),
    }))
  ), [blockedClaimsRaw]);
  const blockedCategories = React.useMemo(() => ["All", ...Array.from(new Set(blockedClaims.map(c => c.ruleCategory)))], [blockedClaims]);
  const filteredBlockedClaims = React.useMemo(() => {
    const search = blockedSearch.trim().toLowerCase();
    return blockedClaims.filter(c =>
      (blockedFilter === "All" || c.ruleCategory === blockedFilter) &&
      (endorsedFilter === "all" || (endorsedFilter === "endorsed" ? c.endorsed : !c.endorsed)) &&
      !claimedBlockedIds.has(c.id) &&
      c.patient.toLowerCase().includes(search)
    );
  }, [blockedClaims, blockedFilter, endorsedFilter, claimedBlockedIds, blockedSearch]);
  const pendingBlockedCount = React.useMemo(() => blockedClaims.filter(c => c.status === "pending").length, [blockedClaims]);
  const filteredPendingCount = React.useMemo(() => filteredBlockedClaims.filter(c => c.status === "pending").length, [filteredBlockedClaims]);
  const blockedActiveFilterCount = (blockedFilter !== "All" ? 1 : 0) + (endorsedFilter !== "all" ? 1 : 0);

  // Group filtered blocked claims by drug
  const drugGroups = React.useMemo(() => {
    const blockedByDrug = filteredBlockedClaims.reduce<Record<string, typeof filteredBlockedClaims>>((acc, bc) => {
      if (!acc[bc.drug]) acc[bc.drug] = [];
      acc[bc.drug].push(bc);
      return acc;
    }, {});
    return Object.entries(blockedByDrug);
  }, [filteredBlockedClaims]);

  const toggleBlockedOne = (id: string) => setBlockedSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleBlockedGroup = (ids: string[]) => {
    setBlockedSelectedIds(prev => {
      const next = new Set(prev);
      const allIn = ids.every(id => next.has(id));
      if (allIn) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };
  const allBlockedSelected = filteredBlockedClaims.length > 0 && filteredBlockedClaims.every(c => blockedSelectedIds.has(c.id));
  const someBlockedSelected = filteredBlockedClaims.some(c => blockedSelectedIds.has(c.id)) && !allBlockedSelected;
  const toggleAllBlocked = () => {
    if (allBlockedSelected) setBlockedSelectedIds(new Set());
    else setBlockedSelectedIds(new Set(filteredBlockedClaims.map(c => c.id)));
  };
  const toggleDrugExpand = (drug: string) => setExpandedDrugs(prev => { const next = new Set(prev); if (next.has(drug)) next.delete(drug); else next.add(drug); return next; });


  const loadPrescriptionImage = React.useCallback((modalId: string, branch: string, incomingId: string) => {
    getPrescriptionImage(branch, incomingId).then(images => {
      const src = toImageDataUrl(images.length > 0 ? images[0] : undefined);
      setPrescriptionModal(prev => (prev && prev.id === modalId ? { ...prev, prescriptionImageUrl: src, isImageLoading: false } : prev));
    });
  }, []);

  // Stable identity — passed as onOpen to the memoised ClaimRowLine.
  const openClaimDetail = React.useCallback(async (row: ClaimRow, isClaimed = false, focusDrug?: string) => {
    const exemptionDisplay = row.exemption ?? "—";
    const canLoad = Boolean(activeBranch && row.prescriptionIncomingId);
    if (!canLoad) {
      showToast({
        variant: "destructive",
        title: "Couldn't open prescription",
        description: `This claim for ${row.patient} has no prescription reference, so its details can't be loaded.`,
      });
      return;
    }
    // Open immediately; prescription items + image are loaded from the API below
    // and flow into the modal (which resyncs its item list when they arrive).
    setPrescriptionModal({
      id: row.id, prescriptionId: row.id, prescriptionIncomingId: row.prescriptionIncomingId, dobIso: row.dob, isClaimed,
      focusDrug,
      patient: row.patient, rxDate: row.rxDate,
      prescriber: "—", epsBarcode: row.epsBarcode, prescriptionType: rowPrescriptionType(row), exemption: row.exemption,
      lastExemptionCheck: row.lastExemption, treatmentType: row.treatmentType,
      items: [],
      prescriptionImageUrl: undefined,
      nhsNumber: row.nhsNumber ?? "—",
      dob: fmtClaimDate(row.dob),
      chargeStatus: exemptionDisplay === MISSING_EXEMPTION ? undefined : exemptionDisplay === "Not Exempt" ? "Charge Paid" : "Exempt from Charge",
      // formType, dispensedDate, prescriber and nominatedPharmacy are not in any
      // claims payload yet, so they are left unset and render as "—" / hidden
      // rather than being invented. Populate them once the API carries them.
      isLoading: true,
      isImageLoading: true,
    });
    const data: any = await getPrescriptionByIncomingId({ prescriptionIncomingId: row.prescriptionIncomingId, pharmacyID: activeBranch });
    const model = data?.prescriptionGetModel;
    const apiItems: any[] = model?.prescriptionItems ?? [];
    if (!model || apiItems.length === 0) {
      setPrescriptionModal(prev => (prev && prev.id === row.id ? null : prev));
      showToast({
        variant: "destructive",
        title: "Couldn't open prescription",
        description: data
          ? `No prescription details were returned for ${row.patient}. The prescription may have been removed or is still being processed.`
          : `The prescription details for ${row.patient} couldn't be loaded. Please try again.`,
      });
      return;
    }
    const items: PrescriptionItem[] = apiItems.map((it: any, i: number) => {
      const drug = (it?.drugWrittenAs || it?.originalPrescribedDrugName || it?.prescribedItem?.writtenAs || "—").trim();
      // quantity is a decimal (e.g. 90.00) — drop trailing zeros for display.
      const qty = it?.quantity != null ? String(Number(it.quantity)) : "—";
      // unitText is the dispensed unit as recorded on the prescription item
      // ("unit dose", "ml", "capsules"); fall back to the drug's form only when
      // the API doesn't supply one.
      const unit = String(it?.unitText ?? "").trim() || dispensedFormFor(drug);
      const pack = qty !== "—" ? `${qty} ${unit}` : "—";
      // drugTariffPrice is the tariff price for a single unit, so the claim's DT
      // value is that price across the quantity dispensed.
      const dtUnitPrice = parsePrice(it?.drugTariffPrice);
      const dtQuantity = Number(it?.quantity);
      const dtTotal = dtUnitPrice * (Number.isFinite(dtQuantity) && dtQuantity > 0 ? dtQuantity : 1);
      return {
        id: it?.prescriptionItemId || `${row.id}-item-${i}`,
        drug,
        pack,
        quantity: qty,
        dosage: it?.dosageText || "—",
        isSelected: i === 0,
        endorsements: mapApiEndorsements(it?.additionalEndorsements),
        requiredEndorsements: [],
        prescriptionIncomingId: it?.prescriptionIncomingId,
        prescriptionItemId: it?.prescriptionItemId,
        notDispensedReasonCode: it?.notDispensedReasonCode ?? "",
        itemCrossedOut: Boolean(it?.itemCrossedOut),
        packagedDoses: it?.packagedDoses,
        unitText: unit,
        drugTariffCategory: numOrUndefined(it?.prescribedItem?.drugTariffCategoryType ?? it?.drugTariffCategoryType),
        prescribedItemId: it?.prescribedItemId || it?.prescribedItem?.prescribedItemId,
        endorsedProductId: it?.allowedEndorse?.productId || undefined,
        dispenseItemId: it?.dispenseItemId || undefined,
        claimDtPrice: Number.isFinite(dtTotal) ? `£${dtTotal.toFixed(2)}` : undefined,
      } as PrescriptionItem;
    });

    const resolvedPrescriptionId = isRealGuid(model?.prescriptionId) ? model.prescriptionId : row.id;
    const ids = Array.from(new Set(items.map(it => it.prescribedItemId).filter(Boolean)));
    const loadsItemInfo = ids.length > 0 || isRealGuid(resolvedPrescriptionId);
    const isCurrent = (prev: PrescriptionDetail | null) => !!prev && prev.id === row.id;

    setPrescriptionModal(prev => (isCurrent(prev) ? {
      ...prev,
      items,
      prescriptionId: model?.prescriptionId ?? prev.prescriptionId,
      exemptionRecord: model?.exemption ?? data?.exemption ?? null,
      isLoading: false,
      isItemInfoLoading: loadsItemInfo,
      isConcessionLoading: ids.length > 0,
    } : prev));
    const itemInfoRequest = loadsItemInfo
      ? Promise.all([
        ids.length ? getItemInfo(activeBranch, ids) : Promise.resolve([] as any[]),
        isRealGuid(resolvedPrescriptionId) ? getClaimPrescriptionItems(activeBranch, resolvedPrescriptionId) : Promise.resolve(null),
      ])
      : null;

    if (ids.length) {
      getConcessionPrices(activeBranch, ids).then(concessionPrices => {
        const concessionByItem = buildConcessionProducts(concessionPrices);
        setPrescriptionModal(prev => (isCurrent(prev) ? {
          ...prev,
          items: prev.items.map(it => ({ ...it, concession: it.prescribedItemId ? concessionByItem.get(String(it.prescribedItemId).toLowerCase()) : undefined })),
          isConcessionLoading: false,
        } : prev));
      });
    }

    loadPrescriptionImage(row.id, activeBranch, row.prescriptionIncomingId!);

    if (!itemInfoRequest) return;
    const [products, savedRecord] = await itemInfoRequest;
    const savedItems = savedClaimItemsLookup(savedRecord);
    const byItem = new Map<string, any[]>();
    (Array.isArray(products) ? products : []).forEach((p: any) => {
      const key = p?.prescribedItemId;
      if (!key) return;
      if (!byItem.has(key)) byItem.set(key, []);
      byItem.get(key)!.push(p);
    });
    const enriched = items.map(base => {
      const it: PrescriptionItem = { ...base };
      const saved = savedItems.size > 0 ? findSavedClaimItem(savedItems, it) : undefined;
      if (saved) applySavedClaimItem(it, saved);
      const raw = it.prescribedItemId ? byItem.get(it.prescribedItemId) ?? [] : [];
      const dispensedQty = parseInt(it.quantity, 10) || 0;
      const mapped = raw.map((p: any) => {
        const packSize = Number(p?.packSize) || 0;
        const tradePrice = Number(p?.tradePrice) || 0;
        const perUnit = packSize > 0 ? dispensedQty / packSize : 0;
        const company = String(p?.company ?? "").trim();
        const description = String(p?.description ?? it.drug ?? "").trim();
        const rawCategory = numOrUndefined(p?.drugTariffCategory);
        const drugTariffCategory = rawCategory === 0 ? undefined : rawCategory;
        const notInDt = drugTariffCategory == null && company.toLowerCase() !== NO_BRAND_COMPANY;
        const reimbursement = notInDt ? 0 : Number(p?.reimbursementPrice) || 0;
        return {
          productId: String(p?.productId ?? ""),
          brandName: company ? `${description} (${company})` : description || "—",
          description,
          company,
          packSize,
          tradePrice,
          reimbursement,
          costForQty: tradePrice * perUnit,
          reimbForQty: reimbursement * perUnit,
          drugTariffCategory,
        } as ItemProduct;
      });
      const priced = mapped.filter(p => isNoBrandProduct(p) || isNotInDrugTariff(p) || (p.tradePrice > 0 && p.reimbursement > 0));
      it.products = sameDrugProducts(priced, it.drug, it.endorsedProductId);
      const savedProductId = saved?.productId;
      if (isRealGuid(savedProductId) && !it.products.some(p => p.productId === savedProductId)) {
        const savedProduct = mapped.find(p => p.productId === savedProductId);
        if (savedProduct) it.products = [...it.products, savedProduct];
      }
      const savedProductIdx = isRealGuid(savedProductId)
        ? it.products.findIndex(p => p.productId === savedProductId)
        : -1;
      it.selProductIdx = savedProductIdx >= 0 ? savedProductIdx : undefined;
      it.savedProductId = savedProductIdx >= 0 ? savedProductId : undefined;
      const endorsed = it.endorsedProductId ? mapped.find(p => p.productId === it.endorsedProductId) : undefined;
      it.endorsedReimbPrice = endorsed?.reimbursement;
      it.endorsedPackSize = endorsed?.packSize;
      return it;
    });
    const enrichedById = new Map(enriched.map(it => [it.id, it]));

    setPrescriptionModal(prev => (isCurrent(prev) ? {
      ...prev,
      items: prev.items.map(p => {
        const e = enrichedById.get(p.id);
        return e ? { ...e, endorsements: p.endorsements, concession: p.concession } : p;
      }),
      isItemInfoLoading: false,
    } : prev));
  }, [activeBranch, loadPrescriptionImage]);

  const openBlockedDetail = async (bc: typeof blockedClaims[0]) => {
    const isSpecial = bc.ruleCategory === "Specials";
    const isConcession = bc.ruleCategory === "Concession" || bc.ruleCategory === "Concessions";
    const required = isSpecial
      ? [{ code: "SP", label: "Special (Specials endorsement)" }]
      : isConcession
        ? [{ code: "NCSO", label: "No Cheaper Stock Obtainable (NCSO)" }]
        : [];
    const existing = bc.endorsed
      ? required.map(r => ({ ...r, appliedDate: bc.blockedDate }))
      : [];
    const selectedItem: PrescriptionItem = {
      id: bc.id, drug: bc.drug, pack: bc.pack, quantity: "1", dosage: bc.ruleName ?? "",
      isSelected: true, ruleCategory: bc.ruleCategory, ruleName: bc.ruleName,
      dtPrice: bc.dtPrice, costPrice: bc.costPrice,
      endorsements: existing, requiredEndorsements: required,
    };
    const canLoad = Boolean(activeBranch && bc.prescriptionIncomingId);
    setPrescriptionModal({
      id: bc.id, patient: bc.patient, rxDate: bc.rxDate,
      prescriber: bc.prescriber, epsBarcode: bc.epsBarcode,
      exemption: "Exempt",
      items: [selectedItem],
      prescriptionImageUrl: undefined,
      nhsNumber: "—",
      dob: "—",
      chargeStatus: "Exempt from Charge",
      // As above: get-blocked-claims carries no form type, dispensed date or
      // nomination flag, so they stay unset and render as "—".
      isImageLoading: canLoad,
    });
    if (canLoad) {
      loadPrescriptionImage(bc.id, activeBranch, bc.prescriptionIncomingId);
    }
  };

  // Persist a set of claims via the save-claims API, then snapshot them into the
  // Claimed tab (so they remain visible after the live list refreshes) and reload.
  const claimRows = async (rows: ClaimRow[]) => {
    const ids = rows.map(r => r.id);
    if (!activeBranch || ids.length === 0 || isClaiming) return false;
    setIsClaiming(true);
    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const result: any = await saveClaims(activeBranch, ids);
    setIsClaiming(false);
    if (!result) {
      warn("Couldn't save the claim", `${ids.length > 1 ? `${ids.length} claims` : "This claim"} couldn't be saved. Please try again.`);
      return false;
    }
    const outcomes = claimOutcomes(result);
    const claimedIds = new Set(outcomes.filter(o => o.isClaimed).map(o => o.prescriptionId).filter(Boolean) as string[]);
    const savedRows = rows.filter(r => claimedIds.has(r.id));
    const failed = rows.length - savedRows.length;
    if (failed > 0) {
      warn(
        "Couldn't save the claim",
        result?.message
          || (failed > 1 ? `${failed} of ${rows.length} claims were not accepted. Please try again.` : "This claim was not accepted. Please try again."),
      );
    }
    if (savedRows.length === 0) return false;
    notify(
      "Successfully claimed",
      savedRows.length === 1
        ? `${savedRows[0].patient} · claimed on ${today}.`
        : `${savedRows.length} items claimed on ${today}.`,
    );
    setClaimedSnapshots(prev => { const next = { ...prev }; savedRows.forEach(r => { next[r.id] = { ...r, _source: "Claim", _claimedAt: today }; }); return next; });
    setClaimedClaimIds(prev => new Set([...prev, ...savedRows.map(r => r.id)]));
    setSelectedIds(new Set());
    invalidateClaimedCycles();
    loadClaims(activeBranch);
    return failed === 0;
  };

  // The modal's prescriptionId comes from get-prescription-by-incoming and can
  // differ from the claims-list id, so resolve the row by either reference.
  const onClaimSaved = async (prescriptionId: string, prescriptionIncomingId?: string) => {
    const row = allRows.find(r => r.id === prescriptionId)
      ?? (prescriptionIncomingId ? allRows.find(r => r.prescriptionIncomingId === prescriptionIncomingId) : undefined);
    if (!row) {
      warn("Couldn't save the claim", "This prescription is no longer in the to-be-claimed list. Please refresh and try again.");
      return false;
    }
    return await claimRows([row]);
  };

  const runBulkRtec = async (rows: ClaimRow[]) => {
    const incomingIds = rows.map(r => r.prescriptionIncomingId).filter(Boolean) as string[];
    if (!activeBranch || incomingIds.length === 0 || rtecBulkChecking) return;
    setRtecBulkChecking(true);
    setRtecBulkMessage(null);
    const result: any = await bulkRtec(activeBranch, incomingIds);
    setRtecBulkChecking(false);
    if (!result) {
      warn("RTEC check didn't complete", "The Real Time Exemption Check couldn't be run against the NHS Spine. Please try again.");
      return;
    }
    setRtecBulkMessage(result?.message || `RTEC check complete for ${result?.count ?? incomingIds.length} prescription(s).`);
    loadClaims(activeBranch);
  };

  const copyExemptions = async (rows: ClaimRow[]) => {
    const prescriptionIds = rows.map(r => r.id).filter(isRealGuid);
    if (!activeBranch || prescriptionIds.length === 0 || copyingExemptions) return;
    setCopyingExemptions(true);
    const result: any = await bulkExemption(activeBranch, { prescriptionIds });
    setCopyingExemptions(false);
    if (!result) {
      warn("Couldn't copy exemptions", "The last recorded exemptions couldn't be copied. Please try again.");
      return;
    }
    if ((result?.success ?? result?.Success ?? false) === false) {
      warn("Couldn't copy exemptions", result?.message || result?.Message || "The last recorded exemptions couldn't be copied. Please try again.");
      return;
    }
    const updatedIds: any = result?.prescriptionIdsUpdated ?? result?.PrescriptionIdsUpdated ?? [];
    const updated = Array.isArray(updatedIds) ? updatedIds.length : 0;
    notify(
      "Exemptions copied",
      updated === 0
        ? `No exemption needed copying across ${prescriptionIds.length} selected prescription${prescriptionIds.length === 1 ? "" : "s"}.`
        : `Last recorded exemption copied onto ${updated} of ${prescriptionIds.length} selected prescription${prescriptionIds.length === 1 ? "" : "s"}.`,
    );
    setSelectedIds(new Set());
    loadClaims(activeBranch);
  };

  const clearClaimRows = async (rows: ClaimRow[]) => {
    const prescriptionIds = rows.map(r => r.id).filter(isRealGuid);
    if (!activeBranch || prescriptionIds.length === 0 || clearingClaims) return false;
    setClearingClaims(true);
    let serverMessage = "";
    const result: any = await bulkClearClaims(activeBranch, prescriptionIds, (message: string) => { serverMessage = message; });
    setClearingClaims(false);
    if (!result) {
      warn(
        prescriptionIds.length === 1 ? "Couldn't clear prescription" : "Couldn't clear prescriptions",
        serverMessage || `The selected prescription${prescriptionIds.length === 1 ? "" : "s"} couldn't be cleared from the claim screen. Please try again.`,
      );
      return false;
    }
    const count = prescriptionIds.length;
    setClearedClaimIds(prev => new Set([...prev, ...prescriptionIds]));
    setSelectedIds(prev => { const next = new Set(prev); prescriptionIds.forEach(id => next.delete(id)); return next; });
    notify(
      count === 1 ? "Prescription cleared" : "Prescriptions cleared",
      `${count} prescription${count === 1 ? "" : "s"} cleared from the claim screen.`,
    );
    return true;
  };

  const applyExemptionToRow = (prescriptionId: string, prescriptionIncomingId: string | undefined, exemptionStatusType: number) => {
    if (!(exemptionStatusType > 0) || exemptionStatusType === 99) return;
    const raw = titanClaims.find(r => r.prescriptionId === prescriptionId)
      ?? (prescriptionIncomingId ? titanClaims.find(r => r.prescriptionIncomingId === prescriptionIncomingId) : undefined);
    if (!raw) return;
    const claimStatus = typeof raw.claimStatus === "number" ? raw.claimStatus : -1;
    setClaimRowPatches(prev => ({
      ...prev,
      [raw.prescriptionId]: {
        ...prev[raw.prescriptionId],
        exemptionId: exemptionStatusType,
        exemptionStatus: undefined,
        claimStatus: CLAIM_STATUS_AFTER_EXEMPTION[claimStatus] ?? claimStatus,
      },
    }));
  };

  const claimBlockedRows = async (bcs: typeof blockedClaims) => {
    const ids = bcs.map(b => b.id);
    const incomingIds = bcs.map(b => b.prescriptionIncomingId).filter(Boolean);
    if (!activeBranch || ids.length === 0 || isClaiming) return;
    setIsClaiming(true);
    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const result: any = await saveClaims(activeBranch, incomingIds.length ? incomingIds : ids);
    setIsClaiming(false);
    if (!result) {
      warn("Couldn't save the claim", `${ids.length > 1 ? `${ids.length} blocked claims` : "This blocked claim"} couldn't be saved. Please try again.`);
      return;
    }
    const outcomes = claimOutcomes(result);
    if (!outcomes.some(o => o.isClaimed)) {
      warn(
        "Couldn't save the claim",
        result?.message
          || `${ids.length > 1 ? "The blocked claims were" : "This blocked claim was"} not accepted. Please try again.`,
      );
      return;
    }
    notify("Successfully claimed", `${ids.length} previously blocked item${ids.length === 1 ? "" : "s"} claimed on ${today}.`);
    setClaimedSnapshots(prev => {
      const next = { ...prev };
      bcs.forEach(bc => { next[bc.id] = { id: bc.id, patient: bc.patient, drug: bc.drug, exemption: "Exempt", lastExemption: "—", rxDate: bc.rxDate, epsBarcode: bc.epsBarcode, treatmentType: "Acute", dtPrice: bc.dtPrice, costPrice: bc.costPrice, endorsed: bc.endorsed, _source: "Recovered Block", _claimedAt: today }; });
      return next;
    });
    setClaimedBlockedIds(prev => new Set([...prev, ...ids]));
    setBlockedSelectedIds(new Set());
    invalidateClaimedCycles();
    loadBlockedClaims(activeBranch);
  };

  const isPreviousClaimedCycle = claimedCycle === "last";

  const claimedRowsAll: ClaimedRow[] = React.useMemo(() => {
    const apiClaimedIds = new Set(apiClaimedRows.map(r => r.id));
    return [
      ...(apiClaimedRows as ClaimedRow[]),
      ...(isPreviousClaimedCycle ? [] : (Object.values(claimedSnapshots) as ClaimedRow[]).filter(r => !apiClaimedIds.has(r.id))),
    ];
  }, [apiClaimedRows, claimedSnapshots, isPreviousClaimedCycle]);

  const claimedRows: ClaimedRow[] = React.useMemo(() => {
    const search = claimedSearch.trim();
    return filterRowsByClaimSearch(claimedRowsAll.filter(r =>
      (claimedCategoryFilter === "All" || rowCategories(r).includes(claimedCategoryFilter)) &&
      (claimedExemptionFilter === "All" || r.exemption === claimedExemptionFilter)
    ), search, r => r._claimedAt);
  }, [claimedRowsAll, claimedCategoryFilter, claimedExemptionFilter, claimedSearch]);

  const claimedTotals = React.useMemo(() => {
    const totalDt = claimedRows.reduce((s, e) => s + (parseFloat(e.dtPrice) || 0), 0);
    const totalCost = claimedRows.reduce((s, e) => s + (parseFloat(e.costPrice) || 0), 0);
    return { totalDt, totalCost, totalMargin: totalDt - totalCost };
  }, [claimedRows]);

  const claimedFacets = React.useMemo(() => {
    const claimedCategoryCounts: Record<string, number> = {};
    const claimedExemptionCounts: Record<string, number> = {};
    claimedRowsAll.forEach(r => {
      rowCategories(r).forEach(c => { claimedCategoryCounts[c] = (claimedCategoryCounts[c] || 0) + 1; });
      claimedExemptionCounts[r.exemption] = (claimedExemptionCounts[r.exemption] || 0) + 1;
    });
    return {
      claimedCategoryCounts,
      claimedCategoryTypes: ["All", ...Object.keys(claimedCategoryCounts)],
      claimedExemptionCounts,
      claimedExemptionTypes: ["All", ...Object.keys(claimedExemptionCounts)],
    };
  }, [claimedRowsAll]);

  const claimedGroups: [string, ClaimedRow[]][] = React.useMemo(() => {
    if (claimedGroupBy === "none") return [];
    const key = claimedGroupBy === "drug" ? "drug" : "patient";
    const groups = new Map<string, ClaimedRow[]>();
    claimedRows.forEach(r => { const g = r[key]; if (!groups.has(g)) groups.set(g, []); groups.get(g)!.push(r); });
    return Array.from(groups.entries()).sort((a, b) =>
      claimedGroupBy === "drug"
        ? a[0].localeCompare(b[0], "en-GB", { sensitivity: "base" })
        : b[1].length - a[1].length,
    );
  }, [claimedRows, claimedGroupBy]);

  const claimedPrescriptions = React.useMemo(() => {
    const map = new Map<string, ClaimedRow[]>();
    claimedRows.forEach(r => {
      const key = r.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      key, patient: items[0].patient, epsBarcode: items[0].epsBarcode, rxDate: items[0].rxDate,
      exemption: items[0].exemption, lastExemption: items[0].lastExemption,
      claimedAt: items[0]._claimedAt, items,
    }));
  }, [claimedRows]);

  const openClaimFromList = React.useCallback(
    (row: ClaimRow, focusDrug?: string) => openClaimDetail(row, false, focusDrug ?? searchedDrugFor(row, patientSearch)),
    [openClaimDetail, patientSearch],
  );
  const openClaimedEditRef = React.useRef<(r: ClaimedRow, focusDrug?: string) => void>(() => {});
  openClaimedEditRef.current = (r: ClaimedRow, focusDrug?: string) => {
    if (r._source === "Recovered Block") {
      const bc = blockedClaims.find(b => b.id === r.id);
      if (bc) openBlockedDetail(bc);
    } else {
      openClaimDetail(r, true, focusDrug);
    }
  };
  const openClaimedEdit = React.useCallback((r: ClaimRow, focusDrug?: string) => openClaimedEditRef.current(r as ClaimedRow, focusDrug), []);
  const toggleClaimedOne = React.useCallback((id: string) => setClaimedSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }), []);

  if (showOverview) {
    const textPrimary = dark ? "text-white" : "text-gray-900";
    const textSecondary = dark ? "text-gray-400" : "text-gray-500";

    // Per-branch claim breakdown from the get-dashboard-stats metrics
    // (Total 81 / Exempt 82 / Not-Exempt 83 / Evidence-Not-Seen 84 / Needs-Attention 85,
    // Missing-Exemptions 98 / Better-Pack 99 / Incomplete-Endorsements 100).
    // Any metric the API doesn't return shows 0.
    const overviewRows = pharmacies.map(b => {
      const total = getMetricValue(b as any, METRIC.TOTAL_CLAIMS, 0);
      const exempt = getMetricValue(b as any, METRIC.EXEMPT_CLAIMS, 0);
      const notExempt = getMetricValue(b as any, METRIC.NOT_EXEMPT_CLAIMS, 0);
      const evidenceNS = getMetricValue(b as any, METRIC.EVIDENCE_NOT_SEEN, 0);
      const missingExemptions = getMetricValue(b as any, METRIC.MISSING_EXEMPTIONS, 0);
      const betterPack = getMetricValue(b as any, METRIC.BETTER_PACK, 0);
      const incompleteEndorsements = getMetricValue(b as any, METRIC.INCOMPLETE_ENDORSEMENTS, 0);
      const attn = getMetricValue(b as any, METRIC.CLAIMS_NEEDING_ATTENTION, 0);
      return { branch: b, total, exempt, notExempt, evidenceNS, missingExemptions, betterPack, incompleteEndorsements, attn };
    });

    const allOverviewSelected = overviewRows.length > 0 && overviewRows.every(r => overviewSelectedIds.has(r.branch.id));
    const someOverviewSelected = overviewRows.some(r => overviewSelectedIds.has(r.branch.id)) && !allOverviewSelected;
    const toggleOverviewAll = () => setOverviewSelectedIds(allOverviewSelected ? new Set() : new Set(overviewRows.map(r => r.branch.id)));
    const toggleOverviewOne = (id: string) => setOverviewSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

    const exportOverviewSummary = () => {
      const selectedRows = overviewRows.filter(r => overviewSelectedIds.has(r.branch.id));
      const header = ["Branch", "Total Claims", "Exempt", "Not Exempt", "Evidence Not Seen", "Missing Exemptions", "Better Pack Available", "Incomplete Endorsements", "Needs Attention"];
      const csvRows = selectedRows.map(r => [r.branch.name, r.total, r.exempt, r.notExempt, r.evidenceNS, r.missingExemptions, r.betterPack, r.incompleteEndorsements, r.attn].join(","));
      const csv = [header.join(","), ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "estate-claims-summary.csv";
      link.click();
      URL.revokeObjectURL(url);
    };

    const totalClaimsAllBranches = overviewRows.reduce((s, r) => s + r.total, 0);
    const totalAttnAllBranches = overviewRows.reduce((s, r) => s + r.attn, 0);

    return (
      <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className={`text-lg font-semibold ${textPrimary}`}>Claims — All Branches</h2>
          <p className={`text-sm mt-0.5 ${textSecondary}`}>{totalClaimsAllBranches} total claims across estate · {totalAttnAllBranches} need attention</p>
        </div>
      </div>
      <div className={`flex-1 rounded-lg border overflow-hidden transition-colors duration-300 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
        <div className="overflow-auto h-full">
          <table className="w-full">
            <thead
              className={`sticky top-0 transition-colors duration-300 ${dark ? "bg-gray-700" : "bg-gray-50"}`}
            >
              <tr>
                <th className="pl-6 pr-2 py-3 w-8">
                  <Checkbox checked={allOverviewSelected} ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someOverviewSelected; }} onCheckedChange={toggleOverviewAll} />
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Branch</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Total Claims</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Exempt</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Not Exempt</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Evidence Not Seen</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Missing Exemptions</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Better Pack Available</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Incomplete Endorsements</th>
                <th className={`px-6 py-3 text-left text-xs font-medium transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"} uppercase tracking-wider`}>Needs Attention</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${dark ? "divide-gray-700" : "divide-gray-200"}`}>
              {overviewRows.map(({ branch: b, total, exempt, notExempt, evidenceNS, missingExemptions, betterPack, incompleteEndorsements, attn }) => (
                  <tr
                    key={b.id}
                    onClick={() => { setActiveBranch(b.id); onBranchChange(b.id); setShowOverview(false); }}
                    className={`border-t cursor-pointer transition-colors ${dark ? "border-gray-700 hover:bg-gray-700/50" : "border-gray-200 hover:bg-gray-50"} ${overviewSelectedIds.has(b.id) ? (dark ? "bg-gray-700/40" : "bg-purple-50/60") : ""}`}
                  >
                    <td className="pl-6 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={overviewSelectedIds.has(b.id)} onCheckedChange={() => toggleOverviewOne(b.id)} />
                    </td>
                    <td className={`px-6 py-3 font-medium ${textPrimary}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />
                        {b.name}
                      </div>
                    </td>
                    <td className={`px-6 py-3 text-sm ${dark ? "text-gray-300" : "text-gray-700"}`}>{total}</td>
                    <td className={`px-6 py-3 text-sm text-emerald-500 font-medium`}>{exempt}</td>
                    <td className={`px-6 py-3 text-sm ${notExempt > 0 ? "text-red-500 font-semibold" : dark ? "text-gray-400" : "text-gray-600"}`}>{notExempt}</td>
                    <td className={`px-6 py-3 text-sm ${evidenceNS > 0 ? "text-amber-500 font-semibold" : dark ? "text-gray-400" : "text-gray-600"}`}>{evidenceNS}</td>
                    <td className={`px-6 py-3 text-sm ${missingExemptions > 0 ? "text-red-500 font-semibold" : dark ? "text-gray-400" : "text-gray-600"}`}>{missingExemptions}</td>
                    <td className={`px-6 py-3 text-sm ${betterPack > 0 ? "text-purple-500 font-semibold" : dark ? "text-gray-400" : "text-gray-600"}`}>{betterPack}</td>
                    <td className={`px-6 py-3 text-sm ${incompleteEndorsements > 0 ? "text-amber-500 font-semibold" : dark ? "text-gray-400" : "text-gray-600"}`}>{incompleteEndorsements}</td>
                    <td className="px-6 py-3">
                      {attn > 0
                        ? <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${dark ? "bg-red-500/20 text-red-400 border-red-800" : "bg-red-100 text-red-600 border-red-200"}`}>{attn} flagged</span>
                        : <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${dark ? "bg-emerald-500/20 text-emerald-400 border-emerald-800" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>Clear</span>
                      }
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {overviewSelectedIds.size > 0 && createPortal(
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-2xl backdrop-blur-md transition-all ${dark ? "bg-gray-900/95 border-gray-700" : "bg-white/95 border-gray-200"}`}>
          <span className={`text-sm font-medium ${dark ? "text-white" : "text-gray-900"}`}>{overviewSelectedIds.size} branch{overviewSelectedIds.size === 1 ? "" : "es"} selected</span>
          <div className={`w-px h-5 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
          <button
            onClick={exportOverviewSummary}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-500 transition-colors cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" /> Export Summary ({overviewSelectedIds.size})
          </button>
          <button
            onClick={() => setOverviewSelectedIds(new Set())}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"}`}
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>,
        document.body
      )}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
    <div>
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <button onClick={() => setShowOverview(true)} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg cursor-pointer transition-colors ${dark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            <ArrowLeft className="w-3.5 h-3.5" /> All Branches
          </button>
        </div>
      </div>

      {/* Claims / Blocked Claims tabs + store selector */}
      <div className={`flex items-center justify-between gap-4 mb-5 border-b ${dark ? "border-gray-700" : "border-gray-200"}`}>
        <div className="flex items-center gap-2 pb-2">
          <button
            onClick={() => setClaimsView("claims")}
            className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${claimsView === "claims" ? dark ? "border-gray-300 text-white" : "border-gray-900 text-gray-900" : `border-transparent ${dark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}`}
          >
            <FileText className="w-3.5 h-3.5" />
            To be claimed
          </button>
          <button
            onClick={() => setClaimsView("blocked")}
            className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${claimsView === "blocked" ? dark ? "border-gray-300 text-white" : "border-gray-900 text-gray-900" : `border-transparent ${dark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}`}
          >
            <Lock className="w-3.5 h-3.5" />
            Blocked Claims
            {pendingBlockedCount > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${dark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-600"}`}>{pendingBlockedCount}</span>
            )}
          </button>
          <button
            onClick={() => { setClaimsView("claimed"); setClaimedCycle("current"); setClaimedSelectedIds(new Set()); }}
            className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${claimsView === "claimed" && claimedCycle === "current" ? dark ? "border-gray-300 text-white" : "border-gray-900 text-gray-900" : `border-transparent ${dark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}`}
          >
            <Check className="w-3.5 h-3.5" />
            Claimed ({CURRENT_CYCLE_LABEL})
            {(claimedClaimIds.size + claimedBlockedIds.size) > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${dark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>{claimedClaimIds.size + claimedBlockedIds.size}</span>
            )}
          </button>
          <button
            onClick={() => { setClaimsView("claimed"); setClaimedCycle("last"); setClaimedSelectedIds(new Set()); }}
            className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${claimsView === "claimed" && claimedCycle === "last" ? dark ? "border-gray-300 text-white" : "border-gray-900 text-gray-900" : `border-transparent ${dark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}`}
          >
            <Check className="w-3.5 h-3.5" />
            Claimed ({PREVIOUS_CYCLE_LABEL})
          </button>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <div className={`w-px h-5 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
          <Building2 className={`w-3.5 h-3.5 ${dark ? "text-gray-500" : "text-gray-400"}`} />
          <select
            value={activeBranch}
            onChange={e => { setActiveBranch(e.target.value); onBranchChange(e.target.value); }}
            className={`text-xs font-medium rounded-lg border px-2.5 py-1 cursor-pointer outline-none transition-colors ${
              dark
                ? "bg-gray-800 border-gray-700 text-gray-200 hover:border-gray-600"
                : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
            }`}
          >
            {pharmacies.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      </div>

      {(visitedViews.has("blocked") || claimsView === "blocked") && (
        <div hidden={claimsView !== "blocked"}>
        {/* Blocked Claims Headline Stats */}
        {(() => {
          const totalBlocked = blockedClaims.length;
          const pendingCount = blockedClaims.filter(c => c.status === "pending").length;
          const approvedCount = blockedClaims.filter(c => c.status === "approved").length;
          const rejectedCount = blockedClaims.filter(c => c.status === "rejected").length;
          const endorsedCount = blockedClaims.filter(c => c.endorsed).length;
          const notEndorsedCount = totalBlocked - endorsedCount;
          const totalDtVal = blockedClaims.reduce((sum, c) => sum + parseFloat(c.dtPrice.replace("£", "")), 0);
          const totalCostVal = blockedClaims.reduce((sum, c) => sum + parseFloat(c.costPrice.replace("£", "")), 0);
          const concessionVal = blockedClaims.filter(c => c.concessionPrice).reduce((sum, c) => sum + parseFloat(c.concessionPrice!.replace("£", "")), 0);
          return (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
              <div className={`rounded-xl p-4 border ${dark ? "bg-amber-500/10 border-amber-500/20" : "bg-amber-50 border-amber-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className={`w-4 h-4 ${dark ? "text-amber-400" : "text-amber-600"}`} />
                  <span className={`text-xs font-medium ${dark ? "text-amber-400" : "text-amber-600"}`}>Pending</span>
                </div>
                <p className={`text-2xl font-bold ${dark ? "text-amber-300" : "text-amber-700"}`}>{pendingCount}</p>
                <p className={`text-xs mt-0.5 ${dark ? "text-gray-500" : "text-gray-400"}`}>Awaiting review</p>
              </div>
              <div className={`rounded-xl p-4 border ${dark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-emerald-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Check className={`w-4 h-4 ${dark ? "text-emerald-400" : "text-emerald-600"}`} />
                  <span className={`text-xs font-medium ${dark ? "text-emerald-400" : "text-emerald-600"}`}>Approved</span>
                </div>
                <p className={`text-2xl font-bold ${dark ? "text-emerald-300" : "text-emerald-700"}`}>{approvedCount}</p>
              </div>
              <div className={`rounded-xl p-4 border ${dark ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className={`w-4 h-4 ${dark ? "text-red-400" : "text-red-600"}`} />
                  <span className={`text-xs font-medium ${dark ? "text-red-400" : "text-red-600"}`}>Rejected</span>
                </div>
                <p className={`text-2xl font-bold ${dark ? "text-red-300" : "text-red-700"}`}>{rejectedCount}</p>
              </div>
              <div className={`rounded-xl p-4 border ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className={`w-4 h-4 ${dark ? "text-blue-400" : "text-blue-600"}`} />
                  <span className={`text-xs font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Endorsement</span>
                </div>
                <p className={`text-2xl font-bold ${dark ? "text-white" : "text-gray-900"}`}>{endorsedCount}/{totalBlocked}</p>
                <p className={`text-xs mt-0.5 ${dark ? "text-gray-500" : "text-gray-400"}`}>{notEndorsedCount} not endorsed</p>
              </div>
              <div className={`rounded-xl p-4 border ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className={`w-4 h-4 ${dark ? "text-blue-400" : "text-blue-600"}`} />
                  <span className={`text-xs font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Total DT Value</span>
                </div>
                <p className={`text-2xl font-bold ${dark ? "text-white" : "text-gray-900"}`}>£{totalDtVal.toFixed(2)}</p>
                <p className={`text-xs mt-0.5 ${dark ? "text-gray-500" : "text-gray-400"}`}>Cost: £{totalCostVal.toFixed(2)}</p>
              </div>
              <div className={`rounded-xl p-4 border ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <PoundSterling className={`w-4 h-4 ${dark ? "text-violet-400" : "text-violet-600"}`} />
                  <span className={`text-xs font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Concession Value</span>
                </div>
                <p className={`text-2xl font-bold ${dark ? "text-violet-300" : "text-violet-700"}`}>£{concessionVal.toFixed(2)}</p>
                <p className={`text-xs mt-0.5 ${dark ? "text-gray-500" : "text-gray-400"}`}>{blockedClaims.filter(c => c.concessionPrice).length} on concession</p>
              </div>
            </div>
          );
        })()}

        <div className="mb-3">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${dark ? "text-gray-500" : "text-gray-400"}`} />
              <Input
                placeholder="Search blocked claims by patient name..."
                value={blockedSearch}
                onChange={e => { setBlockedSearch(e.target.value); setBlockedSelectedIds(new Set()); }}
                className={`pl-10 transition-colors duration-300 ${
                  dark
                    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    : "bg-white border-gray-300"
                }`}
              />
              {blockedSearch && (
                <button
                  onClick={() => setBlockedSearch("")}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer ${dark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <Popover
              open={blockedFilterOpen}
              onOpenChange={(open) => {
                setBlockedFilterOpen(open);
                if (open) {
                  setPendingBlockedFilter(blockedFilter);
                  setPendingEndorsedFilter(endorsedFilter);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`relative ${dark ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700" : "bg-white border-gray-300 hover:bg-gray-50"}`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {blockedActiveFilterCount > 0 && (
                    <Badge className="ml-2 px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center" variant="default">
                      {blockedActiveFilterCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" collisionPadding={16} className={`w-[560px] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 flex items-center justify-between">
                    <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Filter Blocked Claims</h3>
                    {blockedActiveFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setPendingBlockedFilter("All"); setPendingEndorsedFilter("all"); }}
                        className={`h-7 text-xs ${dark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Reset
                      </Button>
                    )}
                  </div>

                  <Separator className={`col-span-2 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />

                  <div className="space-y-2">
                    <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Action Type</label>
                    <Select value={pendingBlockedFilter} onValueChange={setPendingBlockedFilter}>
                      <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                        <SelectValue placeholder="Action type" />
                      </SelectTrigger>
                      <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                        {blockedCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Endorsed</label>
                    <Select value={pendingEndorsedFilter} onValueChange={(v: "all" | "endorsed" | "not-endorsed") => setPendingEndorsedFilter(v)}>
                      <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                        <SelectValue placeholder="Endorsed" />
                      </SelectTrigger>
                      <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="endorsed">Endorsed</SelectItem>
                        <SelectItem value="not-endorsed">Not Endorsed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2 flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBlockedFilterOpen(false)}
                      className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setBlockedFilter(pendingBlockedFilter);
                        setEndorsedFilter(pendingEndorsedFilter);
                        setBlockedSelectedIds(new Set());
                        setBlockedFilterOpen(false);
                      }}
                      className="flex-1 bg-black hover:bg-gray-900 text-white"
                    >
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className={`rounded-lg border overflow-x-auto ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
          <table className="w-full text-sm">
            <thead className={`sticky top-0 transition-colors duration-300 ${dark ? "bg-gray-700" : "bg-gray-50"}`}>
              <tr>
                <th className="pl-3 pr-1 py-3 w-10">
                  <Checkbox
                    checked={allBlockedSelected}
                    ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someBlockedSelected; }}
                    onCheckedChange={toggleAllBlocked}
                  />
                </th>
                {["Drug", "Patients", "Pack", "Conc. Status", "DT Price", "Concession", "Cost Price", "Margin", "Rule", "Endorsed"].map(h => (
                  <th key={h} className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${dark ? "text-gray-300" : "text-gray-500"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drugGroups.slice(0, blockedVisibleCount).map(([drug, claims], gi) => {
                const isExpanded = expandedDrugs.has(drug);
                const groupIds = claims.map(c => c.id);
                const allGroupSelected = groupIds.every(id => blockedSelectedIds.has(id));
                const someGroupSelected = groupIds.some(id => blockedSelectedIds.has(id)) && !allGroupSelected;
                const pendingInGroup = claims.filter(c => c.status === "pending").length;
                const firstClaim = claims[0];
                const uniqueStatuses = Array.from(new Set(claims.map(c => c.status)));
                return (
                  <React.Fragment key={drug}>
                    {/* Group header row */}
                    <tr
                      className={`border-t cursor-pointer ${dark ? "border-gray-800 hover:bg-gray-800/40" : "border-gray-100 hover:bg-gray-50"} ${gi % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"} ${allGroupSelected ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : ""}`}
                      onClick={() => toggleDrugExpand(drug)}
                    >
                      <td className="px-4 py-3 cursor-pointer" onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) toggleBlockedGroup(groupIds); }}>
                        <Checkbox
                          checked={allGroupSelected}
                          ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someGroupSelected; }}
                          onCheckedChange={() => toggleBlockedGroup(groupIds)}
                        />
                      </td>
                      <td className={`px-4 py-3 font-medium ${dark ? "text-white" : "text-gray-900"}`}>
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""} ${dark ? "text-gray-500" : "text-gray-400"}`} />
                          <Pill className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`} />
                          {drug}
                        </div>
                      </td>
                      <td className={`px-4 py-3 ${dark ? "text-gray-400" : "text-gray-500"}`}>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                          {claims.length} patient{claims.length !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className={`px-4 py-3 ${dark ? "text-gray-400" : "text-gray-500"}`}>{firstClaim.pack}</td>
                      <td className="px-4 py-3">
                        {(() => {
                          if (!firstClaim.concessionPrice) return <span className={`text-xs ${dark ? "text-gray-600" : "text-gray-400"}`}>—</span>;
                          if (!firstClaim.concessionActive) return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>
                              <CircleAlert className="w-3 h-3" /> Not on Concession
                            </span>
                          );
                          const start = firstClaim.concessionStartDate ? new Date(firstClaim.concessionStartDate) : null;
                          const now = new Date();
                          const monthsOn = start ? Math.round((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)) : 0;
                          const isStale = monthsOn > 2;
                          return isStale ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                              <Clock className="w-3 h-3" /> Going Stale
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                              <CircleCheck className="w-3 h-3" /> On Concession
                            </span>
                          );
                        })()}
                      </td>
                      <td className={`px-4 py-3 font-mono text-xs ${dark ? "text-gray-300" : "text-gray-700"}`}>{firstClaim.dtPrice}</td>
                      <td className={`px-4 py-3 font-mono text-xs ${dark ? "text-gray-300" : "text-gray-700"}`}>
                        {firstClaim.concessionPrice || <span className={`${dark ? "text-gray-600" : "text-gray-400"}`}>—</span>}
                      </td>
                      <td className={`px-4 py-3 font-mono text-xs ${dark ? "text-gray-300" : "text-gray-700"}`}>{firstClaim.costPrice}</td>
                      {(() => {
                        const dt = parseFloat(firstClaim.dtPrice.replace("£", ""));
                        const cost = parseFloat(firstClaim.costPrice.replace("£", ""));
                        const margin = dt - cost;
                        const pct = dt > 0 ? ((margin / dt) * 100).toFixed(1) : "0.0";
                        const isNeg = margin < 0;
                        return (
                          <td className={`px-4 py-3 font-mono text-xs ${isNeg ? dark ? "text-red-400" : "text-red-600" : dark ? "text-emerald-400" : "text-emerald-700"}`}>
                            {isNeg ? "" : "+"}£{margin.toFixed(2)} ({pct}%)
                          </td>
                        );
                      })()}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                          <Lock className="w-3 h-3" />
                          {firstClaim.ruleCategory}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const allEndorsed = claims.every(c => c.endorsed);
                          const noneEndorsed = claims.every(c => !c.endorsed);
                          const endorsedCount = claims.filter(c => c.endorsed).length;
                          return allEndorsed ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                              <ShieldCheck className="w-3 h-3" /> All endorsed
                            </span>
                          ) : noneEndorsed ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>
                              <CircleAlert className="w-3 h-3" /> Not endorsed
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                              <CircleAlert className="w-3 h-3" /> {endorsedCount}/{claims.length}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                    {/* Expanded individual patient rows */}
                    {isExpanded && claims.map((bc, ci) => {
                      const isChecked = blockedSelectedIds.has(bc.id);
                      return (
                        <tr
                          key={bc.id}
                          onClick={() => openBlockedDetail(bc)}
                          className={`border-t cursor-pointer ${dark ? "border-gray-800/60 hover:bg-gray-800/30" : "border-gray-100/60 hover:bg-gray-50"} ${isChecked ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : dark ? "bg-gray-900/50" : "bg-gray-50/50"}`}
                        >
                          <td className="px-4 py-2.5 pl-6 cursor-pointer" onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) toggleBlockedOne(bc.id); }}>
                            <Checkbox checked={isChecked} onCheckedChange={() => toggleBlockedOne(bc.id)} />
                          </td>
                          <td className={`px-4 py-2.5 pl-14 ${dark ? "text-gray-300" : "text-gray-700"}`}>
                            <span className={`text-xs font-medium ${dark ? "text-gray-200" : "text-gray-800"}`}>{bc.patient}</span>
                            <span className={`ml-2 text-[10px] ${dark ? "text-gray-500" : "text-gray-400"}`}>{bc.prescriber} · {bc.rxDate}</span>
                          </td>
                          <td className={`px-4 py-2.5 font-mono text-[10px] ${dark ? "text-gray-500" : "text-gray-400"}`}>{bc.epsBarcode}</td>
                          <td className={`px-4 py-2.5 ${dark ? "text-gray-400" : "text-gray-500"}`}>{bc.pack}</td>
                          <td className="px-4 py-2.5">
                            {(() => {
                              if (!bc.concessionPrice) return <span className={`text-xs ${dark ? "text-gray-600" : "text-gray-400"}`}>—</span>;
                              if (!bc.concessionActive) return (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>
                                  <CircleAlert className="w-3 h-3" /> Not on Concession
                                </span>
                              );
                              const start = bc.concessionStartDate ? new Date(bc.concessionStartDate) : null;
                              const now = new Date();
                              const monthsOn = start ? Math.round((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)) : 0;
                              const isStale = monthsOn > 2;
                              return isStale ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                                  <Clock className="w-3 h-3" /> Going Stale
                                </span>
                              ) : (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                                  <CircleCheck className="w-3 h-3" /> On Concession
                                </span>
                              );
                            })()}
                          </td>
                          <td className={`px-4 py-2.5 font-mono text-xs ${dark ? "text-gray-300" : "text-gray-700"}`}>{bc.dtPrice}</td>
                          <td className={`px-4 py-2.5 font-mono text-xs ${dark ? "text-gray-300" : "text-gray-700"}`}>
                            {bc.concessionPrice || <span className={`${dark ? "text-gray-600" : "text-gray-400"}`}>—</span>}
                          </td>
                          <td className={`px-4 py-2.5 font-mono text-xs ${dark ? "text-gray-300" : "text-gray-700"}`}>{bc.costPrice}</td>
                          {(() => {
                            const dt = parseFloat(bc.dtPrice.replace("£", ""));
                            const cost = parseFloat(bc.costPrice.replace("£", ""));
                            const margin = dt - cost;
                            const pct = dt > 0 ? ((margin / dt) * 100).toFixed(1) : "0.0";
                            const isNeg = margin < 0;
                            return (
                              <td className={`px-4 py-2.5 font-mono text-xs ${isNeg ? dark ? "text-red-400" : "text-red-600" : dark ? "text-emerald-400" : "text-emerald-700"}`}>
                                {isNeg ? "" : "+"}£{margin.toFixed(2)} ({pct}%)
                              </td>
                            );
                          })()}
                          <td className={`px-4 py-2.5 text-[10px] ${dark ? "text-gray-500" : "text-gray-400"}`}>{bc.ruleName.replace("Block Claiming — ", "")}</td>
                          <td className="px-4 py-2.5">
                            {bc.endorsed ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                                <ShieldCheck className="w-3 h-3" /> Endorsed
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>
                                <CircleAlert className="w-3 h-3" /> Not endorsed
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {drugGroups.length > blockedVisibleCount && (
                <ShowMoreRow
                  shown={blockedVisibleCount}
                  total={drugGroups.length}
                  columns={11}
                  dark={dark}
                  onShowMore={() => setBlockedVisibleCount(count => count + ROWS_PAGE_SIZE)}
                />
              )}
            </tbody>
          </table>
        </div>

        {/* Blocked claims selection action bar */}
        {claimsView === "blocked" && blockedSelectedIds.size > 0 && createPortal(
          <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-2xl backdrop-blur-md transition-all ${dark ? "bg-gray-900/95 border-gray-700" : "bg-white/95 border-gray-200"}`}>
            <span className={`text-sm font-medium ${dark ? "text-white" : "text-gray-900"}`}>{blockedSelectedIds.size} selected</span>
            <div className={`w-px h-5 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
            <button
              onClick={() => claimBlockedRows(filteredBlockedClaims.filter(c => blockedSelectedIds.has(c.id)))}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-colors cursor-pointer"
            >
              <Stamp className="w-3.5 h-3.5" /> Claim Selected
            </button>
            <button
              onClick={() => setBlockedSelectedIds(new Set())}
              className={`p-2 rounded-lg transition-colors cursor-pointer ${dark ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>,
          document.body
        )}
        </div>
      )}

      {(visitedViews.has("claims") || claimsView === "claims") && (
      <div hidden={claimsView !== "claims"}>
      {/* Claims Headline Stats */}
      {isTitanClaimsLoading ? (
        <div className="flex flex-wrap gap-3 mb-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={`flex items-start gap-3 min-w-[150px] rounded-xl px-4 py-3 border ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
              <div className={`w-10 h-10 rounded-lg animate-pulse ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
              <div>
                <div className={`h-3 w-20 rounded animate-pulse ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
                <div className={`h-6 w-12 rounded mt-2 animate-pulse ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
              </div>
            </div>
          ))}
        </div>
      ) : (() => {
        const formsOf = (list: ClaimRow[]) => list.length;
        const itemsOf = (list: ClaimRow[]) => list.reduce((sum, r) => sum + (r.itemCount ?? 1), 0);
        const failedRows = preTileRows.filter(rowHasFailedSubmission);
        const todoRows = preTileRows.filter(r => !rowHasFailedSubmission(r) && getAttentionReasons(r).length > 0);
        const readyRows = preTileRows.filter(r => getAttentionReasons(r).length === 0);
        const epsCount = preTileRows.filter(r => rowPrescriptionType(r) === "EPS").length;
        const manualCount = preTileRows.length - epsCount;
        const epsPct = preTileRows.length > 0 ? (epsCount / preTileRows.length) * 100 : 0;
        // NHS claims for a dispensing month must reach the NHSBSA by the 5th of
        // the following month.
        const now = new Date();
        const deadline = new Date(now.getFullYear(), now.getMonth() + 1, 5);
        const daysLeft = Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / 86400000));

        const toggleTile = (key: string) => {
          setClaimsExcludedTiles(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
          });
          setExpandedClaimGroups(new Set());
          setSelectedIds(new Set());
        };
        const cardCls = (included: boolean) => included
          ? (dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200")
          : (dark ? "bg-transparent border-gray-700 border-dashed opacity-60" : "bg-gray-50 border-gray-300 border-dashed opacity-60");
        const labelCls = `text-[11px] font-medium ${dark ? "text-gray-400" : "text-gray-500"}`;
        const unitCls = `text-xs font-normal ml-1 ${dark ? "text-gray-500" : "text-gray-400"}`;
        const tickTitle = (included: boolean, label: string) => included ? `Untick to hide ${label} claims` : `Tick to show ${label} claims`;
        const formTiles = [
          { key: "failed", label: "Failed", list: failedRows, showItems: false, Icon: X, iconBg: dark ? "bg-red-500/10" : "bg-red-50", iconClass: dark ? "text-red-400" : "text-red-600", numClass: dark ? "text-red-300" : "text-red-700" },
          { key: "todo", label: "To do", list: todoRows, showItems: true, Icon: FileText, iconBg: dark ? "bg-amber-500/10" : "bg-amber-50", iconClass: dark ? "text-amber-400" : "text-amber-600", numClass: dark ? "text-amber-300" : "text-amber-700" },
          { key: "ready", label: "Ready to claim", list: readyRows, showItems: true, Icon: CircleCheck, iconBg: dark ? "bg-emerald-500/10" : "bg-emerald-50", iconClass: dark ? "text-emerald-400" : "text-emerald-600", numClass: dark ? "text-emerald-300" : "text-emerald-700" },
        ];
        const typeTicks = [
          { key: "eps", label: "EPS", count: epsCount, dot: "bg-blue-600" },
          { key: "manual", label: "Manual", count: manualCount, dot: dark ? "bg-blue-300" : "bg-blue-200" },
        ];
        return (
          <div className="flex flex-wrap gap-3 mb-5">
            {formTiles.map(tile => {
              const included = !claimsExcludedTiles.has(tile.key);
              return (
                <label key={tile.key} title={tickTitle(included, tile.label)} className={`flex items-start gap-3 min-w-[150px] rounded-xl px-4 py-3 border cursor-pointer transition-all ${cardCls(included)}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tile.iconBg}`}>
                    <tile.Icon className={`w-5 h-5 ${tile.iconClass}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={labelCls}>{tile.label}</p>
                    <div className="flex items-baseline gap-4">
                      <p className={`text-xl font-bold ${tile.numClass}`}>{formsOf(tile.list)}{tile.showItems && <span className={unitCls}>Forms</span>}</p>
                      {tile.showItems && (
                        <p className={`text-xl font-bold ${tile.numClass}`}>{itemsOf(tile.list)}<span className={unitCls}>Items</span></p>
                      )}
                    </div>
                  </div>
                  <Checkbox checked={included} onCheckedChange={() => toggleTile(tile.key)} className="ml-2" />
                </label>
              );
            })}

            <div className={`flex items-center gap-4 rounded-xl px-4 py-3 border ${cardCls(true)}`}>
              <div
                className="w-10 h-10 rounded-full shrink-0"
                style={{ background: `conic-gradient(#2563eb 0 ${epsPct}%, ${dark ? "#93c5fd" : "#bfdbfe"} ${epsPct}% 100%)`, WebkitMask: "radial-gradient(circle, transparent 55%, #000 56%)", mask: "radial-gradient(circle, transparent 55%, #000 56%)" }}
              />
              <div className="flex flex-col gap-1.5">
                {typeTicks.map(t => {
                  const included = !claimsExcludedTiles.has(t.key);
                  return (
                    <label key={t.key} title={tickTitle(included, t.label)} className={`flex items-center gap-2 cursor-pointer text-[11px] ${included ? (dark ? "text-gray-300" : "text-gray-700") : (dark ? "text-gray-500" : "text-gray-400")}`}>
                      <span className={`w-2 h-2 rounded-full ${t.dot}`} />
                      <span className="flex-1">{t.label} ({t.count})</span>
                      <Checkbox checked={included} onCheckedChange={() => toggleTile(t.key)} className="ml-4" />
                    </label>
                  );
                })}
              </div>
            </div>

            <div className={`flex items-center gap-3 min-w-[130px] rounded-xl px-4 py-3 border ${cardCls(true)}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${dark ? "bg-blue-500/10" : "bg-blue-50"}`}>
                <CalendarClock className={`w-5 h-5 ${dark ? "text-blue-400" : "text-blue-600"}`} />
              </div>
              <div title={`Claims must be submitted by ${deadline.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}>
                <p className={labelCls}>Days left</p>
                <p className={`text-xl font-bold ${dark ? "text-blue-300" : "text-blue-700"}`}>{daysLeft}</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Patient search + Filters */}
      <div className="mb-3">
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${dark ? "text-gray-500" : "text-gray-400"}`} />
          <Input
            placeholder="Search claims by patient, drug, barcode, category…"
            value={patientSearch}
            onChange={e => { setPatientSearch(e.target.value); setSelectedIds(new Set()); }}
            className={`pl-10 transition-colors duration-300 ${
              dark
                ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                : "bg-white border-gray-300"
            }`}
          />
          {patientSearch && (
            <button
              onClick={() => setPatientSearch("")}
              className={`absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer ${dark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <Select
          value={claimsGroupBy}
          onValueChange={(v: "none" | "drug" | "patient" | "category") => {
            setClaimsGroupBy(v);
            setExpandedClaimGroups(new Set());
            setSelectedIds(new Set());
          }}
        >
          <SelectTrigger className={`w-[180px] shrink-0 ${dark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
            <SelectValue placeholder="Group by" />
          </SelectTrigger>
          <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
            <SelectItem value="none">No grouping</SelectItem>
            <SelectItem value="drug">Group by Drug</SelectItem>
            <SelectItem value="patient">Group by Patient</SelectItem>
            <SelectItem value="category">Group by Category</SelectItem>
          </SelectContent>
        </Select>

        <Popover
          open={claimsFilterOpen}
          onOpenChange={(open) => {
            setClaimsFilterOpen(open);
            if (open) {
              setPendingExemptionFilter(exemptionFilter);
              setPendingCategoryFilter(categoryFilter);
              setPendingPrescriptionType(claimsPrescriptionType === "" ? "All" : claimsPrescriptionType);
              setPendingAttentionFilter(claimsAttentionFilter);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`relative ${dark ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700" : "bg-white border-gray-300 hover:bg-gray-50"}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {claimsActiveFilterCount > 0 && (
                <Badge className="ml-2 px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center" variant="default">
                  {claimsActiveFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" collisionPadding={16} className={`w-[560px] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex items-center justify-between">
                <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Filter Claims</h3>
                {claimsActiveFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setPendingExemptionFilter("All"); setPendingCategoryFilter("All"); setPendingAttentionFilter("all"); setPendingPrescriptionType("All"); }}
                    className={`h-7 text-xs ${dark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Reset
                  </Button>
                )}
              </div>

              <Separator className={`col-span-2 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />

              <div className="space-y-2">
                <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Exemption Status</label>
                <Select value={pendingExemptionFilter} onValueChange={setPendingExemptionFilter}>
                  <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                    <SelectValue placeholder="Exemption status" />
                  </SelectTrigger>
                  <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                    {exemptionTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type} ({type === "All" ? allRows.length : (exemptionCounts[type] ?? 0)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Prescription Type</label>
                <Select value={pendingPrescriptionType} onValueChange={(v: "All" | "EPS" | "Manual") => setPendingPrescriptionType(v)}>
                  <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                    <SelectValue placeholder="Prescription type" />
                  </SelectTrigger>
                  <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                    <SelectItem value="All">All</SelectItem>
                    <SelectItem value="EPS">EPS</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Category</label>
                <Select value={pendingCategoryFilter} onValueChange={setPendingCategoryFilter}>
                  <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                    {categoryTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type} ({type === "All" ? allRows.length : (categoryCounts[type] ?? 0)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Warnings</label>
                <Select value={pendingAttentionFilter} onValueChange={(v: ClaimStatusFilter) => setPendingAttentionFilter(v)}>
                  <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                    <SelectValue placeholder="Warnings" />
                  </SelectTrigger>
                  <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                    <SelectItem value="all">All Claims</SelectItem>
                    <SelectItem value="needs-attention">Any Warning</SelectItem>
                    <SelectItem value="Failed">Failed</SelectItem>
                    <SelectItem value="Incomplete Endorsement">Incomplete Endorsement</SelectItem>
                    <SelectItem value="Missing Exemption">Missing Exemption</SelectItem>
                    <SelectItem value="RTEC Not Done">RTEC Not Done</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Expiring">Expiring</SelectItem>
                    <SelectItem value="clear">Clear</SelectItem>
                  </SelectContent>
                </Select>
              </div>


              <div className="col-span-2 flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClaimsFilterOpen(false)}
                  className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setExemptionFilter(pendingExemptionFilter);
                    setCategoryFilter(pendingCategoryFilter);
                    applyPrescriptionType(pendingPrescriptionType);
                    setClaimsAttentionFilter(pendingAttentionFilter);
                    setExpandedClaimGroups(new Set());
                    setSelectedIds(new Set());
                    setClaimsFilterOpen(false);
                  }}
                  className="flex-1 bg-black hover:bg-gray-900 text-white"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      </div>

      <div className={`rounded-lg border overflow-x-auto ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
        <table className="w-full text-sm">
          <thead className={`sticky top-0 transition-colors duration-300 ${dark ? "bg-gray-700" : "bg-gray-50"}`}>
            <tr>
              <th className="pl-3 pr-1 py-3 w-10">
                <Checkbox
                  checked={allSelected}
                  ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someSelected; }}
                  onCheckedChange={toggleAll}
                />
              </th>
              {[
                { label: claimsGroupBy === "drug" ? "Drug / Patient Name" : "Patient Name", key: "patient", align: "left", sortable: true },
                { label: "Age", key: "age", align: "left", sortable: true },
                { label: "Items", key: "items", align: "left", sortable: false },
                { label: "Prescription Type", key: "prescriptionType", align: "left", sortable: false },
                { label: "Category", key: "category", align: "left", sortable: false },
                { label: "Exemption Status", key: "exemption", align: "left", sortable: true },
                { label: "Rx Date", key: "rxDate", align: "left", sortable: true },
                { label: "DT / Net Reimb", key: "netReimb", align: "right", sortable: true },
                { label: "Warnings", key: "attention", align: "left", sortable: true },
              ].map(h => (
                <th
                  key={h.key}
                  onClick={h.sortable ? () => {
                    if (sortColumn === h.key) {
                      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                    } else {
                      setSortColumn(h.key as any);
                      setSortDirection("asc");
                    }
                  } : undefined}
                  className={`px-3 py-3 text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${h.sortable ? "cursor-pointer hover:opacity-80" : ""} ${h.align === "right" ? "text-right" : "text-left"} ${dark ? "text-gray-300" : "text-gray-500"}`}
                >
                  <div className={`flex items-center gap-2 ${h.align === "right" ? "justify-end" : ""}`}>
                    <span>{h.label}</span>
                    {h.sortable && sortColumn === h.key && (
                      <span className="text-xs ml-1">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isTitanClaimsLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className={`border-t ${dark ? "border-gray-800" : "border-gray-100"}`}>
                  {Array.from({ length: 10 }).map((__, c) => (
                    <td key={c} className="px-3 py-3.5">
                      <div className={`h-3.5 rounded animate-pulse ${c === 0 ? "w-4" : "w-full max-w-[120px]"} ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center py-12">
                  <FileText className={`w-6 h-6 mx-auto mb-2 ${dark ? "text-gray-600" : "text-gray-300"}`} />
                  <p className={`text-sm ${dark ? "text-gray-500" : "text-gray-400"}`}>
                    {claimsLoadError?.branch === activeBranch && /not linked/i.test(claimsLoadError.message)
                      ? claimsLoadError.message
                      : "No claims for this store"}
                  </p>
                </td>
              </tr>
            ) : claimsGroupBy === "none" ? prescriptions.slice(0, claimsVisibleCount).map((script, gi) => {
              // Single-item prescription: render as a normal clickable row.
              if (script.items.length === 1) {
                const row = script.items[0];
                return <ClaimRowLine key={row.id} row={row} i={gi} dark={dark} isChecked={selectedIds.has(row.id)} onToggle={toggleOne} onOpen={openClaimFromList} exemptionStyle={exemptionStyle} itemCount={row.itemCount} resolveRtec={resolveRtec} />;
              }
              const groupIds = script.items.map(c => c.id);
              const allGroupSelected = groupIds.every(id => selectedIds.has(id));
              const someGroupSelected = groupIds.some(id => selectedIds.has(id)) && !allGroupSelected;
              const totalDt = script.items.reduce((s, c) => s + parseFloat(c.dtPrice), 0);
              const totalNetReimb = script.items.reduce((s, c) => s + calcNetReimb(c), 0);
              return (
                <React.Fragment key={script.key}>
                  <tr
                    className={`border-t cursor-pointer ${dark ? "border-gray-800 hover:bg-gray-800/40" : "border-gray-100 hover:bg-gray-50"} ${gi % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"} ${allGroupSelected ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : ""}`}
                    onClick={() => openClaimDetail(script.items[0])}
                  >
                    <td className="pl-3 pr-3 py-3 cursor-pointer" onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) toggleClaimGroupSelection(groupIds); }}>
                      <Checkbox
                        checked={allGroupSelected}
                        ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someGroupSelected; }}
                        onCheckedChange={() => toggleClaimGroupSelection(groupIds)}
                      />
                    </td>
                    <td className={`pl-1 pr-6 py-3 font-medium ${dark ? "text-white" : "text-gray-900"}`}>{script.patient}</td>
                    <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>{ageDisplay(script.items[0])}</td>
                    <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>
                      {(() => {
                        const chargeable = isMissingExemption(script.exemption);
                        const chargeCount = chargeable ? script.items.length : 0;
                        return (
                          <div className="leading-tight">
                            <span>{script.items.length}</span>
                            <span className={`block text-[10px] font-normal ${dark ? "text-gray-500" : "text-gray-400"}`}>{chargeCount} NHS charge{chargeCount !== 1 ? "s" : ""}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className={`px-3 py-3 text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>{rowPrescriptionType(script.items[0])}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {Array.from(new Set(script.items.flatMap(c => rowCategories(c)))).map(cat => (
                          <CategoryTag key={cat} category={cat} dark={dark} />
                        ))}
                      </div>
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className={`text-sm ${dark ? "text-gray-300" : "text-gray-700"}`}>{script.exemption}</span>
                        <RtecChip status={script.items.every(it => resolveRtec(it) !== "RTEC Not Done") ? "RTEC Exempt" : "RTEC Not Done"} dark={dark} />
                      </div>
                    </td>
                    <td className={`px-3 py-3 ${dark ? "text-gray-300" : "text-gray-700"}`}>{script.rxDate}</td>
                    <td className="px-3 py-3 text-right"><DtNetReimbCell dt={totalDt} netReimb={totalNetReimb} dark={dark} /></td>
                    <StatusChips items={script.items} dark={dark} resolveRtec={resolveRtec} />
                  </tr>
                </React.Fragment>
              );
            }) : claimGroups.slice(0, claimsVisibleCount).map(([groupName, claims], gi) => {
              const isExpanded = expandedClaimGroups.has(groupName);
              const groupIds = claims.map(c => c.id);
              const allGroupSelected = groupIds.every(id => selectedIds.has(id));
              const someGroupSelected = groupIds.some(id => selectedIds.has(id)) && !allGroupSelected;
              const totalDt = claims.reduce((s, c) => s + parseFloat(c.dtPrice), 0);
              const totalNetReimb = claims.reduce((s, c) => s + calcNetReimb(c), 0);
              const groupDrugNames = uniqueDrugNames(claims);
              return (
                <React.Fragment key={groupName}>
                  <tr
                    className={`border-t cursor-pointer ${dark ? "border-gray-800 hover:bg-gray-800/40" : "border-gray-100 hover:bg-gray-50"} ${gi % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"} ${allGroupSelected ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : ""}`}
                    onClick={() => toggleClaimGroup(groupName)}
                  >
                    <td className="pl-3 pr-3 py-3 cursor-pointer" onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) toggleClaimGroupSelection(groupIds); }}>
                      <Checkbox
                        checked={allGroupSelected}
                        ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someGroupSelected; }}
                        onCheckedChange={() => toggleClaimGroupSelection(groupIds)}
                      />
                    </td>
                    <td className={`pl-1 pr-6 py-3 font-medium ${dark ? "text-white" : "text-gray-900"}`}>
                      <div className="flex items-center gap-2">
                        <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""} ${dark ? "text-gray-500" : "text-gray-400"}`} />
                        {claimsGroupBy === "drug" ? <Pill className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`} /> : claimsGroupBy === "category" ? <Tag className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`} /> : <Users className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`} />}
                        <div className="min-w-0">
                          <div className="truncate max-w-[320px]">{groupName}</div>
                          {(claimsGroupBy === "patient" || claimsGroupBy === "category") && groupDrugNames.length > 0 && (
                            <div
                              title={groupDrugNames.join(", ")}
                              className={`text-[10px] font-normal truncate max-w-[320px] ${dark ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {summariseDrugNames(groupDrugNames)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-500"}`} colSpan={6}>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                        {claims.length} claim{claims.length !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right"><DtNetReimbCell dt={totalDt} netReimb={totalNetReimb} dark={dark} /></td>
                    <StatusChips items={claims} dark={dark} resolveRtec={resolveRtec} />
                  </tr>
                  {isExpanded && claims.slice(0, ROWS_PAGE_SIZE).map((row, i) => (
                    <ClaimRowLine key={row.id} row={row} i={i} dark={dark} isChecked={selectedIds.has(row.id)} onToggle={toggleOne} onOpen={openClaimFromList} focusDrug={claimsGroupBy === "drug" ? groupName : undefined} exemptionStyle={exemptionStyle} itemCount={row.itemCount} resolveRtec={resolveRtec} indent />
                  ))}
                </React.Fragment>
              );
            })}
            {(() => {
              const total = claimsGroupBy === "none" ? prescriptions.length : claimGroups.length;
              if (isTitanClaimsLoading || total <= claimsVisibleCount) return null;
              return (
                <ShowMoreRow
                  shown={claimsVisibleCount}
                  total={total}
                  columns={10}
                  dark={dark}
                  onShowMore={() => setClaimsVisibleCount(count => count + ROWS_PAGE_SIZE)}
                />
              );
            })()}
          </tbody>
        </table>
      </div>

      {/* Selection action bar */}
      {claimsView === "claims" && selectedIds.size > 0 && (() => {
        const selectedRows = allRows.filter(r => selectedIds.has(r.id));
        const blockedRows = selectedRows.filter(claimNeedsExemption);
        const claimableRows = selectedRows.filter(r => !claimNeedsExemption(r));
        const warnCount = claimableRows.filter(r => getAttentionReasons(r).length > 0).length;
        return createPortal(
          <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-2xl backdrop-blur-md transition-all ${dark ? "bg-gray-900/95 border-gray-700" : "bg-white/95 border-gray-200"}`}>
            <span className={`text-sm font-medium ${dark ? "text-white" : "text-gray-900"}`}>{selectedIds.size} selected</span>
            {blockedRows.length > 0 && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>
                <CircleAlert className="w-3 h-3" /> {blockedRows.length} missing exemption
              </span>
            )}
            {warnCount > 0 && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                <CircleAlert className="w-3 h-3" /> {warnCount} need attention
              </span>
            )}
            <div className={`w-px h-5 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
            <button
              onClick={() => {
                if (claimableRows.length === 0) return;
                if (warnCount > 0) setClaimAttentionWarningOpen(true);
                else claimRows(claimableRows);
              }}
              disabled={claimableRows.length === 0}
              title={blockedRows.length > 0
                ? `${blockedRows.length} of ${selectedRows.length} selected can't be claimed until an exemption is set`
                : undefined}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white transition-colors ${claimableRows.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-emerald-500 cursor-pointer"}`}
            >
              <Check className="w-3.5 h-3.5" /> Claim Selected ({claimableRows.length})
            </button>
            <button
              onClick={() => runBulkRtec(selectedRows)}
              disabled={rtecBulkChecking}
              title="Run a Real Time Exemption Check against the NHS Spine for the selected items"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${rtecBulkChecking ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"}`}
            >
              {rtecBulkChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              {rtecBulkChecking ? `Checking ${selectedIds.size}…` : `Run RTEC Check (${selectedIds.size})`}
            </button>
            {rtecBulkMessage && (
              <span className={`text-[11px] ${dark ? "text-gray-300" : "text-gray-600"}`}>{rtecBulkMessage}</span>
            )}
            <button
              onClick={() => copyExemptions(selectedRows)}
              disabled={copyingExemptions}
              title="Copy each patient's last recorded exemption onto the selected prescriptions"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${copyingExemptions ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"}`}
            >
              {copyingExemptions ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
              {copyingExemptions ? `Copying ${selectedIds.size}…` : `Copy Exemptions (${selectedIds.size})`}
            </button>
            <button
              onClick={() => setClearClaimsWarningOpen(true)}
              disabled={clearingClaims}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${clearingClaims ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${dark ? "bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30" : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"}`}
            >
              {clearingClaims ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {clearingClaims ? `Clearing ${selectedIds.size}…` : "Clear"}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className={`p-2 rounded-lg transition-colors cursor-pointer ${dark ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>,
          document.body
        );
      })()}
      </div>
      )}

      {clearClaimsWarningOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-sm rounded-xl border shadow-2xl p-5 ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
            <div className="flex items-center gap-2 mb-2">
              <CircleAlert className={`w-5 h-5 ${dark ? "text-red-400" : "text-red-600"}`} />
              <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Clear selected prescriptions?</h3>
            </div>
            <p className={`text-xs leading-relaxed mb-4 ${dark ? "text-gray-400" : "text-gray-600"}`}>
              You are about to clear {selectedIds.size} prescription{selectedIds.size === 1 ? "" : "s"} from the claim screen. They will no longer appear here and will not be claimed.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setClearClaimsWarningOpen(false)}
                className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setClearClaimsWarningOpen(false);
                  clearClaimRows(allRows.filter(r => selectedIds.has(r.id)));
                }}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Needs-attention warning before claiming */}
      {claimAttentionWarningOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-sm rounded-xl border shadow-2xl p-5 ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
            <div className="flex items-center gap-2 mb-2">
              <CircleAlert className={`w-5 h-5 ${dark ? "text-amber-400" : "text-amber-600"}`} />
              <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Some items still need attention</h3>
            </div>
            {(() => {
              const selectedRows = allRows.filter(r => selectedIds.has(r.id));
              const blockedCount = selectedRows.filter(claimNeedsExemption).length;
              const claimableCount = selectedRows.length - blockedCount;
              const warnCount = selectedRows.filter(r => !claimNeedsExemption(r) && getAttentionReasons(r).length > 0).length;
              return (
                <p className={`text-xs leading-relaxed mb-4 ${dark ? "text-gray-400" : "text-gray-600"}`}>
                  {warnCount} of {claimableCount} item(s) about to be claimed have an incomplete endorsement or a better pack available. Claiming now may result in an incorrect or reduced claim.
                  {blockedCount > 0 && ` ${blockedCount} further item(s) have no confirmed exemption and will not be claimed.`}
                </p>
              );
            })()}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setClaimAttentionWarningOpen(false)}
                className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}
              >
                Review Items
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  claimRows(allRows.filter(r => selectedIds.has(r.id) && !claimNeedsExemption(r)));
                  setClaimAttentionWarningOpen(false);
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
              >
                Claim Anyway
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {(visitedViews.has("claimed") || claimsView === "claimed") && (() => {
        const isPreviousCycle = isPreviousClaimedCycle;
        const cycleLabel = isPreviousCycle ? PREVIOUS_CYCLE_LABEL : CURRENT_CYCLE_LABEL;
        const isClaimedLoading = isPreviousCycle ? isClaimedLastLoading : isClaimedCurrentLoading;
        const { totalDt, totalCost, totalMargin } = claimedTotals;
        const { claimedCategoryCounts, claimedCategoryTypes, claimedExemptionCounts, claimedExemptionTypes } = claimedFacets;
        const claimedActiveFilterCount = (claimedCategoryFilter !== "All" ? 1 : 0) + (claimedExemptionFilter !== "All" ? 1 : 0) + (claimedGroupBy !== "none" ? 1 : 0);

        const toggleClaimedGroupSelection = (ids: string[]) => setClaimedSelectedIds(prev => {
          const allIn = ids.every(id => prev.has(id));
          const next = new Set(prev);
          ids.forEach(id => { if (allIn) next.delete(id); else next.add(id); });
          return next;
        });
        const toggleClaimedGroup = (groupKey: string) => setExpandedClaimedGroups(prev => {
          const next = new Set(prev);
          if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
          return next;
        });
        const allClaimedSelected = claimedRows.length > 0 && claimedRows.every(r => claimedSelectedIds.has(r.id));
        const someClaimedSelected = claimedRows.some(r => claimedSelectedIds.has(r.id)) && !allClaimedSelected;
        const toggleAllClaimed = () => {
          if (allClaimedSelected) setClaimedSelectedIds(new Set());
          else setClaimedSelectedIds(new Set(claimedRows.map(r => r.id)));
        };

        return (
          <div hidden={claimsView !== "claimed"}>
            {/* Claimed cycle summary */}
            <div className="grid grid-cols-4 gap-4 mb-5">
              <div className={`rounded-xl border p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <p className={`text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>Claimed in {cycleLabel}</p>
                <p className={`text-2xl font-semibold mt-1 ${dark ? "text-white" : "text-gray-900"}`}>{claimedRows.length}</p>
              </div>
              <div className={`rounded-xl border p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <p className={`text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>Total DT value</p>
                <p className={`text-2xl font-semibold mt-1 ${dark ? "text-white" : "text-gray-900"}`}>£{totalDt.toFixed(2)}</p>
              </div>
              <div className={`rounded-xl border p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <p className={`text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>Total cost</p>
                <p className={`text-2xl font-semibold mt-1 ${dark ? "text-white" : "text-gray-900"}`}>£{totalCost.toFixed(2)}</p>
              </div>
              <div className={`rounded-xl border p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                <p className={`text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>Margin</p>
                <p className={`text-2xl font-semibold mt-1 ${totalMargin >= 0 ? dark ? "text-emerald-400" : "text-emerald-600" : dark ? "text-red-400" : "text-red-600"}`}>£{totalMargin.toFixed(2)}</p>
              </div>
            </div>

            {/* Patient search + Filters */}
            <div className="mb-3">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${dark ? "text-gray-500" : "text-gray-400"}`} />
                <Input
                  placeholder={`Search ${cycleLabel} claims...`}
                  value={claimedSearch}
                  onChange={e => { setClaimedSearch(e.target.value); setClaimedSelectedIds(new Set()); }}
                  className={`pl-10 transition-colors duration-300 ${dark ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400" : "bg-white border-gray-300"}`}
                />
                {claimedSearch && (
                  <button onClick={() => setClaimedSearch("")} className={`absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer ${dark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <Popover
                open={claimedFilterOpen}
                onOpenChange={(open) => {
                  setClaimedFilterOpen(open);
                  if (open) { setPendingClaimedCategoryFilter(claimedCategoryFilter); setPendingClaimedExemptionFilter(claimedExemptionFilter); setPendingClaimedGroupBy(claimedGroupBy); }
                }}
              >
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`relative ${dark ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700" : "bg-white border-gray-300 hover:bg-gray-50"}`}>
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {claimedActiveFilterCount > 0 && (
                      <Badge className="ml-2 px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center" variant="default">{claimedActiveFilterCount}</Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" collisionPadding={16} className={`w-[560px] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-4 ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 flex items-center justify-between">
                      <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Filter Claimed</h3>
                      {claimedActiveFilterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => { setPendingClaimedCategoryFilter("All"); setPendingClaimedExemptionFilter("All"); setPendingClaimedGroupBy("none"); }} className={`h-7 text-xs ${dark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}>
                          <X className="w-3 h-3 mr-1" /> Reset
                        </Button>
                      )}
                    </div>

                    <Separator className={`col-span-2 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />

                    <div className="space-y-2">
                      <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Exemption Status</label>
                      <Select value={pendingClaimedExemptionFilter} onValueChange={setPendingClaimedExemptionFilter}>
                        <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                          <SelectValue placeholder="Exemption status" />
                        </SelectTrigger>
                        <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                          {claimedExemptionTypes.map(type => (
                            <SelectItem key={type} value={type}>{type} ({type === "All" ? claimedRowsAll.length : (claimedExemptionCounts[type] ?? 0)})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Category</label>
                      <Select value={pendingClaimedCategoryFilter} onValueChange={setPendingClaimedCategoryFilter}>
                        <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                          {claimedCategoryTypes.map(type => (
                            <SelectItem key={type} value={type}>{type} ({type === "All" ? claimedRowsAll.length : (claimedCategoryCounts[type] ?? 0)})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2 space-y-2">
                      <label className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>Group By</label>
                      <Select value={pendingClaimedGroupBy} onValueChange={(v: "none" | "drug" | "patient") => setPendingClaimedGroupBy(v)}>
                        <SelectTrigger className={`w-full ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300"}`}>
                          <SelectValue placeholder="Group by" />
                        </SelectTrigger>
                        <SelectContent className={dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="drug">Drug</SelectItem>
                          <SelectItem value="patient">Patient</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2 flex gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => setClaimedFilterOpen(false)} className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}>Cancel</Button>
                      <Button size="sm" onClick={() => { setClaimedCategoryFilter(pendingClaimedCategoryFilter); setClaimedExemptionFilter(pendingClaimedExemptionFilter); setClaimedGroupBy(pendingClaimedGroupBy); setExpandedClaimedGroups(new Set()); setClaimedSelectedIds(new Set()); setClaimedFilterOpen(false); }} className="flex-1 bg-black hover:bg-gray-900 text-white">Apply Filters</Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            </div>

            <div className={`rounded-lg border overflow-x-auto ${dark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
              <table className="w-full text-sm">
                <thead className={`sticky top-0 transition-colors duration-300 ${dark ? "bg-gray-700" : "bg-gray-50"}`}>
                  <tr>
                    <th className="pl-3 pr-1 py-3 w-10">
                      <Checkbox checked={allClaimedSelected} ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someClaimedSelected; }} onCheckedChange={toggleAllClaimed} />
                    </th>
                    {[claimedGroupBy === "drug" ? "Drug / Patient Name" : "Patient Name", "Age", "Items", "Prescription Type", "Category", "Exemption Status", "Rx Date", "Claimed Date", "DT / Net Reimb", "Warnings"].map(h => (
                      <th key={h} className={`px-3 py-3 text-xs font-medium uppercase tracking-wider ${h === "DT / Net Reimb" ? "text-right" : "text-left"} ${dark ? "text-gray-300" : "text-gray-500"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isClaimedLoading ? (
                    <TableRowsSkeleton isDarkMode={dark} columns={11} rows={8} />
                  ) : claimedRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-12">
                        <Check className={`w-6 h-6 mx-auto mb-2 ${dark ? "text-gray-600" : "text-gray-300"}`} />
                        <p className={`text-sm ${dark ? "text-gray-500" : "text-gray-400"}`}>{isPreviousCycle ? `No claims recorded in ${cycleLabel}` : "Nothing claimed yet this cycle"}</p>
                      </td>
                    </tr>
                  ) : claimedGroupBy === "none" ? claimedPrescriptions.slice(0, claimedVisibleCount).map((script, gi) => {
                    if (script.items.length === 1) {
                      const row = script.items[0];
                      return <ClaimRowLine key={row.id} row={row} i={gi} dark={dark} isChecked={claimedSelectedIds.has(row.id)} onToggle={toggleClaimedOne} onOpen={openClaimedEdit} exemptionStyle={exemptionStyle} claimedAt={row._claimedAt} itemCount={row.itemCount} resolveRtec={resolveRtec} />;
                    }
                    const groupIds = script.items.map(c => c.id);
                    const allGroupSelected = groupIds.every(id => claimedSelectedIds.has(id));
                    const someGroupSelected = groupIds.some(id => claimedSelectedIds.has(id)) && !allGroupSelected;
                    const gDt = script.items.reduce((s, c) => s + parseFloat(c.dtPrice), 0);
                    const gNetReimb = script.items.reduce((s, c) => s + calcNetReimb(c), 0);
                    return (
                      <tr
                        key={script.key}
                        className={`border-t cursor-pointer ${dark ? "border-gray-800 hover:bg-gray-800/40" : "border-gray-100 hover:bg-gray-50"} ${gi % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"} ${allGroupSelected ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : ""}`}
                        onClick={() => openClaimedEdit(script.items[0])}
                      >
                        <td className="pl-3 pr-3 py-3 cursor-pointer" onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) toggleClaimedGroupSelection(groupIds); }}>
                          <Checkbox checked={allGroupSelected} ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someGroupSelected; }} onCheckedChange={() => toggleClaimedGroupSelection(groupIds)} />
                        </td>
                        <td className={`pl-1 pr-6 py-3 font-medium ${dark ? "text-white" : "text-gray-900"}`}>{script.patient}</td>
                        <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>{ageDisplay(script.items[0])}</td>
                        <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>
                          {(() => {
                            const chargeable = isMissingExemption(script.exemption);
                            const chargeCount = chargeable ? script.items.length : 0;
                            return (
                              <div className="leading-tight">
                                <span>{script.items.length}</span>
                                <span className={`block text-[10px] font-normal ${dark ? "text-gray-500" : "text-gray-400"}`}>{chargeCount} NHS charge{chargeCount !== 1 ? "s" : ""}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className={`px-3 py-3 text-xs font-medium ${dark ? "text-gray-300" : "text-gray-700"}`}>{rowPrescriptionType(script.items[0])}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {Array.from(new Set(script.items.flatMap(c => rowCategories(c)))).map(cat => (
                              <CategoryTag key={cat} category={cat} dark={dark} />
                            ))}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm ${dark ? "text-gray-300" : "text-gray-700"}`}>{script.exemption}</span>
                            <RtecChip status={script.items.every(it => resolveRtec(it) !== "RTEC Not Done") ? "RTEC Exempt" : "RTEC Not Done"} dark={dark} />
                          </div>
                        </td>
                        <td className={`px-3 py-3 ${dark ? "text-gray-300" : "text-gray-700"}`}>{script.rxDate}</td>
                        <td className={`px-3 py-3 ${dark ? "text-gray-300" : "text-gray-700"}`}>{script.claimedAt}</td>
                        <td className="px-3 py-3 text-right"><DtNetReimbCell dt={gDt} netReimb={gNetReimb} dark={dark} /></td>
                        <StatusChips items={script.items} dark={dark} resolveRtec={resolveRtec} />
                      </tr>
                    );
                  }) : claimedGroups.slice(0, claimedVisibleCount).map(([groupName, claims], gi) => {
                    const isExpanded = expandedClaimedGroups.has(groupName);
                    const groupIds = claims.map(c => c.id);
                    const allGroupSelected = groupIds.every(id => claimedSelectedIds.has(id));
                    const someGroupSelected = groupIds.some(id => claimedSelectedIds.has(id)) && !allGroupSelected;
                    const gDt = claims.reduce((s, c) => s + parseFloat(c.dtPrice), 0);
                    const gNetReimb = claims.reduce((s, c) => s + calcNetReimb(c), 0);
                    return (
                      <React.Fragment key={groupName}>
                        <tr
                          className={`border-t cursor-pointer ${dark ? "border-gray-800 hover:bg-gray-800/40" : "border-gray-100 hover:bg-gray-50"} ${gi % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"} ${allGroupSelected ? dark ? "bg-gray-800/70" : "bg-blue-50/60" : ""}`}
                          onClick={() => toggleClaimedGroup(groupName)}
                        >
                          <td className="pl-3 pr-3 py-3 cursor-pointer" onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) toggleClaimedGroupSelection(groupIds); }}>
                            <Checkbox checked={allGroupSelected} ref={(el: HTMLButtonElement | null) => { if (el) (el as any).indeterminate = someGroupSelected; }} onCheckedChange={() => toggleClaimedGroupSelection(groupIds)} />
                          </td>
                          <td className={`pl-1 pr-6 py-3 font-medium ${dark ? "text-white" : "text-gray-900"}`}>
                            <div className="flex items-center gap-2">
                              <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""} ${dark ? "text-gray-500" : "text-gray-400"}`} />
                              {claimedGroupBy === "drug" ? <Pill className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`} /> : <Users className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-gray-500" : "text-gray-400"}`} />}
                              <div className="min-w-0">
                                <div className="truncate max-w-[320px]">{groupName}</div>
                                {claimedGroupBy === "patient" && (
                                  <div
                                    title={uniqueDrugNames(claims).join(", ")}
                                    className={`text-[10px] font-normal truncate max-w-[320px] ${dark ? "text-gray-500" : "text-gray-400"}`}
                                  >
                                    {summariseDrugNames(uniqueDrugNames(claims))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className={`px-3 py-3 ${dark ? "text-gray-400" : "text-gray-500"}`} colSpan={7}>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                              {claims.length} claim{claims.length !== 1 ? "s" : ""}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right"><DtNetReimbCell dt={gDt} netReimb={gNetReimb} dark={dark} /></td>
                          <StatusChips items={claims} dark={dark} resolveRtec={resolveRtec} />
                        </tr>
                        {isExpanded && claims.slice(0, ROWS_PAGE_SIZE).map((row, i) => (
                          <ClaimRowLine key={row.id} row={row} i={i} dark={dark} isChecked={claimedSelectedIds.has(row.id)} onToggle={toggleClaimedOne} onOpen={openClaimedEdit} focusDrug={claimedGroupBy === "drug" ? groupName : undefined} exemptionStyle={exemptionStyle} claimedAt={row._claimedAt} itemCount={row.itemCount} resolveRtec={resolveRtec} indent />
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {(() => {
                    const total = claimedGroupBy === "none" ? claimedPrescriptions.length : claimedGroups.length;
                    if (isClaimedLoading || total <= claimedVisibleCount) return null;
                    return (
                      <ShowMoreRow
                        shown={claimedVisibleCount}
                        total={total}
                        columns={11}
                        dark={dark}
                        onShowMore={() => setClaimedVisibleCount(count => count + ROWS_PAGE_SIZE)}
                      />
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {/* Claimed selection action bar */}
            {claimsView === "claimed" && claimedSelectedIds.size > 0 && createPortal(
              <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-2xl backdrop-blur-md transition-all ${dark ? "bg-gray-900/95 border-gray-700" : "bg-white/95 border-gray-200"}`}>
                <span className={`text-sm font-medium ${dark ? "text-white" : "text-gray-900"}`}>{claimedSelectedIds.size} selected</span>
                <div className={`w-px h-5 ${dark ? "bg-gray-700" : "bg-gray-200"}`} />
                <button
                  onClick={() => setClaimedSelectedIds(new Set())}
                  className={`p-2 rounded-lg transition-colors cursor-pointer ${dark ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                  title="Clear selection"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>,
              document.body
            )}
          </div>
        );
      })()}

      {/* Prescription detail modal */}
      {prescriptionModal && (
        <PrescriptionDetailModal
          dark={dark}
          detail={prescriptionModal}
          pharmacyId={activeBranch}
          onClose={() => setPrescriptionModal(null)}
          onItemEndorsementChange={(itemId, endorsements) => {
            setPrescriptionModal(prev => prev ? { ...prev, items: prev.items.map(it => it.id === itemId ? { ...it, endorsements } : it) } : null);
          }}
          onExemptionChange={(update) => {
            setPrescriptionModal(prev => prev ? { ...prev, ...update } : null);
          }}
          onExemptionSaved={(exemptionStatusType) => applyExemptionToRow(prescriptionModal.id, prescriptionModal.prescriptionIncomingId, exemptionStatusType)}
          onClaimSaved={onClaimSaved}
          onClaimAmended={() => { if (activeBranch) { loadClaims(activeBranch); refreshClaimedCycle(); } }}
          onClear={(() => {
            if (prescriptionModal.isClaimed) return undefined;
            const row = allRows.find(r => r.id === prescriptionModal.id)
              ?? (prescriptionModal.prescriptionIncomingId ? allRows.find(r => r.prescriptionIncomingId === prescriptionModal.prescriptionIncomingId) : undefined);
            if (!row || claimedClaimIds.has(row.id)) return undefined;
            return async () => {
              const cleared = await clearClaimRows([row]);
              if (cleared) setPrescriptionModal(null);
            };
          })()}
          onWarn={warn}
          onNotify={notify}
        />
      )}
    </div>
    </TooltipProvider>
  );
}

// ── PRESCRIPTION DETAIL MODAL ──────────────────────────────────────────────────
interface ItemProduct {
  productId: string;
  brandName: string;
  description: string;
  company: string;
  packSize: number;
  tradePrice: number;
  reimbursement: number;
  costForQty: number;
  reimbForQty: number;
  drugTariffCategory?: number;
}

const normDrugName = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Claim alternatives must stay the same medicine as the one dispensed: same
// product description (name + strength + form), differing only by manufacturer.
// Anything with a different product name (a brand, or another drug entirely)
// is dropped even when it reimburses better.
function sameDrugProducts(products: ItemProduct[], writtenAs: string, endorsedProductId?: string): ItemProduct[] {
  if (products.length === 0) return products;
  const endorsed = products.find(p => p.productId === endorsedProductId);
  let target = normDrugName(endorsed?.description ?? "");
  if (!target) {
    // No endorsed product to anchor on — match the prescribed name instead,
    // preferring an exact hit and otherwise the closest description by tokens.
    const written = normDrugName(writtenAs);
    const tokens = new Set(written.split(" ").filter(Boolean));
    let bestScore = 0;
    for (const p of products) {
      const d = normDrugName(p.description);
      if (!d) continue;
      if (d === written) { target = d; bestScore = Infinity; break; }
      const score = d.split(" ").filter(t => tokens.has(t)).length;
      if (score > bestScore) { bestScore = score; target = d; }
    }
    if (bestScore === 0) return products;
  }
  const same = products.filter(p => normDrugName(p.description) === target);
  return same.length > 0 ? same : products;
}

interface PrescriptionItem {
  id: string;
  drug: string;
  pack: string;
  quantity: string;
  dosage: string;
  dispensedManufacturer?: string;
  isSelected: boolean;
  ruleCategory?: string;
  ruleName?: string;
  dtPrice?: string;
  costPrice?: string;
  endorsements: { code: string; label: string; appliedDate?: string; extraInfo?: Record<string, string>; api?: any }[];
  requiredEndorsements: { code: string; label: string }[];
  brandOptions?: { id: string; brand: string }[];
  selectedBrandId?: string;
  packSizeOptions?: number[];
  selectedPackSize?: number;
  cheapestOption?: { brandId: string; packSize: number; cost: number };
  prescriptionItemId?: string;
  notDispensedReasonCode?: string;
  itemCrossedOut?: boolean;
  packagedDoses?: number;
  unitText?: string;
  drugTariffCategory?: number;
  prescribedItemId?: string;
  claimDtPrice?: string;
  products?: ItemProduct[];
  selProductIdx?: number;
  endorsedProductId?: string;
  savedProductId?: string;
  endorsedReimbPrice?: number;
  endorsedPackSize?: number;
  concession?: ConcessionProduct;
  dispenseItemId?: string;
  roundUpPacks?: boolean;
}

// Searching for a drug then opening the script should land on that drug.
function searchedDrugFor(row: ClaimRow, search: string): string | undefined {
  const term = normaliseSearch(search);
  if (!term) return undefined;
  return rowDrugNames(row).find(d => normaliseSearch(d).includes(term));
}

// Opening a claim from a drug-grouped row should land on that drug's item, not
// on whatever happens to be first on the script.
function focusItemIdFor(items: PrescriptionItem[], focusDrug?: string): string {
  const fallback = items[0]?.id ?? "";
  const target = normDrugName(focusDrug ?? "");
  if (!target) return fallback;
  const exact = items.find(i => normDrugName(i.drug) === target);
  if (exact) return exact.id;
  const partial = items.find(i => {
    const name = normDrugName(i.drug);
    return name.length > 0 && (name.includes(target) || target.includes(name));
  });
  return partial?.id ?? fallback;
}

interface PrescriptionDetail {
  id: string;
  prescriptionId?: string;
  prescriptionIncomingId?: string;
  focusDrug?: string;
  dobIso?: string;
  patient: string;
  rxDate: string;
  prescriber: string;
  epsBarcode: string;
  prescriptionType?: PrescriptionType;
  exemption: string;
  lastExemptionCheck?: string;
  treatmentType?: string;
  prescriptionImageUrl?: string;
  items: PrescriptionItem[];
  nhsNumber?: string;
  dob?: string;
  chargeStatus?: string;
  formType?: string;
  dispensedDate?: string;
  nominatedPharmacy?: boolean;
  exemptionCode?: string;
  exemptionCertNumber?: string;
  exemptionCertExpiry?: string;
  exemptionEvidenceSeen?: boolean;
  // Raw exemption model as returned by get-prescription-by-incoming; null/absent
  // means the prescription has no exemption on record yet.
  exemptionRecord?: any;
  isClaimed?: boolean;
  isLoading?: boolean;   // prescription + all per-item get-item-info calls in flight
  isImageLoading?: boolean;
  isItemInfoLoading?: boolean;
  isConcessionLoading?: boolean;
}

const endorsedProductFor = (item: PrescriptionItem): ItemProduct | undefined =>
  isRealGuid(item.endorsedProductId)
    ? (item.products ?? []).find(p => p.productId === item.endorsedProductId)
    : undefined;

function packCountFor(dispensedQty: number, packSize: number): number | null {
  if (!(dispensedQty > 0) || !(packSize > 0)) return null;
  return dispensedQty / packSize;
}

const isWholePackCount = (packCount: number): boolean =>
  Math.abs(packCount - Math.round(packCount)) < 1e-6;

const NON_SPLITTABLE_UNIT = /^(ml|millilitres?|milliliters?|l|litres?|liters?|g|grams?|gm|kg|kilograms?|doses?|actuations?|sprays?|puffs?)$/;

const isNonSplittableUnit = (unit: string): boolean =>
  NON_SPLITTABLE_UNIT.test(String(unit ?? "").trim().toLowerCase().replace(/\.$/, ""));

function roundUpPackOption(option: PackPriceOption): PackPriceOption {
  if (!(option.packCount > 0) || isWholePackCount(option.packCount)) return option;
  const packCount = Math.ceil(option.packCount);
  return {
    ...option,
    packCount,
    lineDt: option.packDt * packCount,
    lineNet: option.lineNet * (packCount / option.packCount),
    roundedFrom: option.packCount,
  };
}

const NO_BRAND_LABEL = "No Brand";
const NO_BRAND_COMPANY = "special order";

const isNoBrandProduct = (product?: ItemProduct): boolean =>
  !!product
  && product.drugTariffCategory == null
  && String(product.company ?? "").trim().toLowerCase() === NO_BRAND_COMPANY;

const isNotInDrugTariff = (product?: ItemProduct): boolean =>
  !!product && product.drugTariffCategory == null && !isNoBrandProduct(product);

function packPriceOptionsFor(item: PrescriptionItem, dispensedQty: number): PackPriceOption[] {
  const products = item.products ?? [];
  const dispensedProductId = isRealGuid(item.endorsedProductId) ? item.endorsedProductId : undefined;
  const byPackSize = new Map<number, { product: ItemProduct; idx: number }[]>();
  products.forEach((p, idx) => {
    if (packCountFor(dispensedQty, p.packSize) === null) return;
    if (!byPackSize.has(p.packSize)) byPackSize.set(p.packSize, []);
    byPackSize.get(p.packSize)!.push({ product: p, idx });
  });

  return Array.from(byPackSize.entries())
    .map(([packSize, entries]) => {
      const bestReimbursement = entries.reduce((best, e) => Math.max(best, e.product.reimbursement), 0);
      const dispensed = entries.find(e => e.product.productId === dispensedProductId);
      const brandRank = (e: { product: ItemProduct; idx: number }) =>
        e.idx === item.selProductIdx ? 2 : e === dispensed ? 1 : 0;
      const byBrandOrder = (a: { product: ItemProduct }, b: { product: ItemProduct }) =>
        Number(isNotInDrugTariff(a.product)) - Number(isNotInDrugTariff(b.product))
        || a.product.tradePrice - b.product.tradePrice;
      const byCompany = new Map<string, { product: ItemProduct; idx: number }>();
      entries
        .filter(e => !isNoBrandProduct(e.product))
        .filter(e => e === dispensed || isNotInDrugTariff(e.product) || e.product.reimbursement >= bestReimbursement - 0.005)
        .sort(byBrandOrder)
        .forEach(e => {
          const key = String(e.product.company || e.product.description || "—").trim().toLowerCase();
          const current = byCompany.get(key);
          if (!current || brandRank(e) > brandRank(current)) byCompany.set(key, e);
        });
      const branded = Array.from(byCompany.values())
        .sort(byBrandOrder)
        .map(e => ({
          productIdx: e.idx,
          company: e.product.company || e.product.description || "—",
          notInDt: isNotInDrugTariff(e.product),
        }));

      const noBrandEntries = entries.filter(e => isNoBrandProduct(e.product));
      const noBrand = noBrandEntries.length > 0
        ? [{
            productIdx: (noBrandEntries.find(e => e === dispensed) ?? noBrandEntries[0]).idx,
            company: NO_BRAND_LABEL,
            isNoBrand: true,
          }]
        : [];

      const brands = [...branded, ...noBrand];

      const chosen =
        entries.find(e => e.idx === item.selProductIdx)
        ?? dispensed
        ?? entries.find(e => e.idx === brands[0]?.productIdx)
        ?? entries[0];
      const packCount = packCountFor(dispensedQty, packSize) ?? 0;
      const lineReimbursement = chosen.product.reimbursement * packCount;
      // Each pack option's own DT category decides its deduction — claiming a
      // different pack can move the item between Part VIIIA and the branded
      // scale, so the rate is read from the chosen product, not the item.
      const netRate = 1 - tariffNetReimbRate(chosen.product.drugTariffCategory ?? item.drugTariffCategory);
      return {
        productIdx: chosen.idx,
        packSize,
        packDt: chosen.product.reimbursement,
        packCount,
        lineDt: lineReimbursement,
        lineNet: lineReimbursement * netRate,
        inDrugTariff: !isNotInDrugTariff(chosen.product),
        brands,
      };
    })
    .sort((a, b) => b.packSize - a.packSize);
}

function itemBetterPackInfo(item: PrescriptionItem): { productIdx: number; gain: number; baselineReimb: number; baselineDt: number } | null {
  const products = item.products ?? [];
  const endorsed = endorsedProductFor(item);
  if (!endorsed || !(endorsed.reimbForQty > 0)) return null;
  let bestIdx = -1;
  let bestReimb = endorsed.reimbForQty;
  products.forEach((p, pi) => {
    if (p.productId === endorsed.productId) return;
    if (p.reimbForQty > bestReimb + 0.005) {
      bestIdx = pi;
      bestReimb = p.reimbForQty;
    }
  });
  if (bestIdx < 0) return null;
  return {
    productIdx: bestIdx,
    gain: bestReimb - endorsed.reimbForQty,
    baselineReimb: endorsed.reimbForQty,
    baselineDt: endorsed.costForQty,
  };
}

// Current NHS (England) EPS prescription charge exemption categories.
// `requiresCertificate` categories capture a certificate number + expiry date
// (e.g. maternity / medical exemption certificate, PPC, HC2, war pension).
interface ExemptionCategory {
  code: string;
  label: string;
  badge: string;
  requiresCertificate?: boolean;
  paysCharge?: boolean;
  // Age is proved by the date of birth on the prescription, so there is no
  // evidence for the pharmacist to sight and the checkbox stays off.
  noEvidence?: boolean;
}

// Codes and wording match the exemption boxes on the reverse of the current FP10.
// Ordered by box letter, as printed on the back of the prescription.
const EXEMPTION_CATEGORIES: ExemptionCategory[] = [
  { code: "A", label: "(A) is 60 years of age or over or is under 16 years of age", badge: "(A) Age exempt", noEvidence: true },
  { code: "B", label: "(B) is 16, 17 or 18 and in full-time education", badge: "(B) 16–18 Education" },
  { code: "D", label: "(D) has a valid maternity exemption certificate", badge: "(D) Maternity", requiresCertificate: true },
  { code: "E", label: "(E) has a valid medical exemption certificate", badge: "(E) Medical", requiresCertificate: true },
  { code: "F", label: "(F) has a valid prescription prepayment certificate (PPC)", badge: "(F) Prepayment", requiresCertificate: true },
  { code: "G", label: "(G) has a Prescription Exemption Certificate issued by the Ministry of Defence", badge: "(G) MoD certificate", requiresCertificate: true },
  { code: "H", label: "(H) gets Income Support or income-related Employment and Support Allowance (ESA)", badge: "(H) Income Support" },
  { code: "K", label: "(K) gets income-based Jobseeker's Allowance (JSA)", badge: "(K) Income-based JSA" },
  { code: "L", label: "(L) is named on a current HC2 charges certificate", badge: "(L) HC2", requiresCertificate: true },
  { code: "M", label: "(M) has a valid NHS Tax Credit Exemption Certificate", badge: "(M) Tax Credit", requiresCertificate: true },
  { code: "S", label: "(S) gets Pension Credit Guarantee Credit", badge: "(S) Pension Credit" },
  { code: "U", label: "(U) gets Universal Credit and meets the criteria for free help with health costs", badge: "(U) Universal Credit" },
  { code: "W", label: "(W) has a valid HRT prepayment certificate (HRT PPC)", badge: "(W) HRT certificate", requiresCertificate: true },
  { code: "XC", label: "(X) is supplied free-of-charge contraception", badge: "(X) Contraceptive", noEvidence: true },
  { code: "X", label: "Not exempt — patient pays the charge", badge: "Not Exempt", paysCharge: true },
];

const EXEMPTION_STATUS_TYPES: Record<string, number> = {
  A: 2,
  B: 3,
  D: 5,
  E: 6,
  F: 7,
  G: 8,
  H: 11,
  K: 12,
  L: 9,
  M: 13,
  S: 14,
  U: 17,
  W: 21,
  XC: 10,
  X: 1,
};

const EXEMPTION_CODE_BY_STATUS_TYPE: Record<number, string> = Object.entries(EXEMPTION_STATUS_TYPES)
  .reduce((acc, [code, type]) => { acc[type] = code; return acc; }, {} as Record<number, string>);

const EXEMPTION_STATUS_LABELS: Record<number, string> = {
  4: "(C) 60 years of age or older",
  10: "(X) Free-of-charge contraceptive",
  15: "Free-of-charge HMP / detained patient medication",
  16: "Charge paid at old rate",
  18: "(Y) Free-of-charge sexual health medication",
  19: "RTEC — valid maternity exemption certificate",
  20: "RTEC — does not need to pay the charge",
  21: "(W) HRT certificate",
  22: "No charge (Wales)",
  23: "Welsh Entitlement Card",
};

const CHARGE_PAID_STATUS_TYPES = [1, 16];

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// save-claims reports a per-prescription outcome; a row is only claimed when
// its entry comes back with isClaimed true.
const claimOutcomes = (result: any): { prescriptionId?: string; isClaimed: boolean }[] =>
  (Array.isArray(result?.results) ? result.results : []).map((o: any) => ({
    prescriptionId: o?.prescriptionId ?? o?.PrescriptionId,
    isClaimed: Boolean(o?.isClaimed ?? o?.IsClaimed),
  }));

// Prescription images come back as bare base64 with no mime type, and they are
// not always PNG, so derive the type from the payload's magic prefix.
const BASE64_IMAGE_TYPES: [string, string][] = [
  ["R0lGOD", "image/gif"],
  ["iVBORw0KGgo", "image/png"],
  ["/9j/", "image/jpeg"],
  ["UklGR", "image/webp"],
  ["Qk", "image/bmp"],
  ["JVBERi0", "application/pdf"],
];

const toImageDataUrl = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  const base64 = raw.trim();
  if (base64.startsWith("data:")) return base64;
  const match = BASE64_IMAGE_TYPES.find(([prefix]) => base64.startsWith(prefix));
  return `data:${match?.[1] ?? "image/png"};base64,${base64}`;
};

const isRealGuid = (id: unknown): id is string =>
  typeof id === "string" && id.length > 0 && id !== EMPTY_GUID;

// Maps the legacy demo exemption labels onto the FP10 box letters.
const LEGACY_EXEMPTION_CODES: Record<string, string> = {
  "Exempt": "A",
  "Maternity": "D",
  "Medical": "E",
  "Prepayment": "F",
  "Not Exempt": "X",
};

const findExemptionCategory = (detail: PrescriptionDetail): ExemptionCategory | undefined =>
  EXEMPTION_CATEGORIES.find(c => c.code === detail.exemptionCode)
  ?? EXEMPTION_CATEGORIES.find(c => c.badge === detail.exemption)
  ?? EXEMPTION_CATEGORIES.find(c => c.code === LEGACY_EXEMPTION_CODES[detail.exemption]);

// Fields captured when an endorsement is added; all of them are mandatory.
// `api` names the add-endorsement request property the value is posted in;
// fields without one are labelled and appended to Details.
type EndorsementApiField =
  | "value" | "quantity" | "price" | "supplier" | "packSize"
  | "brand" | "batchNumber" | "dispenseDate" | "mhranumber" | "details";

const ENDORSEMENT_EXTRA_FIELDS: Record<string, { key: string; label: string; placeholder: string; required?: boolean; type?: "text" | "date" | "number"; api?: EndorsementApiField }[]> = {
  SP: [
    { key: "supplier", label: "Supplier / Manufacturer", placeholder: "e.g. Specials Lab Ltd", required: true, api: "supplier" },
    { key: "price", label: "Price Charged (£)", placeholder: "e.g. 120.00", required: true, type: "number", api: "price" },
    { key: "packSize", label: "Pack Size", placeholder: "e.g. 150ml", required: true, api: "packSize" },
    { key: "formulation", label: "Formulation", placeholder: "e.g. Oral Suspension", required: true },
    { key: "batchNumber", label: "Batch Number", placeholder: "e.g. SL-2026-0342", required: true, api: "batchNumber" },
  ],
  NCSO: [
    { key: "pharmacistInitials", label: "Pharmacist Initials", placeholder: "e.g. FM", required: true },
    { key: "dispensedDate", label: "Dispensed Date", placeholder: "", required: true, type: "date", api: "dispenseDate" },
    { key: "manufacturer", label: "Manufacturer / Brand Name", placeholder: "e.g. Accord Healthcare", required: true, api: "brand" },
    { key: "packSize", label: "Pack Size", placeholder: "e.g. 28 tablets", required: true, api: "packSize" },
  ],
  SSP: [
    { key: "sspReference", label: "SSP Reference Number", placeholder: "e.g. SSP-001-2026", required: true },
    { key: "alternativeDrug", label: "Alternative Drug Supplied", placeholder: "e.g. Sertraline 50mg Tablets", required: true },
    { key: "reason", label: "Reason for Supply", placeholder: "e.g. Original drug unavailable", required: true },
  ],
  ED: [
    { key: "formula", label: "Formula / Ingredients", placeholder: "e.g. Aqueous cream with 1% hydrocortisone", required: true },
    { key: "quantity", label: "Quantity Prepared", placeholder: "e.g. 100", required: true, type: "number", api: "quantity" },
  ],
  MF: [
    { key: "measurements", label: "Measurements Taken", placeholder: "e.g. Heel-to-knee: 42cm", required: true },
  ],
  IP: [
    { key: "price", label: "Price (£)", placeholder: "e.g. 120.00", required: true, type: "number", api: "price" },
    { key: "packSize", label: "Pack Size", placeholder: "e.g. 28 tablets", required: true, api: "packSize" },
    { key: "supplier", label: "Supplier", placeholder: "e.g. AAH Pharmaceuticals", required: true, api: "supplier" },
    { key: "licenceNumber", label: "Manufacturer's / Importer's Licence No.", placeholder: "e.g. ML 12345", required: true, api: "mhranumber" },
    { key: "batchNumber", label: "Batch Number", placeholder: "e.g. SL-2026-0342", required: true, api: "batchNumber" },
  ],
  XP: [
    { key: "details", label: "Details", placeholder: "e.g. Courier charge for urgent supply", required: true, api: "details" },
    { key: "totalValue", label: "Total Value (£)", placeholder: "e.g. 4.50", required: true, type: "number", api: "value" },
  ],
};

// Endorsement codes the backend PrescriptionItemEndorseType enum accepts; `value`
// is the enum member sent as additionEndorseType on /Claims/add-endorsement.
const ENDORSEMENT_OPTIONS: { code: string; label: string; value: number }[] = [
  { code: "SSP", label: "Serious Shortage Protocol (SSP)", value: 20 },
  { code: "NCSO", label: "No Cheaper Stock Obtainable (NCSO)", value: 8 },
  { code: "SP", label: "Special / Specials Endorsement (SP)", value: 6 },
  { code: "FS", label: "Free Supply of Sexual Health Treatment (FS)", value: 19 },
  { code: "MF", label: "Measured and Fitted (MF)", value: 2 },
  { code: "ED", label: "Extemporaneously Dispensed (ED)", value: 4 },
  { code: "IP", label: "Price for Less Common Products (IP)", value: 12 },
  { code: "BB", label: "Broken Bulk (BB)", value: 1 },
  { code: "XP", label: "Out of Pocket Expenses (XP)", value: 3 },
  { code: "PC", label: "Prescriber Contacted (PC)", value: 9 },
  { code: "PNC", label: "Prescriber Not Contacted (PNC)", value: 10 },
  { code: "NDEC", label: "No Dispenser Endorsement Code (NDEC)", value: 11 },
  { code: "RC", label: "Rebate Claimed (RC)", value: 5 },
  { code: "PD", label: "Packaged Doses (PD)", value: 14 },
  { code: "ACBS", label: "Advisory Committee on Borderline Substances (ACBS)", value: 17 },
  { code: "SLS", label: "Selected List Scheme (SLS)", value: 16 },
  { code: "AF", label: "Assorted Flavours (AF)", value: 18 },
  { code: "CC", label: "Contraceptive (CC)", value: 13 },
  { code: "WR", label: "Waste Reduction Scheme (WR)", value: 26 },
];

const PRESCRIBER_ENDORSE_TYPE_IDS = new Set([9, 10, 13, 16, 17, 18, 19, 20, 26]);

const isPrescriberEndorsement = (code: string): boolean => {
  const option = ENDORSEMENT_OPTIONS.find(o => o.code === code);
  return option !== undefined && PRESCRIBER_ENDORSE_TYPE_IDS.has(option.value);
};
// Builds one PrescriptionItemAdditionalEndorsementRequestModel. Values come from
// the endorsement record on the prescription item (get-prescription-by-incoming),
// then the fields captured for that code in the modal, then the dispensed item.
// Anything the endorsement does not carry is posted as null.
const toEndorsementRequest = (e: { code: string; extraInfo?: Record<string, string>; api?: any }, item?: PrescriptionItem) => {
  const src = e.api ?? {};
  const extra = e.extraInfo ?? {};
  const product = item?.selProductIdx != null ? item.products?.[item.selProductIdx] : undefined;

  // Split the captured fields into the request properties they map to and the
  // free-text ones that make up Details.
  const fromForm: Record<string, string> = {};
  const formDetails: string[] = [];
  (ENDORSEMENT_EXTRA_FIELDS[e.code] ?? []).forEach(f => {
    const v = String(extra[f.key] ?? "").trim();
    if (!v) return;
    if (f.api === "details") formDetails.unshift(v);
    else if (f.api) fromForm[f.api] = v;
    else formDetails.push(`${f.label}: ${v}`);
  });

  const fromItem: Record<string, any> = {
    quantity: item?.quantity,
    packagedDoses: item?.packagedDoses,
    packSize: product?.packSize ?? item?.selectedPackSize,
    brand: product?.brandName ?? item?.dispensedManufacturer,
    price: product?.tradePrice != null ? product.tradePrice.toFixed(2) : undefined,
  };
  const pick = (...keys: string[]): string | null => {
    for (const k of keys) {
      const v = src[k] ?? fromForm[k] ?? extra[k] ?? fromItem[k];
      if (v !== null && v !== undefined && String(v).trim() !== "") return String(v).trim();
    }
    return null;
  };
  const dec = (v: string | null) => { const n = Number(v); return v !== null && Number.isFinite(n) ? n : null; };
  const int = (v: string | null) => { const n = parseInt(v ?? "", 10); return Number.isFinite(n) ? n : null; };
  const date = (v: string | null) => {
    if (!v || String(v).startsWith("0001")) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const srcDetails = String(src.details ?? "").trim();
  const details = [srcDetails, ...formDetails].filter(Boolean).join("; ");

  return {
    additionEndorseType: ENDORSEMENT_OPTIONS.find(o => o.code === e.code)?.value ?? 0,
    value: dec(pick("value")),
    quantity: int(pick("quantity")),
    vpid: dec(pick("vpId", "vPId", "vpid")),
    expiryDate: date(pick("expiryDate")),
    mhranumber: pick("mhraNumber", "mHRANumber", "mhranumber"),
    batchNumber: pick("batchNumber"),
    details: details || null,
    price: pick("price"),
    supplier: pick("supplier"),
    packSize: pick("packSize"),
    dispenseDate: date(pick("dispenseDate", "dispensedDate")),
    brand: pick("brand", "manufacturer"),
    packagedDoses: int(pick("packagedDoses")),
  };
};
function PrescriptionDetailModal({ dark, detail, pharmacyId, onClose, onItemEndorsementChange, onExemptionChange, onExemptionSaved, onClaimSaved, onClaimAmended, onClear, onWarn, onNotify }: {
  dark: boolean;
  detail: PrescriptionDetail;
  pharmacyId: string;
  onClose: () => void;
  onItemEndorsementChange: (itemId: string, endorsements: PrescriptionItem["endorsements"]) => void;
  onExemptionChange: (update: Partial<Pick<PrescriptionDetail, "exemption" | "exemptionCode" | "exemptionCertNumber" | "exemptionCertExpiry" | "exemptionEvidenceSeen" | "chargeStatus" | "lastExemptionCheck">>) => void;
  onExemptionSaved?: (exemptionStatusType: number) => void;
  onClaimSaved?: (prescriptionId: string, prescriptionIncomingId?: string) => Promise<boolean>;
  onClaimAmended?: () => void;
  onClear?: () => Promise<void>;
  onWarn: (title: string, message: string) => void;
  onNotify: (title: string, message: string) => void;
}) {
  const [items, setItems] = React.useState<PrescriptionItem[]>(detail.items);
  const [clearConfirmOpen, setClearConfirmOpen] = React.useState(false);
  const [roundUpPrompt, setRoundUpPrompt] = React.useState<{ itemId: string; packSize: number; packCount: number; dispensedQty: number; unit: string } | null>(null);
  const [clearingRx, setClearingRx] = React.useState(false);
  const [focusedItemId, setFocusedItemId] = React.useState<string>(focusItemIdFor(detail.items, detail.focusDrug));
  const [addEndorsementForItem, setAddEndorsementForItem] = React.useState<string | null>(null);
  const [pendingEndorsement, setPendingEndorsement] = React.useState<{ itemId: string; code: string; label: string } | null>(null);
  const [extraFormData, setExtraFormData] = React.useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = React.useState<"items" | "prescription">("items");
  const [expandedEndorsements, setExpandedEndorsements] = React.useState<Set<string>>(new Set());
  const [editingExemption, setEditingExemption] = React.useState(false);
  const [exDraft, setExDraft] = React.useState<{ code: string; certNumber: string; certExpiry: string; evidenceSeen: boolean }>({ code: "", certNumber: "", certExpiry: "", evidenceSeen: false });
  const [originalExemptionCode, setOriginalExemptionCode] = React.useState("");
  const [savingExemption, setSavingExemption] = React.useState(false);
  const [exemptionRecordId, setExemptionRecordId] = React.useState<string>("");
  const [serverMissing, setServerMissing] = React.useState(false);
  const [serverStatusLabel, setServerStatusLabel] = React.useState<string | null>(null);
  const [loadingExemption, setLoadingExemption] = React.useState(false);
  const [rtecChecking, setRtecChecking] = React.useState(false);
  const [rtecMessage, setRtecMessage] = React.useState<{ text: string; ok: boolean } | null>(null);
  const [barcodeCopied, setBarcodeCopied] = React.useState(false);
  // A manual prescription has no EPS barcode, so the reference stands in for it.
  const isManualPrescription = detail.prescriptionType === "Manual";
  const barcodeReference = ((isManualPrescription ? detail.prescriptionId ?? detail.id : detail.epsBarcode) ?? "").trim() || "—";
  const copyBarcodeReference = async () => {
    if (barcodeReference === "—") return;
    try {
      await navigator.clipboard.writeText(barcodeReference);
      setBarcodeCopied(true);
      window.setTimeout(() => setBarcodeCopied(false), 1500);
    } catch {
      onWarn("Couldn't copy", "The clipboard isn't available in this browser. Select the reference and copy it manually.");
    }
  };
  const [claimingRx, setClaimingRx] = React.useState(false);
  const [replacementSent, setReplacementSent] = React.useState(false);
  const [endorsementsSent, setEndorsementsSent] = React.useState(false);
  const warn = (title: string, message: string) => onWarn(title, message);
  const notifySaved = (title: string, message: string) => onNotify(title, message);
  const [savedEndorsementCodes, setSavedEndorsementCodes] = React.useState<Record<string, string[]>>({});
  const itemInfoLoading = !!detail.isItemInfoLoading;
  const concessionLoading = !!detail.isConcessionLoading;
  const skeletonTone = dark ? "bg-gray-700/60" : "bg-gray-200";

  React.useEffect(() => {
    if (detail.isItemInfoLoading !== false) return;
    setItems(prev => prev.map(it => {
      const fresh = detail.items.find(d => d.id === it.id);
      return fresh ? { ...fresh, endorsements: it.endorsements, concession: it.concession ?? fresh.concession } : it;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.isItemInfoLoading]);

  React.useEffect(() => {
    if (detail.isConcessionLoading !== false) return;
    setItems(prev => prev.map(it => ({ ...it, concession: detail.items.find(d => d.id === it.id)?.concession })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.isConcessionLoading]);

  // Prescription items load asynchronously (get-prescription-by-incoming), so
  // resync the local item list whenever the modal target or item count changes.
  React.useEffect(() => {
    setItems(detail.items);
    setReplacementSent(false);
    setEndorsementsSent(false);
    setSavedEndorsementCodes(Object.fromEntries(detail.items.map(i => [i.id, i.endorsements.map(e => e.code)])));
    setFocusedItemId(prev => (detail.items.some(i => i.id === prev) ? prev : focusItemIdFor(detail.items, detail.focusDrug)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id, detail.items.length]);

  const applyExemptionRecord = (record: any): void => {
    const id = record?.prescriptionExemptionId;
    if (!isRealGuid(id)) {
      setExemptionRecordId("");
      setServerStatusLabel(null);
      setServerMissing(true);
      return;
    }
    setExemptionRecordId(id);

    const statusType = Number(record.exemptionStatusType ?? 0);
    const cat = EXEMPTION_CATEGORIES.find(c => c.code === EXEMPTION_CODE_BY_STATUS_TYPE[statusType]);
    const label = cat?.badge ?? EXEMPTION_STATUS_LABELS[statusType] ?? null;
    setServerStatusLabel(cat ? null : label);
    setServerMissing(!label);
    if (!label) return;

    const expiry = record.expiryDate ? String(record.expiryDate).slice(0, 10) : undefined;
    onExemptionChange({
      exemption: label,
      exemptionCode: cat?.code,
      exemptionCertNumber: record.certificateNumber || undefined,
      exemptionCertExpiry: expiry,
      exemptionEvidenceSeen: record.evidenceSeen ?? false,
      chargeStatus: cat?.paysCharge || CHARGE_PAID_STATUS_TYPES.includes(statusType) ? "Charge Paid" : "Exempt from Charge",
    });
  };

  // The exemption model arrives with get-prescription-by-incoming, so map it
  // straight from there rather than fetching it separately.
  React.useEffect(() => {
    if (detail.isLoading) {
      setExemptionRecordId("");
      setServerStatusLabel(null);
      setServerMissing(false);
      setLoadingExemption(true);
      return;
    }
    setLoadingExemption(false);
    // undefined means no payload carried an exemption field (e.g. the blocked
    // claims modal), so leave the detail's own exemption in place; null means
    // the payload came back without one.
    if (detail.exemptionRecord === undefined) return;
    applyExemptionRecord(detail.exemptionRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id, detail.isLoading, detail.exemptionRecord]);

  // A prescription can only be claimed once an exemption is on record.
  const exemptionCategory = serverMissing ? undefined : findExemptionCategory(detail);
  const unmappedExemptionLabel = !exemptionCategory && !serverMissing ? serverStatusLabel : null;
  const exemptionSet = !!exemptionCategory || !!unmappedExemptionLabel;

  const runRtecCheck = async () => {
    if (rtecChecking) return;
    if (!pharmacyId || !isRealGuid(detail.prescriptionIncomingId)) {
      warn("RTEC check unavailable", "This prescription can't be checked — no prescription reference is available.");
      return;
    }
    setRtecChecking(true);
    setRtecMessage(null);
    const result: any = await bulkRtec(pharmacyId, [detail.prescriptionIncomingId]);
    if (!result) {
      setRtecChecking(false);
      warn("RTEC check didn't complete", "The Real Time Exemption Check couldn't be run against the NHS Spine. Please try again.");
      return;
    }
    setRtecMessage({ text: result?.message || "RTEC check complete.", ok: true });
    let statusType = 0;
    if (isRealGuid(detail.prescriptionId)) {
      const record = await getExemption(pharmacyId, detail.prescriptionId);
      applyExemptionRecord(record);
      if (isRealGuid(record?.prescriptionExemptionId)) statusType = Number(record.exemptionStatusType ?? 0);
    }
    setRtecChecking(false);
    onExemptionSaved?.(statusType);
  };

  const startEditExemption = () => {
    const current = serverMissing ? undefined : findExemptionCategory(detail);
    setExDraft({
      code: current?.code ?? "",
      certNumber: serverMissing ? "" : detail.exemptionCertNumber ?? "",
      certExpiry: serverMissing ? "" : detail.exemptionCertExpiry ?? "",
      evidenceSeen: serverMissing ? false : detail.exemptionEvidenceSeen ?? false,
    });
    setOriginalExemptionCode(current?.code ?? "");
    setEditingExemption(true);
  };

  const draftCategory = EXEMPTION_CATEGORIES.find(c => c.code === exDraft.code);
  const draftCertValid = !draftCategory?.requiresCertificate || (exDraft.certNumber.trim() && exDraft.certExpiry.trim());
  const originalExemptionCategory = EXEMPTION_CATEGORIES.find(c => c.code === originalExemptionCode);
  const exemptionChangedFromLastKnown = !!originalExemptionCode && !!exDraft.code && exDraft.code !== originalExemptionCode;

  const saveExemption = async () => {
    const cat = EXEMPTION_CATEGORIES.find(c => c.code === exDraft.code);
    if (!cat || !draftCertValid || savingExemption) return;
    if (!detail.prescriptionId || !pharmacyId) {
      warn("Couldn't update the exemption", "This prescription can't be updated — no prescription reference is available.");
      return;
    }
    const certNumber = cat.requiresCertificate ? exDraft.certNumber.trim() : "";
    const certExpiry = cat.requiresCertificate ? exDraft.certExpiry.trim() : "";
    const evidenceSeen = cat.paysCharge || cat.noEvidence ? false : exDraft.evidenceSeen;

    setSavingExemption(true);
    const saved = await addUpdateExemption(pharmacyId, {
      prescriptionExemptionId: exemptionRecordId || EMPTY_GUID,
      prescriptionId: detail.prescriptionId,
      exemptionStatusType: EXEMPTION_STATUS_TYPES[cat.code] ?? 0,
      expiryDate: certExpiry ? new Date(certExpiry).toISOString() : null,
      certificateNumber: certNumber || null,
      name: "",
      dateOfBirth: detail.dobIso ?? null,
      evidenceSeen,
      signatureImage: null,
    });
    setSavingExemption(false);

    if (!saved) {
      warn("Couldn't save the exemption", "The exemption couldn't be saved. Please try again.");
      return;
    }

    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    onExemptionChange({
      exemption: cat.badge,
      exemptionCode: cat.code,
      exemptionCertNumber: certNumber || undefined,
      exemptionCertExpiry: certExpiry || undefined,
      exemptionEvidenceSeen: evidenceSeen,
      chargeStatus: cat.paysCharge ? "Charge Paid" : "Exempt from Charge",
      lastExemptionCheck: today,
    });
    setServerMissing(false);
    setServerStatusLabel(null);
    setEditingExemption(false);
    if (!exemptionRecordId) {
      const record = await getExemption(pharmacyId, detail.prescriptionId);
      const newId = record?.prescriptionExemptionId;
      if (isRealGuid(newId)) setExemptionRecordId(newId);
    }
    onExemptionSaved?.(EXEMPTION_STATUS_TYPES[cat.code] ?? 0);
  };

  const toggleEndorsementExpand = (itemId: string, code: string) => {
    const key = `${itemId}:${code}`;
    setExpandedEndorsements(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const getItemEndorsementInfo = (item: PrescriptionItem) => {
    const existingCodes = new Set(item.endorsements.map(e => e.code));
    const requiredCodes = new Set(item.requiredEndorsements.map(e => e.code));
    const missing = item.requiredEndorsements.filter(r => !existingCodes.has(r.code));
    return { existingCodes, requiredCodes, missing };
  };

  const updateItemEndorsements = (itemId: string, updated: PrescriptionItem["endorsements"]) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, endorsements: updated } : it));
    onItemEndorsementChange(itemId, updated);
  };

  const updateItemBrand = (itemId: string, brandId: string) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, selectedBrandId: brandId } : it));
  };

  const updateItemPackSize = (itemId: string, packSize: number) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, selectedPackSize: packSize } : it));
  };

  // Pick a product/pack candidate to claim; this drives Claim DT + Net Reimb.
  const updateItemProductIdx = (itemId: string, productIdx: number | undefined) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, selProductIdx: productIdx, roundUpPacks: false } : it));
  };

  const setItemRoundUpPacks = (itemId: string, roundUpPacks: boolean) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, roundUpPacks } : it));
  };

  const promptRoundUp = (itemId: string, option: PackPriceOption | undefined, dispensedQty: number, unit: string) => {
    if (!option || !isNonSplittableUnit(unit) || !(option.packCount > 0) || isWholePackCount(option.packCount)) return;
    setRoundUpPrompt({ itemId, packSize: option.packSize, packCount: option.packCount, dispensedQty, unit });
  };

  const selectPackProduct = (itemId: string, productIdx: number, options: PackPriceOption[], dispensedQty: number, unit: string) => {
    updateItemProductIdx(itemId, productIdx);
    promptRoundUp(itemId, options.find(o => o.productIdx === productIdx || o.brands.some(b => b.productIdx === productIdx)), dispensedQty, unit);
  };

  const selectedProductFor = (it: PrescriptionItem) =>
    (it.selProductIdx != null ? it.products?.[it.selProductIdx] : undefined);
  const selectedProductIdFor = (it: PrescriptionItem) => selectedProductFor(it)?.productId;
  const selectedIsNoBrand = (it: PrescriptionItem) => isNoBrandProduct(selectedProductFor(it));
  const itemProductChanged = (it: PrescriptionItem) => {
    const picked = selectedProductIdFor(it);
    if (!isRealGuid(picked)) return false;
    const baselineId = isRealGuid(it.savedProductId) ? it.savedProductId : it.endorsedProductId;
    if (picked === baselineId) return false;
    if (selectedIsNoBrand(it)) {
      const baseline = (it.products ?? []).find(p => p.productId === baselineId);
      return !isNoBrandProduct(baseline);
    }
    return true;
  };
  const anyProductChanged = items.some(itemProductChanged);

  const sendReplacementNotification = async () => {
    const replacementItems = changedItemsPayload();
    if (replacementItems.length === 0) {
      warn("Can't notify the replacement", "No dispense reference is available for the changed items, so the replacement dispense notification can't be sent.");
      return false;
    }
    const result: any = await replacementDispenseNotification(pharmacyId, { items: replacementItems });
    const succeeded = result?.success ?? result?.Success ?? false;
    if (!succeeded) {
      warn("Replacement not notified", result?.message || result?.Message || "The replacement dispense notification couldn't be sent. Please try again.");
      return false;
    }
    setReplacementSent(true);
    return true;
  };

  const changedItemsPayload = () =>
    items
      .filter(itemProductChanged)
      .map(it => {
        const isNoBrand = selectedIsNoBrand(it);
        return {
          dispenseItemId: it.dispenseItemId,
          newProductId: selectedProductIdFor(it),
          isNoBrand,
        };
      })
      .filter(i => isRealGuid(i.dispenseItemId) && isRealGuid(i.newProductId));

  const amendClaim = async () => {
    const amendmentItems = changedItemsPayload();
    if (amendmentItems.length === 0) {
      warn("Can't amend this claim", "No dispense reference is available for the changed items, so the amendment can't be sent.");
      return false;
    }
    const result: any = await claimAmendment(pharmacyId, { items: amendmentItems });
    const succeeded = result?.success ?? result?.Success ?? false;
    if (!succeeded) {
      warn("Claim not amended", result?.message || result?.Message || "This claim couldn't be amended. Please try again.");
      return false;
    }
    return true;
  };

  // add-endorsement replaces the endorsements of every item it is given, so the
  // payload carries all items with their full current endorsement list.
  const endorsementsPayload = () =>
    items
      .filter(it => isRealGuid(it.prescriptionItemId))
      .map(it => ({
        prescriptionItemId: it.prescriptionItemId,
        notDispensedReasonCode: it.notDispensedReasonCode ?? "",
        itemCrossedOut: it.itemCrossedOut ?? false,
        additionalEndorsements: it.endorsements.map(e => toEndorsementRequest(e, it)),
      }));

  const claimItemsPayload = () =>
    items.map(it => {
      const product = it.selProductIdx != null ? it.products?.[it.selProductIdx] : undefined;
      const dt = parsePrice(it.claimDtPrice);
      return {
        prescriptionItemId: isRealGuid(it.prescriptionItemId) ? it.prescriptionItemId : "",
        prescribedItemId: isRealGuid(it.prescribedItemId) ? it.prescribedItemId : "",
        dispenseItemId: isRealGuid(it.dispenseItemId) ? it.dispenseItemId : "",
        productId: product?.productId ?? (isRealGuid(it.endorsedProductId) ? it.endorsedProductId : ""),
        drug: it.drug ?? "",
        brandName: product?.brandName ?? "",
        pack: it.pack ?? "",
        quantity: it.quantity ?? "",
        dosage: it.dosage ?? "",
        packSize: product?.packSize ?? null,
        drugTariffPrice: Number.isFinite(dt) ? dt : null,
        tradePrice: product?.tradePrice ?? null,
        reimbursementPrice: product?.reimbursement ?? null,
        notDispensedReasonCode: it.notDispensedReasonCode ?? "",
        itemCrossedOut: it.itemCrossedOut ?? false,
        packagedDoses: it.packagedDoses ?? null,
        endorsements: it.endorsements.map(e => ({
          ...toEndorsementRequest(e, it),
          code: e.code,
          label: e.label,
          pharmacistInitials: e.extraInfo?.pharmacistInitials ?? "",
        })),
      };
    });

  const persistClaimItems = async () => {
    if (!pharmacyId || !isRealGuid(detail.prescriptionId)) return;
    const payload = claimItemsPayload();
    const saved = await saveClaimPrescriptionItems(pharmacyId, {
      prescriptionId: detail.prescriptionId,
      prescriptionIncomingId: isRealGuid(detail.prescriptionIncomingId) ? detail.prescriptionIncomingId : null,
      items: payload,
    });
    if (!saved) return;
  };

  const endorsementsChanged = () =>
    items.some(it => {
      const saved = savedEndorsementCodes[it.id] ?? [];
      const current = it.endorsements.map(e => e.code);
      return current.length !== saved.length || current.some((c, i) => c !== saved[i]);
    });

  const sendEndorsements = async () => {
    if (endorsementsSent || !endorsementsChanged()) return true;
    const endorsementItems = endorsementsPayload();
    if (endorsementItems.length === 0) return true;
    const result: any = await addEndorsement(pharmacyId, { items: endorsementItems });
    const succeeded = result?.success ?? result?.Success ?? false;
    if (!succeeded) {
      warn("Endorsements not saved", result?.message || result?.Message || "The endorsements couldn't be saved. Please try again.");
      return false;
    }
    setEndorsementsSent(true);
    setSavedEndorsementCodes(Object.fromEntries(items.map(i => [i.id, i.endorsements.map(e => e.code)])));
    return true;
  };

  // On the To Be Claimed tab, Save commits the endorsements / replacement
  // notification and leaves the modal open; only Save & Claim closes it, once
  // save-claims has succeeded. On the Claimed tab a successful Save closes it.
  const save = async () => {
    if (claimingRx) return;
    const hasNewEndorsements = !endorsementsSent && endorsementsChanged() && endorsementsPayload().length > 0;
    const productPending = anyProductChanged && !(!detail.isClaimed && replacementSent);
    if (!hasNewEndorsements && !productPending) return;
    if (!pharmacyId) {
      warn("Can't save this prescription", "No pharmacy reference is available. Please reopen the prescription and try again.");
      return;
    }
    setClaimingRx(true);
    if (hasNewEndorsements && !(await sendEndorsements())) {
      setClaimingRx(false);
      return;
    }
    if (!productPending) {
      setClaimingRx(false);
      notifySaved("Changes saved", "The endorsements on this prescription have been saved.");
      if (detail.isClaimed) onClose();
      return;
    }
    if (detail.isClaimed) {
      const amended = await amendClaim();
      if (amended) await persistClaimItems();
      setClaimingRx(false);
      if (amended) {
        notifySaved("Changes saved", hasNewEndorsements
          ? "The endorsements and the amended claim have been saved."
          : "The amended claim has been saved.");
        onClaimAmended?.();
        onClose();
      }
      return;
    }
    if (await sendReplacementNotification()) {
      await persistClaimItems();
      notifySaved("Changes saved", hasNewEndorsements
        ? "The endorsements and the replacement dispense notification have been saved."
        : "The replacement dispense notification has been sent.");
    }
    setClaimingRx(false);
  };

  const saveAndClaim = async () => {
    if (claimingRx) return;
    if (!exemptionSet) {
      warn("Claim failed", `Cannot claim ${detail.patient}: patient exemption is unconfirmed. Set the patient's exemption status before claiming.`);
      return;
    }
    if (!pharmacyId || !isRealGuid(detail.prescriptionId)) {
      warn("Can't claim this prescription", "No prescription reference is available. Please reopen the prescription and try again.");
      return;
    }
    setClaimingRx(true);
    if (!(await sendEndorsements())) {
      setClaimingRx(false);
      return;
    }
    if (anyProductChanged && !replacementSent) {
      const notified = await sendReplacementNotification();
      if (!notified) {
        setClaimingRx(false);
        return;
      }
      await persistClaimItems();
    }
    const saved = await onClaimSaved?.(detail.id, detail.prescriptionIncomingId);
    setClaimingRx(false);
    if (!saved) {
      return;
    }
    onClose();
  };

  const initiateAdd = (itemId: string, code: string, label: string) => {
    if (ENDORSEMENT_EXTRA_FIELDS[code]?.length) {
      setPendingEndorsement({ itemId, code, label });
      setExtraFormData({});
      setAddEndorsementForItem(null);
    } else {
      commitAdd(itemId, code, label, {});
    }
  };

  const commitAdd = (itemId: string, code: string, label: string, extraInfo: Record<string, string>) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const entry = { code, label, appliedDate: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }), extraInfo: Object.keys(extraInfo).length > 0 ? extraInfo : undefined };
    updateItemEndorsements(itemId, [...item.endorsements, entry]);
    setAddEndorsementForItem(null);
    setPendingEndorsement(null);
    setExtraFormData({});
  };

  const confirmPending = () => {
    if (!pendingEndorsement) return;
    const fields = ENDORSEMENT_EXTRA_FIELDS[pendingEndorsement.code] || [];
    if (!fields.filter(f => f.required).every(f => (extraFormData[f.key] || "").trim())) return;
    commitAdd(pendingEndorsement.itemId, pendingEndorsement.code, pendingEndorsement.label, extraFormData);
  };

  const removeEndorsement = (itemId: string, code: string) => {
    if (isPrescriberEndorsement(code)) return;
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    updateItemEndorsements(itemId, item.endorsements.filter(e => e.code !== code));
  };

  const applyAllRequired = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const { missing } = getItemEndorsementInfo(item);
    const needsForm = missing.find(r => ENDORSEMENT_EXTRA_FIELDS[r.code]?.length);
    if (needsForm) { initiateAdd(itemId, needsForm.code, needsForm.label); return; }
    const newOnes = missing.map(r => ({ code: r.code, label: r.label, appliedDate: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) }));
    updateItemEndorsements(itemId, [...item.endorsements, ...newOnes]);
  };

  const exemptionStyle: Record<string, string> = {
    "Exempt":            dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700",
    "Not Exempt":        dark ? "bg-red-500/15 text-red-400"       : "bg-red-100 text-red-700",
    "Evidence Not Seen": dark ? "bg-amber-500/15 text-amber-400"   : "bg-amber-100 text-amber-700",
    "Maternity":         dark ? "bg-purple-500/15 text-purple-400" : "bg-purple-100 text-purple-700",
    "Medical":           dark ? "bg-blue-500/15 text-blue-400"     : "bg-blue-100 text-blue-700",
    "Prepayment":        dark ? "bg-cyan-500/15 text-cyan-400"     : "bg-cyan-100 text-cyan-700",
    "Missing":           dark ? "bg-red-500/15 text-red-400"       : "bg-red-100 text-red-700",
  };

  const totalEndorsements = items.reduce((sum, it) => sum + it.endorsements.length, 0);
  const totalMissing = items.reduce((sum, it) => {
    const existing = new Set(it.endorsements.map(e => e.code));
    return sum + it.requiredEndorsements.filter(r => !existing.has(r.code)).length;
  }, 0);

  const sectionCard = dark ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-white";
  const sectionLabel = `text-[10px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`;
  const sectionIcon = `w-3.5 h-3.5 ${dark ? "text-gray-400" : "text-gray-500"}`;
  const infoRow = (label: string, value: React.ReactNode) => (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={`shrink-0 text-[11px] ${dark ? "text-gray-400" : "text-gray-500"}`}>{label}</dt>
      <dd className={`min-w-0 text-right text-xs font-medium ${dark ? "text-gray-100" : "text-gray-900"}`}>{value}</dd>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        // font-sans pins the modal to Tailwind's stack. src/index.css sets a
        // different family on `body`, and this modal is portalled onto body, so
        // without it the text renders in Segoe UI rather than the app's own face.
        className={`font-sans relative w-full max-w-7xl h-[calc(100vh-1.5rem)] sm:h-[calc(100vh-3rem)] max-h-[88vh] flex flex-col overflow-hidden rounded-2xl border shadow-2xl ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Loading overlay while prescription + per-item info load */}
        {detail.isLoading && (
          <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-2xl ${dark ? "bg-gray-900/70" : "bg-white/70"} backdrop-blur-sm`}>
            <Loader2 className={`w-6 h-6 animate-spin ${dark ? "text-gray-300" : "text-gray-500"}`} />
            <span className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-600"}`}>Loading prescription…</span>
          </div>
        )}
        {claimingRx && (
          <div className={`absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-2xl ${dark ? "bg-gray-900/70" : "bg-white/70"} backdrop-blur-sm`}>
            <Loader2 className={`w-6 h-6 animate-spin ${dark ? "text-gray-300" : "text-gray-500"}`} />
            <span className={`text-xs font-medium ${dark ? "text-gray-300" : "text-gray-600"}`}>Saving claim…</span>
          </div>
        )}
        {/* Header */}
        <div className={`shrink-0 px-6 py-3 border-b flex items-center justify-between ${dark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}>
          <h3 className={`text-base font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Prescription Details</h3>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors cursor-pointer ${dark ? "text-gray-400 hover:bg-gray-800" : "text-gray-400 hover:bg-gray-100"}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex">
          {/* Left column: patient, prescription and exemption */}
          <aside className={`w-1/4 min-w-[280px] shrink-0 overflow-y-auto border-r p-4 space-y-3 ${dark ? "border-gray-800 bg-gray-950/40" : "border-gray-200 bg-gray-50"}`}>
            <section className={`rounded-xl border p-3 ${sectionCard}`}>
              <div className="flex items-center gap-1.5">
                <Users className={sectionIcon} />
                <p className={sectionLabel}>Patient</p>
              </div>
              <p className={`text-sm font-semibold mt-1.5 ${dark ? "text-white" : "text-gray-900"}`}>{detail.patient}</p>
              <dl className="mt-2 space-y-1.5">
                {infoRow("Date of birth", detail.dob && detail.dob !== "—" ? detail.dob : "—")}
                {infoRow("NHS Number", detail.nhsNumber && detail.nhsNumber !== "—" ? detail.nhsNumber : "—")}
              </dl>
            </section>

            <section className={`rounded-xl border p-3 ${sectionCard}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <FileText className={sectionIcon} />
                  <p className={sectionLabel}>Prescription</p>
                </div>
                {detail.nominatedPharmacy && (
                  <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-700"}`}>
                    <Building2 className="w-2.5 h-2.5" /> Nominated
                  </span>
                )}
              </div>
              <dl className="mt-2 space-y-1.5">
                {infoRow(isManualPrescription ? "Reference" : "EPS Barcode", (
                  <span className="inline-flex items-center gap-1">
                    <span className="break-all">{barcodeReference}</span>
                    {barcodeReference !== "—" && (
                      <button
                        type="button"
                        onClick={copyBarcodeReference}
                        title={barcodeCopied ? "Copied" : `Copy ${isManualPrescription ? "prescription reference" : "EPS barcode"}`}
                        aria-label={`Copy ${isManualPrescription ? "prescription reference" : "EPS barcode"}`}
                        className={`shrink-0 rounded p-0.5 cursor-pointer transition-colors ${barcodeCopied ? dark ? "text-emerald-400" : "text-emerald-600" : dark ? "text-blue-300 hover:bg-gray-800" : "text-blue-600 hover:bg-blue-50"}`}
                      >
                        {barcodeCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </button>
                    )}
                  </span>
                ))}
                {infoRow("Rx Date", detail.rxDate || "—")}
                {detail.dispensedDate && detail.dispensedDate !== detail.rxDate && infoRow("Dispensed", detail.dispensedDate)}
                {infoRow("Form / Type", [detail.formType, detail.treatmentType].filter(Boolean).join(" · ") || "—")}
              </dl>
            </section>

          {/* Exemption panel */}
          {(() => {
            const exCat = exemptionCategory;
            const unmappedLabel = unmappedExemptionLabel;
            const badgeText = exCat?.badge ?? unmappedLabel ?? "";
            const panelTone = editingExemption
              ? (dark ? "border-blue-500/40 bg-blue-500/5" : "border-blue-300 bg-blue-50/60")
              : exemptionSet
                ? (dark ? "border-gray-800 bg-gray-800/30" : "border-gray-200 bg-gray-50")
                : (dark ? "border-amber-500/40 bg-amber-500/10" : "border-amber-300 bg-amber-50");
            const badgeStyle = exCat?.paysCharge
              ? (dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700")
              : (dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700");
            return (
              <section className={`rounded-xl border p-3 ${panelTone}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Stamp className={`w-3.5 h-3.5 ${dark ? "text-gray-400" : "text-gray-500"}`} />
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Prescription Exemption</p>
                </div>

                {/* RTEC check result */}
                {rtecMessage && (
                  <div className={`flex items-center justify-between gap-3 rounded-lg border p-2.5 mb-2 ${rtecMessage.ok ? (dark ? "border-emerald-500/30 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50") : (dark ? "border-red-500/30 bg-red-500/10" : "border-red-200 bg-red-50")}`}>
                    <div className="flex items-center gap-2">
                      {rtecMessage.ok ? <ShieldCheck className={`w-4 h-4 shrink-0 ${dark ? "text-emerald-400" : "text-emerald-600"}`} /> : <ShieldAlert className={`w-4 h-4 shrink-0 ${dark ? "text-red-400" : "text-red-600"}`} />}
                      <div>
                        <p className={`text-xs font-semibold ${rtecMessage.ok ? (dark ? "text-emerald-300" : "text-emerald-700") : (dark ? "text-red-300" : "text-red-700")}`}>{rtecMessage.text}</p>
                        <p className={`text-[10px] ${dark ? "text-gray-400" : "text-gray-600"}`}>Real Time Exemption Checking against the NHS Spine.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setRtecMessage(null)}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-colors cursor-pointer shrink-0 ${dark ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-200"}`}
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* Read-only view */}
                {!editingExemption && loadingExemption ? (
                  <div className={`flex items-center gap-2 text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading exemption…
                  </div>
                ) : !editingExemption && (exemptionSet ? (
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badgeStyle}`}>{badgeText}</span>
                      {detail.chargeStatus && (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          detail.chargeStatus.includes("Exempt") ? dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"
                            : dark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"
                        }`}>{detail.chargeStatus}</span>
                      )}
                      {detail.exemptionEvidenceSeen ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                          <Check className="w-2.5 h-2.5" /> Evidence seen
                        </span>
                      ) : !exCat?.paysCharge && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                          <CircleAlert className="w-2.5 h-2.5" /> Evidence not seen
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-2 ${dark ? "text-gray-300" : "text-gray-700"}`}>{exCat?.label ?? unmappedLabel}</p>
                    {(detail.exemptionCertNumber || detail.exemptionCertExpiry || (detail.lastExemptionCheck && detail.lastExemptionCheck !== "—")) && (
                      <div className={`flex items-center gap-4 mt-1.5 text-[10px] ${dark ? "text-gray-400" : "text-gray-500"}`}>
                        {detail.exemptionCertNumber && <span>Cert no: <span className="font-medium">{detail.exemptionCertNumber}</span></span>}
                        {detail.exemptionCertExpiry && <span>Expires: <span className="font-medium">{detail.exemptionCertExpiry}</span></span>}
                        {detail.lastExemptionCheck && detail.lastExemptionCheck !== "—" && <span>Checked: <span className="font-medium">{detail.lastExemptionCheck}</span></span>}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <CircleAlert className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-amber-400" : "text-amber-600"}`} />
                    <div>
                      <p className={`text-xs font-semibold ${dark ? "text-amber-300" : "text-amber-700"}`}>No exemption set</p>
                      <p className={`text-[11px] mt-0.5 ${dark ? "text-amber-400/80" : "text-amber-600"}`}>
                        {detail.exemption && detail.exemption !== "—" ? `Current status: ${detail.exemption}. ` : ""}Select an exemption category or mark the patient as paying the charge.
                      </p>
                    </div>
                  </div>
                ))}

                {/* Actions sit under the panel content so they get the full
                    card width instead of competing with the heading. */}
                {!editingExemption && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <button
                      onClick={runRtecCheck}
                      disabled={rtecChecking}
                      className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] font-medium whitespace-nowrap transition-colors ${rtecChecking ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border-gray-700" : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"}`}
                    >
                      {rtecChecking ? <Loader2 className="w-3 h-3 shrink-0 animate-spin" /> : <ShieldCheck className="w-3 h-3 shrink-0" />}
                      {rtecChecking ? "Checking Spine…" : "Run RTEC Check"}
                    </button>
                    <button
                      onClick={startEditExemption}
                      disabled={loadingExemption}
                      className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] font-medium whitespace-nowrap transition-colors ${loadingExemption ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${
                        exemptionSet
                          ? dark ? "bg-gray-800 text-blue-400 hover:bg-gray-700 border-gray-700" : "bg-white text-blue-600 hover:bg-blue-50 border-gray-300"
                          : "bg-amber-500 text-white hover:bg-amber-400 border-amber-500"
                      }`}
                    >
                      <Pencil className="w-3 h-3 shrink-0" /> {exemptionSet ? "Change" : "Set exemption"}
                    </button>
                  </div>
                )}

                {/* Editor */}
                {editingExemption && (
                  <div>
                    <select
                      value={exDraft.code}
                      onChange={e => {
                        const code = e.target.value;
                        const cat = EXEMPTION_CATEGORIES.find(c => c.code === code);
                        // Categories with no evidence to sight never carry a tick,
                        // so drop anything captured against the previous choice.
                        setExDraft(d => ({ ...d, code, evidenceSeen: cat?.noEvidence ? false : d.evidenceSeen }));
                      }}
                      className={`w-full text-xs rounded-lg border px-2.5 py-2 mb-2 cursor-pointer ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                    >
                      <option value="">Select exemption reason…</option>
                      {EXEMPTION_CATEGORIES.map(c => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>

                    {draftCategory?.requiresCertificate && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className={`text-[10px] font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Certificate number</label>
                          <input
                            value={exDraft.certNumber}
                            onChange={e => setExDraft(d => ({ ...d, certNumber: e.target.value }))}
                            placeholder="e.g. MATEX-2026-04821"
                            className={`w-full text-xs rounded-lg border px-2.5 py-2 mt-0.5 ${dark ? "bg-gray-900 border-gray-700 text-white placeholder:text-gray-600" : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"}`}
                          />
                        </div>
                        <div>
                          <label className={`text-[10px] font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Expiry date</label>
                          <input
                            type="date"
                            value={exDraft.certExpiry}
                            onChange={e => setExDraft(d => ({ ...d, certExpiry: e.target.value }))}
                            className={`w-full text-xs rounded-lg border px-2.5 py-2 mt-0.5 ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                          />
                        </div>
                      </div>
                    )}

                    {draftCategory && !draftCategory.paysCharge && !draftCategory.noEvidence && (
                      <label className={`flex items-center gap-2 text-xs mb-2 cursor-pointer ${dark ? "text-gray-300" : "text-gray-700"}`}>
                        <input
                          type="checkbox"
                          checked={exDraft.evidenceSeen}
                          onChange={e => setExDraft(d => ({ ...d, evidenceSeen: e.target.checked }))}
                          className="cursor-pointer"
                        />
                        Evidence of exemption seen
                      </label>
                    )}

                    {exemptionChangedFromLastKnown && (
                      <div className={`flex items-start gap-2 rounded-lg border p-2.5 mb-2 ${dark ? "border-amber-500/30 bg-amber-500/10" : "border-amber-200 bg-amber-50"}`}>
                        <CircleAlert className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-amber-400" : "text-amber-600"}`} />
                        <p className={`text-[11px] ${dark ? "text-amber-300" : "text-amber-700"}`}>
                          This is different to the last known exemption (<span className="font-semibold">{originalExemptionCategory?.badge ?? originalExemptionCode}</span>). Double check this is correct before saving.
                        </p>
                      </div>
                    )}


                    <div className="flex items-center justify-end gap-2 mt-1">
                      <button
                        onClick={() => setEditingExemption(false)}
                        disabled={savingExemption}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${savingExemption ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${dark ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-200"}`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveExemption}
                        disabled={!draftCategory || !draftCertValid || savingExemption}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${(!draftCategory || !draftCertValid || savingExemption) ? "opacity-50 cursor-not-allowed bg-emerald-600/50 text-white" : exemptionChangedFromLastKnown ? "cursor-pointer bg-amber-600 text-white hover:bg-amber-500" : "cursor-pointer bg-emerald-600 text-white hover:bg-emerald-500"}`}
                      >
                        {savingExemption
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                          : <><Check className="w-3.5 h-3.5" /> {exemptionChangedFromLastKnown ? "Confirm & save exemption" : "Save exemption"}</>}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            );
          })()}
          </aside>

          {/* Right column: items strip and the selected item's detail */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className={`shrink-0 px-6 pt-4 pb-3 border-b ${dark ? "border-gray-800" : "border-gray-200"}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <span className={sectionLabel}>Items ({items.length})</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${totalMissing > 0 ? dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700" : dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>
                    {totalMissing > 0 ? <CircleAlert className="w-3 h-3" /> : <CircleCheck className="w-3 h-3" />}
                    {totalMissing > 0 ? `${totalMissing} endorsement${totalMissing !== 1 ? "s" : ""} missing` : "All ready"}
                  </span>
                </div>
                <button
                  onClick={() => setActiveTab("prescription")}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${activeTab === "prescription" ? dark ? "border-blue-500/40 bg-blue-500/15 text-blue-300" : "border-blue-300 bg-blue-50 text-blue-700" : dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                >
                  <Eye className="w-3.5 h-3.5" /> Prescription image
                </button>
              </div>
              <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
                {items.map(navItem => {
                  const navMissing = navItem.requiredEndorsements.filter(r => !navItem.endorsements.some(e => e.code === r.code)).length;
                  const isActive = navItem.id === focusedItemId && activeTab === "items";
                  return (
                    <button
                      key={navItem.id}
                      onClick={() => { setActiveTab("items"); setFocusedItemId(navItem.id); setAddEndorsementForItem(null); setPendingEndorsement(null); }}
                      aria-pressed={isActive}
                      className={`w-full text-left rounded-lg border px-2.5 py-2 transition-colors cursor-pointer ${isActive ? dark ? "border-blue-500/50 bg-blue-500/15 ring-1 ring-blue-500/40" : "border-blue-300 bg-blue-50 ring-1 ring-blue-200" : navMissing > 0 ? dark ? "border-red-500/30 bg-red-500/5 hover:bg-gray-800" : "border-red-200 bg-red-50/60 hover:bg-red-50" : dark ? "border-gray-800 hover:bg-gray-800" : "border-gray-200 hover:bg-gray-50"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span title={claimItemTitle(navItem)} className={`text-xs font-medium truncate ${dark ? "text-gray-100" : "text-gray-800"}`}>{claimItemTitle(navItem)}</span>
                        {navMissing > 0
                          ? <CircleAlert className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-red-400" : "text-red-600"}`} />
                          : <CircleCheck className={`w-3.5 h-3.5 shrink-0 ${dark ? "text-emerald-400" : "text-emerald-600"}`} />}
                      </div>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {navItem.drugTariffCategory != null && (
                          <CategoryTag category={drugTariffCategoryLabel(navItem.drugTariffCategory)} dark={dark} />
                        )}
                        {navItem.concession && (
                          <span title="Active price concession" className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded ${dark ? "bg-amber-500/15 text-amber-300" : "bg-amber-100 text-amber-700"}`}><PoundSterling className="w-2.5 h-2.5" /> On Concession</span>
                        )}
                        {navMissing > 0 && (
                          <span title="Incomplete endorsement" className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}><Stamp className="w-2.5 h-2.5" /> Incomplete Endorsement</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected item detail / prescription image */}
            <div className="flex-1 min-h-0 overflow-y-auto">
        {activeTab === "items" && (
          <div className="px-4 sm:px-6 py-4">
            <div className="space-y-2">
              {items.map(item => {
                if (item.id !== focusedItemId) return null;
                const isFocused = item.id === focusedItemId;
                const { existingCodes, requiredCodes, missing: itemMissing } = getItemEndorsementInfo(item);
                const itemFullyEndorsed = item.requiredEndorsements.length > 0 && itemMissing.length === 0;
                const isAddingForThis = addEndorsementForItem === item.id;
                const isPendingForThis = pendingEndorsement?.itemId === item.id;
                const dispensedQty = parseInt(item.quantity, 10) || 0;
                const claimForm = item.unitText?.trim() || dispensedFormFor(item.drug);
                const concession = item.concession;
                const needsAttention = itemMissing.length > 0;
                const products = item.products ?? [];
                const selProduct = item.selProductIdx != null ? products[item.selProductIdx] : undefined;
                const endorsedProduct = endorsedProductFor(item);
                const itemDt = parsePrice(item.claimDtPrice);
                const bpInfo = itemBetterPackInfo(item);
                const dispensedProductIdx = endorsedProduct ? products.indexOf(endorsedProduct) : -1;
                const packPriceOptions = packPriceOptionsFor(item, dispensedQty);
                const dispensedOption = dispensedProductIdx >= 0
                  ? packPriceOptions.find(o => o.productIdx === dispensedProductIdx)
                  : undefined;
                const exactClaimedOption = (item.selProductIdx != null
                  ? packPriceOptions.find(o => o.productIdx === item.selProductIdx)
                  : undefined)
                  ?? dispensedOption
                  ?? packPriceOptions.find(o => o.inDrugTariff)
                  ?? packPriceOptions[0];
                const canRoundUpPacks = isNonSplittableUnit(claimForm);
                const claimedOption = exactClaimedOption && item.roundUpPacks && canRoundUpPacks
                  ? roundUpPackOption(exactClaimedOption)
                  : exactClaimedOption;
                const tablePackOptions = packPriceOptions.map(o => (o === exactClaimedOption ? claimedOption : o));
                const claimDtNum = claimedOption
                  ? claimedOption.lineDt
                  : Number.isFinite(itemDt)
                    ? itemDt
                    : endorsedProduct
                      ? endorsedProduct.reimbForQty
                      : item.endorsedReimbPrice ?? NaN;
                // Net reimbursement is the Claim DT price less the Part V
                // discount deduction for this item's Drug Tariff category.
                const netReimbNum = Number.isFinite(claimDtNum)
                  ? claimDtNum * (1 - tariffNetReimbRate(item.drugTariffCategory))
                  : NaN;
                const packBaseProduct = selProduct ?? endorsedProduct;
                const dispensedPackSize = endorsedProduct?.packSize ?? item.endorsedPackSize ?? 0;
                const packsFor = (packSize: number) => (packSize > 0 ? dispensedQty / packSize : 0);
                const fmtPacks = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));
                return (
                  <div
                    key={item.id}
                    className={`rounded-xl border transition-all ${
                      isFocused
                        ? dark ? "border-blue-500/50 bg-blue-500/10 ring-1 ring-blue-500/30" : "border-blue-400 bg-blue-50 ring-1 ring-blue-200"
                        : needsAttention
                          ? dark ? "border-red-500/40 bg-red-500/5" : "border-red-300 bg-red-50/60"
                          : item.isSelected
                            ? dark ? "border-gray-700 bg-gray-800/50" : "border-gray-300 bg-gray-50"
                            : dark ? "border-gray-800" : "border-gray-200"
                    }`}
                  >
                    {/* Item header - clickable to expand/collapse */}
                    <div className="p-3 space-y-2.5">
                      {/* Row 1: drug name + endorsement status */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Pill className={`w-4 h-4 shrink-0 ${isFocused ? dark ? "text-blue-400" : "text-blue-600" : dark ? "text-gray-500" : "text-gray-400"}`} />
                          <p title={claimItemTitle(item)} className={`text-sm font-semibold truncate ${isFocused ? dark ? "text-white" : "text-gray-900" : dark ? "text-gray-200" : "text-gray-800"}`}>{claimItemTitle(item)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {item.ruleCategory && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>
                              <Lock className="w-2.5 h-2.5" /> {item.ruleCategory}
                            </span>
                          )}
                          {item.endorsements.length > 0 && (
                            <div className="flex items-center gap-1">
                              {item.endorsements.map(e => (
                                <span key={e.code} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>{e.code}</span>
                              ))}
                            </div>
                          )}
                          {itemFullyEndorsed ? (
                            <ShieldCheck className={`w-3.5 h-3.5 ${dark ? "text-emerald-400" : "text-emerald-600"}`} />
                          ) : itemMissing.length > 0 ? (
                            <span className={`inline-flex items-center gap-0.5 text-[9px] font-medium ${dark ? "text-red-400" : "text-red-600"}`}>
                              <CircleAlert className="w-3 h-3" /> {itemMissing.length}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {/* Row 2: status badges */}
                      {(item.isSelected || needsAttention || bpInfo) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {item.isSelected && (
                            <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 rounded font-semibold ${dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-700"}`}>SELECTED</span>
                          )}
                          {needsAttention && (
                            <span className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded font-semibold ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>
                              <CircleAlert className="w-2.5 h-2.5" /> Needs attention · {itemMissing.length} missing
                            </span>
                          )}
                          {bpInfo && !itemInfoLoading && (
                            <span
                              title="A cheaper brand/pack size is available for this item"
                              className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded font-semibold ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}
                            >
                              <TrendingUp className="w-2.5 h-2.5" /> Better Pack
                            </span>
                          )}
                        </div>
                      )}

                      {/* Row 3: dispensed + directions */}
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div className={`rounded-lg border px-2.5 py-2 ${dark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <Package className={`w-3 h-3 shrink-0 ${dark ? "text-gray-400" : "text-gray-500"}`} />
                            <span className={`text-[9px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Dispensed</span>
                          </div>
                          {itemInfoLoading ? (
                            <div className="space-y-1.5 pt-0.5">
                              <Skeleton className={`h-3.5 w-36 animate-pulse ${skeletonTone}`} />
                              <Skeleton className={`h-3 w-52 animate-pulse ${skeletonTone}`} />
                            </div>
                          ) : (
                          <p className={`text-xs font-semibold ${dark ? "text-gray-100" : "text-gray-800"}`}>Quantity supplied: {item.quantity}</p>
                          )}
                          {!itemInfoLoading && (() => {
                            const packSize = dispensedPackSize;
                            if (packSize <= 0) return null;
                            const packs = packsFor(packSize);
                            const unit = item.unitText?.trim() || claimForm;
                            const packLabel = Number.isInteger(packs) ? "Packs supplied" : "Equivalent packs";
                            return (
                              <p className={`text-[10px] mt-0.5 ${dark ? "text-gray-400" : "text-gray-500"}`}>
                                Pack size: {packSize} {unit} · {packLabel}: {fmtPacks(packs)} × {packSize}
                              </p>
                            );
                          })()}
                          {item.dispensedManufacturer && (
                            <p className={`text-[10px] mt-0.5 ${dark ? "text-gray-400" : "text-gray-500"}`}>{item.dispensedManufacturer}</p>
                          )}
                        </div>
                        <div className={`rounded-lg border px-2.5 py-2 ${dark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                          <span className={`text-[9px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Directions</span>
                          <p className={`text-xs mt-1 ${dark ? "text-gray-200" : "text-gray-700"}`}>{item.dosage}</p>
                        </div>
                      </div>

                      {/* Row 4: claim pricing (from get-item-info) */}
                      <div className={`rounded-lg border px-3 py-2 ${dark ? "bg-blue-500/10 border-blue-500/20" : "bg-blue-50 border-blue-100"}`}>
                        {itemInfoLoading ? (
                          <div className="flex items-center gap-6">
                            <div className="space-y-1.5">
                              <Skeleton className={`h-2.5 w-20 animate-pulse ${skeletonTone}`} />
                              <Skeleton className={`h-4 w-14 animate-pulse ${skeletonTone}`} />
                            </div>
                            <div className={`w-px self-stretch ${dark ? "bg-blue-500/20" : "bg-blue-200"}`} />
                            <div className="space-y-1.5">
                              <Skeleton className={`h-2.5 w-16 animate-pulse ${skeletonTone}`} />
                              <Skeleton className={`h-4 w-14 animate-pulse ${skeletonTone}`} />
                            </div>
                          </div>
                        ) : (Number.isFinite(claimDtNum) || Number.isFinite(netReimbNum)) ? (
                          <div className="flex items-center gap-6">
                            <div>
                              <p className={`text-[9px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Claim DT Price</p>
                              <p className={`text-sm font-bold ${dark ? "text-white" : "text-gray-900"}`}>{Number.isFinite(claimDtNum) ? `£${claimDtNum.toFixed(2)}` : "—"}</p>
                            </div>
                            <div className={`w-px self-stretch ${dark ? "bg-blue-500/20" : "bg-blue-200"}`} />
                            <div>
                              <p className={`text-[9px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Net Reimb</p>
                              <p className={`text-sm font-bold ${dark ? "text-blue-300" : "text-blue-700"}`}>{Number.isFinite(netReimbNum) ? `£${netReimbNum.toFixed(2)}` : "—"}</p>
                            </div>
                          </div>
                        ) : (
                          <span className={`text-[10px] italic ${dark ? "text-gray-500" : "text-gray-400"}`}>Claim DT / Net Reimb unavailable</span>
                        )}
                      </div>
                    </div>

                    {/* Inline endorsements panel - shown when expanded */}
                    {isFocused && (
                      <div className={`px-3 pb-3 border-t ${dark ? "border-gray-700/50" : "border-gray-200/80"}`}>
                        <div className="pt-3">
                          {/* Header with Add button */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                              <Stamp className={`w-3.5 h-3.5 ${dark ? "text-gray-400" : "text-gray-500"}`} />
                              <span className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Additional Endorsements</span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); setAddEndorsementForItem(isAddingForThis ? null : item.id); setPendingEndorsement(null); }}
                              className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md transition-colors cursor-pointer ${dark ? "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"}`}
                            >
                              <Plus className="w-2.5 h-2.5" /> Add
                            </button>
                          </div>

                          {/* Missing required endorsements */}
                          {itemMissing.length > 0 && (
                            <div className={`rounded-lg border p-2.5 mb-2 ${dark ? "border-red-500/30 bg-red-500/10" : "border-red-200 bg-red-50"}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <span className={`text-[10px] font-semibold ${dark ? "text-red-400" : "text-red-700"}`}>Required missing</span>
                                <button onClick={(e) => { e.stopPropagation(); applyAllRequired(item.id); }} className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-500 transition-colors cursor-pointer">
                                  <Check className="w-2.5 h-2.5" /> Apply All
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {itemMissing.map(r => (
                                  <button key={r.code} onClick={(e) => { e.stopPropagation(); initiateAdd(item.id, r.code, r.label); }}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium transition-colors cursor-pointer ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"}`}
                                  >
                                    <Plus className="w-2.5 h-2.5" /> {r.code}
                                    {ENDORSEMENT_EXTRA_FIELDS[r.code] && <span className={`text-[8px] ${dark ? "text-amber-400" : "text-amber-600"}`}>(info)</span>}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Add endorsement picker */}
                          {isAddingForThis && (() => {
                            const available = ENDORSEMENT_OPTIONS.filter(o => !existingCodes.has(o.code));
                            const requiredOpts = available.filter(o => requiredCodes.has(o.code));
                            const otherOpts = available.filter(o => !requiredCodes.has(o.code));
                            return (
                              <div className={`rounded-lg border p-2.5 mb-2 ${dark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <p className={`text-[10px] font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Select endorsement</p>
                                  <button onClick={(e) => { e.stopPropagation(); setAddEndorsementForItem(null); }} className={`p-0.5 rounded cursor-pointer ${dark ? "text-gray-400 hover:bg-gray-700" : "text-gray-400 hover:bg-gray-100"}`}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                                <select
                                  value=""
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    const opt = ENDORSEMENT_OPTIONS.find(o => o.code === e.target.value);
                                    if (opt) initiateAdd(item.id, opt.code, opt.label);
                                  }}
                                  className={`w-full text-xs rounded-lg border px-2.5 py-2 cursor-pointer ${dark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                                >
                                  <option value="">Choose an endorsement…</option>
                                  {requiredOpts.length > 0 && (
                                    <optgroup label="Required for this item">
                                      {requiredOpts.map(opt => (
                                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  <optgroup label="All endorsements">
                                    {otherOpts.map(opt => (
                                      <option key={opt.code} value={opt.code}>{opt.label}</option>
                                    ))}
                                  </optgroup>
                                </select>
                              </div>
                            );
                          })()}

                          {/* Extra info form */}
                          {isPendingForThis && pendingEndorsement && ENDORSEMENT_EXTRA_FIELDS[pendingEndorsement.code] && (
                            <div className={`rounded-lg border p-2.5 mb-2 ${dark ? "border-blue-500/30 bg-blue-500/10" : "border-blue-200 bg-blue-50"}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-700"}`}>{pendingEndorsement.code}</span>
                                  <span className={`text-xs font-medium ${dark ? "text-blue-400" : "text-blue-700"}`}>{pendingEndorsement.label}</span>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); setPendingEndorsement(null); setExtraFormData({}); }} className={`p-1 rounded cursor-pointer ${dark ? "text-gray-400 hover:bg-gray-800" : "text-gray-400 hover:bg-gray-100"}`}>
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="space-y-2">
                                {(ENDORSEMENT_EXTRA_FIELDS[pendingEndorsement.code] || []).map(field => (
                                  <div key={field.key} onClick={(e) => e.stopPropagation()}>
                                    <label className={`text-[9px] font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>
                                      {field.label} {field.required && <span className={dark ? "text-red-400" : "text-red-500"}>*</span>}
                                    </label>
                                    <input type={field.type ?? "text"} value={extraFormData[field.key] || ""} onChange={e => setExtraFormData(prev => ({ ...prev, [field.key]: e.target.value }))} placeholder={field.placeholder}
                                      className={`w-full mt-0.5 px-2.5 py-1.5 rounded-md text-xs border outline-none transition-colors ${dark ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"}`}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <button onClick={(e) => { e.stopPropagation(); confirmPending(); }}
                                  disabled={!(ENDORSEMENT_EXTRA_FIELDS[pendingEndorsement.code] || []).filter(f => f.required).every(f => (extraFormData[f.key] || "").trim())}
                                  className={`flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-md transition-colors cursor-pointer ${
                                    (ENDORSEMENT_EXTRA_FIELDS[pendingEndorsement.code] || []).filter(f => f.required).every(f => (extraFormData[f.key] || "").trim())
                                      ? "bg-emerald-600 text-white hover:bg-emerald-500" : dark ? "bg-gray-700 text-gray-500 cursor-not-allowed" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                  }`}
                                >
                                  <Check className="w-2.5 h-2.5" /> Confirm
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setPendingEndorsement(null); setExtraFormData({}); }} className={`px-3 py-1.5 text-[10px] font-medium rounded-md cursor-pointer ${dark ? "text-gray-400" : "text-gray-500"}`}>Cancel</button>
                              </div>
                            </div>
                          )}

                          {/* Applied endorsements list */}
                          {item.endorsements.length === 0 && !isPendingForThis && !isAddingForThis ? (
                            <div className={`text-center py-3 rounded-lg border ${dark ? "border-gray-800" : "border-gray-200"}`}>
                              <p className={`text-[10px] ${dark ? "text-gray-500" : "text-gray-400"}`}>No endorsements on this item</p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {item.endorsements.map(e => {
                                const hasDetails = e.extraInfo && Object.keys(e.extraInfo).length > 0;
                                const isExpanded = expandedEndorsements.has(`${item.id}:${e.code}`);
                                const fieldDefs = ENDORSEMENT_EXTRA_FIELDS[e.code];
                                return (
                                  <div key={e.code} className={`rounded-lg overflow-hidden ${dark ? "bg-gray-800/40" : "bg-white"}`}>
                                    <div className={`flex items-center justify-between px-2.5 py-1.5 ${hasDetails ? "cursor-pointer" : ""}`}
                                      onClick={(ev) => { ev.stopPropagation(); if (hasDetails) toggleEndorsementExpand(item.id, e.code); }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>{e.code}</span>
                                        <div>
                                          <p className={`text-[11px] font-medium ${dark ? "text-gray-200" : "text-gray-800"}`}>{e.label}</p>
                                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0">
                                            {e.appliedDate && <span className={`text-[9px] ${dark ? "text-gray-500" : "text-gray-400"}`}>{e.appliedDate}</span>}
                                            {hasDetails && !isExpanded && <span className={`text-[9px] ${dark ? "text-blue-400" : "text-blue-600"}`}>View details</span>}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {hasDetails && <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""} ${dark ? "text-gray-500" : "text-gray-400"}`} />}
                                        {!isPrescriberEndorsement(e.code) && (
                                          <button onClick={(e2) => { e2.stopPropagation(); removeEndorsement(item.id, e.code); }}
                                            className={`p-1 rounded cursor-pointer ${dark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`} title="Remove">
                                            <X className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {hasDetails && isExpanded && e.extraInfo && (
                                      <div className={`px-2.5 pb-2 pt-1 border-t ${dark ? "border-gray-700/50" : "border-gray-100"}`} onClick={(ev) => ev.stopPropagation()}>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                          {Object.entries(e.extraInfo).map(([k, v]) => {
                                            const fieldDef = fieldDefs?.find(f => f.key === k);
                                            const fieldLabel = fieldDef?.label || k.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()).trim();
                                            return (
                                              <div key={k}>
                                                <p className={`text-[9px] font-semibold uppercase tracking-wider ${dark ? "text-gray-500" : "text-gray-400"}`}>{fieldLabel}</p>
                                                <p className={`text-[11px] font-medium mt-0.5 ${dark ? "text-gray-200" : "text-gray-700"}`}>{v}</p>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {itemInfoLoading ? (
                          <div className={`mt-3 rounded-lg border p-3 space-y-2.5 ${dark ? "border-gray-700" : "border-gray-200"}`}>
                            <Skeleton className={`h-3 w-40 animate-pulse ${skeletonTone}`} />
                            {[0, 1, 2].map(i => (
                              <div key={i} className="flex items-center gap-3">
                                <Skeleton className={`h-4 w-16 animate-pulse ${skeletonTone}`} />
                                <Skeleton className={`h-4 flex-1 animate-pulse ${skeletonTone}`} />
                                <Skeleton className={`h-4 w-14 animate-pulse ${skeletonTone}`} />
                                <Skeleton className={`h-4 w-14 animate-pulse ${skeletonTone}`} />
                              </div>
                            ))}
                          </div>
                        ) : (
                        <PackSizePriceTable
                          // Keyed so the expanded-brand state resets when the
                          // pharmacist switches to another item on the script.
                          key={item.id}
                          itemId={item.id}
                          options={tablePackOptions}
                          dispensedQty={dispensedQty}
                          form={claimForm}
                          selectedProductIdx={claimedOption?.productIdx}
                          onSelect={(productIdx) => selectPackProduct(item.id, productIdx, packPriceOptions, dispensedQty, claimForm)}
                          onRoundUp={canRoundUpPacks ? () => promptRoundUp(item.id, exactClaimedOption, dispensedQty, claimForm) : undefined}
                          onKeepExactPacks={() => setItemRoundUpPacks(item.id, false)}
                          dark={dark}
                        />
                        )}
                        {concessionLoading && item.prescribedItemId && (
                          <div className={`mt-3 rounded-lg border p-3 space-y-3 ${dark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50/40"}`}>
                            <div className="space-y-1.5">
                              <Skeleton className={`h-2.5 w-36 animate-pulse ${skeletonTone}`} />
                              <Skeleton className={`h-3.5 w-48 animate-pulse ${skeletonTone}`} />
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                              {[0, 1, 2, 3].map(i => <Skeleton key={i} className={`h-14 animate-pulse ${skeletonTone}`} />)}
                            </div>
                            <Skeleton className={`h-36 animate-pulse ${skeletonTone}`} />
                          </div>
                        )}
                        {!concessionLoading && concession && (
                          <ConcessionInsightPanel
                            key={`concession-${item.id}`}
                            product={concession}
                            claimPackSize={claimedOption?.packSize ?? dispensedPackSize}
                            dark={dark}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "prescription" && (
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {detail.prescriptionImageUrl ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className={`w-4 h-4 ${dark ? "text-gray-400" : "text-gray-500"}`} />
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${dark ? "text-gray-400" : "text-gray-500"}`}>Original NHS Prescription</h4>
                  </div>
                  <button onClick={() => window.open(detail.prescriptionImageUrl, "_blank")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"}`}
                  >
                    <ArrowUpRight className="w-3 h-3" /> Open Full Size
                  </button>
                </div>
                <div className={`rounded-xl overflow-auto border flex items-start justify-center ${dark ? "border-gray-700 bg-gray-950/40" : "border-gray-200 bg-gray-50"}`}>
                  <img src={detail.prescriptionImageUrl} alt="Original NHS prescription" className="max-h-[52vh] w-auto max-w-full object-contain" />
                </div>
              </div>
            ) : detail.isImageLoading ? (
              <div className={`text-center py-12 rounded-xl border ${dark ? "border-gray-800" : "border-gray-200"}`}>
                <Loader2 className={`w-6 h-6 mx-auto mb-2 animate-spin ${dark ? "text-gray-500" : "text-gray-400"}`} />
                <p className={`text-sm font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>Loading prescription image…</p>
              </div>
            ) : (
              <div className={`text-center py-12 rounded-xl border ${dark ? "border-gray-800" : "border-gray-200"}`}>
                <FileText className={`w-8 h-8 mx-auto mb-2 ${dark ? "text-gray-600" : "text-gray-300"}`} />
                <p className={`text-sm font-medium ${dark ? "text-gray-400" : "text-gray-500"}`}>No prescription image available</p>
              </div>
            )}
          </div>
        )}

            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className={`shrink-0 px-4 sm:px-6 py-3 border-t flex flex-wrap items-center justify-end gap-2 sm:gap-3 `}>
          {onClear && (
            <button
              onClick={() => setClearConfirmOpen(true)}
              disabled={clearingRx || claimingRx}
              className={`mr-auto flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer border disabled:opacity-60 disabled:cursor-not-allowed ${dark ? "bg-red-500/15 text-red-400 hover:bg-red-500/25 border-red-500/30" : "bg-red-50 text-red-700 hover:bg-red-100 border-red-200"}`}
            >
              {clearingRx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Clear
            </button>
          )}
          <button
            onClick={onClose}
            className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${dark ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"}`}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={claimingRx || itemInfoLoading}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer border disabled:opacity-60 disabled:cursor-not-allowed ${dark ? "bg-gray-800 text-gray-200 hover:bg-gray-700 border-gray-700" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"}`}
          >
            <Check className="w-3.5 h-3.5" /> Save
          </button>
          {!detail.isClaimed && (
            <button
              onClick={saveAndClaim}
              disabled={claimingRx || itemInfoLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {claimingRx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save &amp; Queue Claim
            </button>
          )}
        </div>

        {roundUpPrompt && (() => {
          const { itemId, packSize, packCount, dispensedQty, unit } = roundUpPrompt;
          const wholePacks = Math.ceil(packCount);
          const claimedQty = wholePacks * packSize;
          const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));
          return (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={e => e.stopPropagation()}>
              <div className={`w-full max-w-sm rounded-xl border shadow-2xl p-5 ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Package className={`w-5 h-5 ${dark ? "text-blue-400" : "text-blue-600"}`} />
                  <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Round up to whole packs?</h3>
                </div>
                <p className={`text-xs leading-relaxed mb-3 ${dark ? "text-gray-400" : "text-gray-600"}`}>
                  {fmtQty(dispensedQty)} {unit} is {fmtQty(packCount)} packs of {fmtQty(packSize)} {unit}. Claim {wholePacks} whole packs instead?
                </p>
                <div className={`flex gap-2 rounded-lg border p-2.5 mb-4 text-xs leading-relaxed ${dark ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                  <TriangleAlert className="w-4 h-4 shrink-0 mt-px" />
                  <span>
                    <span className="font-semibold">Over-allocation:</span> {wholePacks} × {fmtQty(packSize)} {unit} = {fmtQty(claimedQty)} {unit}, which is {fmtQty(claimedQty - dispensedQty)} {unit} more than the {fmtQty(dispensedQty)} {unit} supplied.
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRoundUpPrompt(null)}
                    className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}
                  >
                    Keep {fmtQty(packCount)} packs
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setItemRoundUpPacks(itemId, true);
                      setRoundUpPrompt(null);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                  >
                    Round up to {wholePacks}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {clearConfirmOpen && onClear && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={e => e.stopPropagation()}>
            <div className={`w-full max-w-sm rounded-xl border shadow-2xl p-5 ${dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <CircleAlert className={`w-5 h-5 ${dark ? "text-red-400" : "text-red-600"}`} />
                <h3 className={`text-sm font-semibold ${dark ? "text-white" : "text-gray-900"}`}>Clear this prescription?</h3>
              </div>
              <p className={`text-xs leading-relaxed mb-4 ${dark ? "text-gray-400" : "text-gray-600"}`}>
                You are about to clear this prescription from the claim screen. It will no longer appear here and will not be claimed.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearConfirmOpen(false)}
                  className={`flex-1 ${dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : ""}`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={async () => {
                    setClearConfirmOpen(false);
                    setClearingRx(true);
                    await onClear();
                    setClearingRx(false);
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-500 text-white"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body,
  );
}
